import type { Locale } from "../stores/locale";

const messages = {
  // Login (first-impression entry — the page every new user hits after OAuth).
  "login.tagline": { ja: "the Git Telescope", en: "the Git Telescope" },
  "login.hero": {
    ja: "あなたのエンジニアリングの軌道を、観測する。",
    en: "Observe your engineering orbit.",
  },
  "login.heroSub": {
    ja: "活動量ではなく、重力を測る。",
    en: "It doesn't measure activity. It measures gravity.",
  },
  "login.signIn": { ja: "Sign in with GitHub", en: "Sign in with GitHub" },
  "login.legalPrefix": {
    ja: "続行すると、",
    en: "By continuing, you agree to our ",
  },
  "login.legalMid": { ja: " と ", en: " and " },
  "login.legalSuffix": { ja: " に同意したものとみなされます。", en: "." },
  "login.termsOfService": { ja: "利用規約", en: "Terms of Service" },
  "login.privacyPolicy": { ja: "プライバシーポリシー", en: "Privacy Policy" },
  "login.motto": {
    ja: "observe gravity · galaxy boundary · quiet rigor",
    en: "observe gravity · galaxy boundary · quiet rigor",
  },

  "common.copy": { ja: "コピー", en: "Copy" },
  "common.copied": { ja: "コピーしました", en: "Copied!" },

  // Sidebar
  "nav.observatory": { ja: "観測所", en: "Observatory" },
  "nav.constellation": { ja: "星座図", en: "Constellation" },
  "nav.repos": { ja: "リポジトリ", en: "Repositories" },
  "nav.teams": { ja: "チーム", en: "Teams" },
  "nav.control-room": { ja: "制御室", en: "Control Room" },
  "nav.plan": { ja: "プラン", en: "Plan" },
  "nav.settings": { ja: "設定", en: "Settings" },
  "nav.eis-config": { ja: "EIS設定", en: "EIS Config" },
  "nav.disclosure": { ja: "自分の見え方", en: "My visibility" },
  "nav.visibility-mode": { ja: "可視性モード", en: "Visibility Mode" },
  "nav.preferences": { ja: "言語・タイムゾーン", en: "Language & Time zone" },
  "nav.api-tokens": { ja: "APIトークン", en: "API Tokens" },
  "nav.slack-connector": { ja: "Slack 連携", en: "Slack Connector" },

  // Settings → Slack connector entry card (links to the dedicated page)
  "settings.slack.title": { ja: "Slack 連携", en: "Slack Connector" },
  "settings.slack.desc": {
    ja: "重要な投稿にリアクションを付けると組織年表に取り込む装置（有料プラン）。",
    en: "React to important messages to land them in your chronicle (paid plan).",
  },
  "settings.slack.open": { ja: "開く", en: "Open" },

  // Observatory
  "observatory.title": { ja: "観測所", en: "Observatory" },
  "observatory.subtitle": {
    ja: "エンジニアリングインパクトの宇宙を観測する",
    en: "Observe the universe of engineering impact",
  },
  "observatory.analyzeAll": { ja: "観測開始", en: "Observe All" },
  "observatory.observePast": { ja: "過去を観測", en: "Observe Past" },
  "observatory.analyzing": { ja: "観測中...", en: "Observing..." },
  "observatory.alreadyObserving": {
    ja: "観測は既に実行中／キュー待ちです。完了までお待ちください。",
    en: "An observation is already running or queued — hang tight until it finishes.",
  },
  "observatory.rateLimited": {
    ja: "観測トリガの上限に達しました。少し待ってから再試行してください。",
    en: "Observation trigger limit reached. Please wait a moment and try again.",
  },
  "observatory.quotaToday": { ja: "本日の観測", en: "Today" },
  "observatory.nextUnlock": { ja: "次回解放", en: "Next unlock" },
  "observatory.dailyCapReached": {
    ja: "本日の観測回数の上限に達しました。次回解放の時刻までお待ちください。",
    en: "You've reached today's observation limit. Please wait until the next unlock time.",
  },
  "observatory.lastObserved": { ja: "最終観測", en: "Last observed" },
  "observatory.staleWarning": {
    ja: "観測がしばらく行われていません。データが古い可能性があります。再観測で更新してください。",
    en: "No recent observation — the data may be stale. Re-observe to refresh.",
  },
  "observatory.viewRuns": { ja: "観測履歴を見る", en: "View observation runs" },
  "observatory.observingTitle": { ja: "観測中…", en: "Observing…" },
  "observatory.observingHint": {
    ja: "初回の観測は git clone と blame 分析のため数分かかります。完了すると自動で表示されます（このページを離れても進行します）。",
    en: "The first observation runs git clone + blame and takes a few minutes. It'll appear automatically when done (it keeps running if you leave this page).",
  },

  // Health Hero
  "health.title": { ja: "組織ヘルス", en: "Organization Health" },
  "health.members": { ja: "メンバー", en: "Members" },
  "health.coreMembers": { ja: "コアメンバー", en: "Core members" },
  "health.topScore": { ja: "最高シグナル", en: "Top Signal" },
  "health.avgSurvival": { ja: "平均生存力", en: "Avg Survival" },
  "health.avgSignal": { ja: "平均シグナル", en: "Avg Signal" },
  "health.emitted": { ja: "総生産", en: "Emitted" },
  "health.alive": { ja: "残存", en: "Alive" },

  // Tier labels
  "tier.elite": { ja: "超新星", en: "Supernova" },
  "tier.irreplaceable": { ja: "代替不可能", en: "Irreplaceable" },
  "tier.core": { ja: "コアメンバー", en: "Core Member" },
  "tier.senior": { ja: "シニア", en: "Senior" },
  "tier.mid": { ja: "ミドル", en: "Mid" },
  "tier.junior": { ja: "ジュニア", en: "Junior" },
  "tier.growing": { ja: "成長中", en: "Growing" },

  // Signal guide
  "guide.junior": { ja: "ジュニア", en: "Junior" },
  "guide.mid": { ja: "ミドル", en: "Mid" },
  "guide.senior": { ja: "シニア", en: "Senior" },
  "guide.seniorPlus": { ja: "シニア+", en: "Senior+" },
  "guide.core": { ja: "代替不可能", en: "Irreplaceable" },
  "guide.elite": { ja: "超新星", en: "Supernova" },

  // Risk cards
  "risk.busFactor": { ja: "バス係数", en: "Bus Factor" },
  "risk.designVacuum": { ja: "設計の空白", en: "Design Vacuum" },
  "risk.catalysisDrift": { ja: "土台形成の漂流", en: "Catalysis Drift" },
  "risk.entropy": { ja: "エントロピー", en: "Entropy" },
  "risk.trustConfidence": { ja: "観測信頼度", en: "Trust Confidence" },
  "risk.lowTrustMembers": {
    ja: "名の低信頼度メンバー",
    en: "low-trust member(s)",
  },
  "risk.healthy": { ja: "健全", en: "Healthy" },
  "risk.warning": { ja: "注意", en: "Warning" },
  "risk.critical": { ja: "危険", en: "Critical" },

  // Four Forces
  "force.structure": { ja: "守る力", en: "Structure" },
  "force.creation": { ja: "壊す力", en: "Disruption" },
  "force.stability": { ja: "安定させる力", en: "Stability" },
  "force.production": { ja: "作る力", en: "Production" },
  "force.structure.hint": {
    ja: "構造を設計し、その整合性を守る力。Architect（設計力 design ＋ 領域幅 breadth）が担う。",
    en: "Designs the structure and guards its integrity. Held by the Architect (design + breadth).",
  },
  "force.creation.hint": {
    ja: "既存を壊して刷新する力。Anchor（触媒 catalysis＝変化を促す）と Cleaner（負債清掃 debt cleanup＝古いコードを壊す）が担う。",
    en: "Tears the existing down to renew it. Held by the Anchor (catalysis — sparks change) and the Cleaner (debt cleanup — breaks down the old).",
  },
  "force.stability.hint": {
    ja: "コードを長く生存させ続ける力。Specialist（生存 survival＝月を経ても残る深いコード）が担う。",
    en: "Keeps code alive over time. Held by the Specialist (survival — deep code that lasts).",
  },
  "force.production.hint": {
    ja: "新しい出力を生み出す力。Producer（生産量 production＝生き残る新規コードの量）が担う。",
    en: "Ships new output. Held by the Producer (production — volume of new code that survives).",
  },

  // Team Health
  "teamHealth.title": { ja: "チームヘルス", en: "Team Health" },
  "teamHealth.complementarity": { ja: "補完性", en: "Complementarity" },
  "teamHealth.sustainability": { ja: "持続性", en: "Sustainability" },
  "teamHealth.growth": { ja: "成長", en: "Growth" },
  "teamHealth.smallTeamNote": {
    ja: "5名以下のチームでは全Role（5種）が揃わないのは構造的に正常。Architect + Anchor の2種が揃えば小規模チームとしては健全。",
    en: "Teams of 5 or fewer structurally cannot cover all 5 roles. Having Architect + Anchor is healthy for a small team.",
  },

  // Accumulated (蓄積) — the career-total lens: Σ over the timeline, alongside the
  // per-active-period average that keeps tenure length from inflating it.
  "accum.title": { ja: "累積（通期）", en: "Accumulated (all-time)" },
  "accum.teamTitle": { ja: "チーム累積", en: "Team accumulated" },
  "accum.gravity": { ja: "累積重力", en: "Accum. gravity" },
  "accum.signal": { ja: "累積シグナル", en: "Accum. signal" },
  "accum.avg": { ja: "平均", en: "Avg" },
  "accum.activeSpan": { ja: "活動期間", en: "Active span" },
  "accum.peak": { ja: "最大", en: "Peak" },
  "accum.periods": { ja: "観測期間", en: "periods" },
  "accum.note": {
    ja: "累積は各期の総和（キャリアの積み上げ）。最大はその中で最も高かった期の水準です。",
    en: "Accumulated is the sum across periods (the career total); Peak is the highest single period.",
  },

  // Transitions
  "transitions.title": { ja: "最近の遷移", en: "Recent Transitions" },
  "transitions.none": {
    ja: "この期間のトポロジー変化はありません",
    en: "No topology changes this period",
  },

  // Constellation
  "constellation.title": { ja: "星座図", en: "Constellation" },
  "constellation.roleTimeline": {
    ja: "役割構成タイムライン",
    en: "Role Composition Timeline",
  },
  "constellation.gravityHeatmap": {
    ja: "重力ヒートマップ",
    en: "Gravity Heatmap",
  },
  "constellation.leaderboard": { ja: "重力分布", en: "Gravity Map" },
  "constellation.metric.total": { ja: "シグナル", en: "Signal" },
  "constellation.distributionSuffix": { ja: "分布", en: " Map" },
  "constellation.cumulative": { ja: "通期", en: "All-time" },
  "constellation.cumulativeAccum": { ja: "通期累計", en: "All-time · accum." },
  "accum.prefix": { ja: "累積", en: "Accum. " },
  "constellation.archetypeAll": { ja: "すべて", en: "All" },

  // Star Detail
  "star.axisScores": { ja: "軸別シグナル", en: "Axis Signals" },
  "star.changePrev": { ja: "前期間からの変化", en: "Change from previous" },
  "star.archetypeObserved": {
    ja: "観測されたアーキタイプ",
    en: "Observed archetypes",
  },
  "star.archetypeObservedHint": {
    ja: "通期で、各役割として観測した期間数。",
    en: "How many periods this person was observed in each role, all-time.",
  },
  "star.timeline": { ja: "シグナル推移", en: "Signal Timeline" },
  "star.evolution": { ja: "トポロジー遷移", en: "Topology Evolution" },
  "star.insights": { ja: "インサイト", en: "Insights" },
  "star.coaching": { ja: "コーチング", en: "Coaching" },
  "star.nextStep": { ja: "次のステップ", en: "Next Evolution" },
  "star.legacy": { ja: "レガシーの足跡", en: "Legacy Footprint" },

  // Axis labels
  "axis.production": { ja: "生産量", en: "Production" },
  "axis.catalysis": { ja: "触媒", en: "Catalysis" },
  "axis.survival": { ja: "生存力", en: "Survival" },
  "axis.design": { ja: "設計力", en: "Design" },
  "axis.breadth": { ja: "領域幅", en: "Breadth" },
  "axis.debtCleanup": { ja: "負債清掃", en: "Debt Cleanup" },
  "axis.indispensability": { ja: "不可欠度", en: "Indispensability" },
  "axis.gravity": { ja: "重力", en: "Gravity" },
  "axis.gravityLifetime": { ja: "生涯重力", en: "Lifetime gravity" },
  "axis.gravityLifetimeHint": {
    ja: "通期の重力は生涯フットプリント（残った構造）で測る。今まさに揺れている重力ではなく、何を残したか。",
    en: "All-time gravity is the lifetime footprint — the structure that lasted, not the gravity live right now.",
  },

  // Short axis labels for the radar chart (kept compact so they fit the small
  // graph; EN stays abbreviated, JP uses the concise term).
  "axis.production.short": { ja: "生産量", en: "Prod" },
  "axis.catalysis.short": { ja: "触媒", en: "Catlys" },
  "axis.survival.short": { ja: "生存力", en: "Surv" },
  "axis.design.short": { ja: "設計力", en: "Design" },
  "axis.breadth.short": { ja: "領域幅", en: "Breadth" },
  "axis.debtCleanup.short": { ja: "負債清掃", en: "Debt" },
  "axis.indispensability.short": { ja: "不可欠度", en: "Indisp" },

  // Diagnosis
  "diag.seniorTeam": { ja: "シニアレベルのチーム", en: "Senior-level team" },
  "diag.midLevel": { ja: "ミドルレベル", en: "Mid level" },
  "diag.growthPhase": { ja: "成長フェーズ", en: "Growth phase" },
  "diag.survivalCritical": {
    ja: "Survival が極めて低い",
    en: "Survival critically low",
  },
  "diag.survivalLow": { ja: "Survival が低め", en: "Survival is low" },
  "diag.noArchitect": {
    ja: "Architect が不在（Design Vacuum）",
    en: "No Architect (Design Vacuum)",
  },
  "diag.oneArchitect": { ja: "Architect が1人のみ", en: "Only 1 Architect" },
  "diag.multiArchitect": {
    ja: "Architect 複数名体制",
    en: "Multiple Architects",
  },
  "diag.noAnchor": { ja: "Anchor が不在", en: "No Anchor" },
  "diag.busFactorRisk": { ja: "Bus Factor リスク", en: "Bus Factor risk" },
  "diag.entropy": {
    ja: "Former/Silent メンバー多数",
    en: "Many Former/Silent members",
  },
  "diag.growing": {
    ja: "Growing メンバーあり",
    en: "Growing members detected",
  },

  // Not found (catch-all route) — telescope metaphor: a coordinate with nothing
  // to observe, not a generic "404".
  "notfound.code": { ja: "404", en: "404" },
  "notfound.title": {
    ja: "観測対象が見つかりません",
    en: "Nothing to observe here",
  },
  "notfound.body": {
    ja: "この座標には観測できるものがありません。URL が間違っているか、ページが移動した可能性があります。",
    en: "There's nothing to observe at this coordinate. The URL may be wrong, or the page may have moved.",
  },
  "notfound.home": { ja: "観測所に戻る", en: "Back to the observatory" },

  // Worker status
  "worker.queued": {
    ja: "キューに追加 — ワーカー待機中...",
    en: "Queued — waiting for worker...",
  },
  "worker.cloning": {
    ja: "リポジトリをクローン中...",
    en: "Cloning repositories...",
  },
  "worker.signaling": {
    ja: "シグナリング中 (git blame + 分析)...",
    en: "Signaling (git blame + analysis)...",
  },
  "worker.persisting": { ja: "結果を保存中...", en: "Saving results..." },
  "worker.completed": { ja: "観測完了", en: "Observation complete" },
  "worker.failed": { ja: "分析失敗", en: "Analysis failed" },
  // Friendly causes for a failed run — the raw worker error (often raw git
  // stderr like "exit status 128") is meaningless to a user, so we map the
  // common cases to plain language. Mirrors ObservationLogPanel.friendlyFailure.
  "worker.err.notFound": {
    ja: "リポジトリが見つかりません（移管・削除・権限なし）",
    en: "Repository not found (transferred, deleted, or no access)",
  },
  "worker.err.permission": {
    ja: "アクセス権限がありません（GitHub App の再認可が必要かも）",
    en: "Permission denied (the GitHub App may need re-authorising)",
  },
  "worker.err.clone": {
    ja: "リポジトリのクローンに失敗しました",
    en: "Failed to clone the repository",
  },
  "worker.err.timeout": {
    ja: "観測がタイムアウトしました（リポジトリが大きすぎる可能性）",
    en: "Observation timed out (the repo may be too large)",
  },
  "worker.err.tooLarge": {
    ja: "リポジトリが大きすぎます",
    en: "Repository too large",
  },
  "worker.err.generic": {
    ja: "観測に失敗しました",
    en: "Observation failed",
  },
  // ETA covers only the clone phase (the only measurable sub-step); signaling
  // + saving follow and aren't counted, so it's labelled approximate.
  "worker.etaApprox": { ja: "残り約 ", en: "~" },

  // Diagnosis detail messages
  "diag.seniorTeam.detail": {
    ja: "7軸すべてでバランスの取れた高い実力を示している。",
    en: "Demonstrating well-balanced, high competence across all 7 axes.",
  },
  "diag.midLevel.detail": {
    ja: "40（シニア相当）に到達するには、Survival（コード生存力）と Design（設計関与）の向上が鍵。書いたコードが残り、設計に関与するほどシグナルは上がる。",
    en: "To reach 40 (senior level), improving Survival (code durability) and Design (architectural involvement) is key. Signals increase as your code persists and you engage in design decisions.",
  },
  "diag.growthPhase.detail": {
    ja: "シグナルが低い原因は個人の問題とは限らない。仕様の品質、プランニングの精度、意思決定のスピード — 組織のプロセスも影響する。まず環境を疑い、次に個人の成長を支援する。",
    en: "Low signals are not necessarily an individual problem. Spec quality, planning precision, and decision-making speed — organizational processes also have an impact. Question the environment first, then support individual growth.",
  },
  "diag.survivalCritical.detail": {
    ja: "書いたコードが残っていない。同じ機能を何度も書き直している可能性。仕様が頻繁に変わる、設計方針が定まっていない、またはコードレビューが機能していないサイン。",
    en: "Written code is not surviving. Possibly rewriting the same features repeatedly. This signals frequent spec changes, undefined design direction, or dysfunctional code review.",
  },
  "diag.survivalLow.detail": {
    ja: "コードの生存力が低い = 設計が変更圧に耐えられていない。正しい仕様理解と正しい設計判断の上でしかSurvivalは伸びない。",
    en: "Low code survival = design cannot withstand change pressure. Survival only improves through correct spec understanding and sound design decisions.",
  },
  "diag.noArchitect.detail": {
    ja: "構造の中心を作る人がいない。設計判断が分散し、コードベースが無秩序に向かう危険がある。Design軸を高める人材の育成または採用が急務。",
    en: "No one is creating structural foundations. Design decisions are scattered, risking codebase disorder. Urgently develop or hire talent to strengthen the Design axis.",
  },
  "diag.oneArchitect.detail": {
    ja: "に設計が集中。この人が抜けると構造が崩壊する（Collapse Risk）。第二のArchitectの育成が重要。",
    en: "Design is concentrated on this person. If they leave, the structure collapses (Collapse Risk). Developing a second Architect is critical.",
  },
  "diag.multiArchitect.detail": {
    ja: "設計力が分散しており、特定個人に依存しない構造。Architectural Engineとして健全。",
    en: "Design capability is distributed — not dependent on any single individual. Architecturally healthy.",
  },
  "diag.noAnchor.detail": {
    ja: "他者の土台となる力が不足。Catalysis が高く Debt Cleanup に貢献するメンバーがいない。チームの土台は構造的に担保されていない状態。",
    en: "Insufficient foundation-building force. No members with high Catalysis contributing to Debt Cleanup. The team's foundation is not structurally guaranteed.",
  },
  "diag.busFactorRisk.detail": {
    ja: "などに依存が集中。これらのメンバーが離脱するとモジュールが保守不能になる。知識移転とペアプロの推進を。",
    en: "Dependency is concentrated on these members. If they leave, modules become unmaintainable. Promote knowledge transfer and pair programming.",
  },
  "diag.entropy.detail": {
    ja: "チームの30%以上が非活動状態。退職済みメンバーのコードが残っている、または席を占めているが耐久性のあるアウトプットがない状態。",
    en: "Over 30% of the team is inactive. Code from departed members remains, or members occupy seats without producing durable output.",
  },
  "diag.growing.detail": {
    ja: "成長フェーズのメンバーがいる。まだ量は出ていないが丁寧に書いている。Production と Design が伸びれば化ける。",
    en: "Members in growth phase detected. Not yet high volume, but writing carefully. They can break through if Production and Design improve.",
  },

  // Risk card details
  "risk.noConcentration": { ja: "集中リスクなし", en: "No concentration risk" },
  "risk.architectsActive": {
    ja: "名のアーキテクトが活動中",
    en: "architect(s) active",
  },
  "risk.teamAvg": { ja: "チーム平均:", en: "Team avg:" },
  "risk.formerSilentMembers": {
    ja: "名の former/silent メンバー",
    en: "former/silent member(s)",
  },
  "risk.noLowTrust": { ja: "信頼度は十分", en: "Confidence adequate" },

  // Trust Signal
  "trust.title": { ja: "信頼度シグナル", en: "Trust Signal" },
  "trust.subtitle": {
    ja: "Impact Signalの観測信頼性",
    en: "Observation confidence of Impact Signal",
  },
  "trust.philosophy": {
    ja: "Trust Signalは観測の信頼度を測るものであり、人間の価値を測るものではない。重力は本質的に偽装しにくい — 社会構造、時間的蓄積、他者の行動は人工的に作れない。",
    en: "Trust measures observation confidence, not human worth. Gravity is inherently hard to fake — social structure, temporal accumulation, and third-party actions cannot be manufactured.",
  },
  "trust.repoPublicity": { ja: "リポジトリ公開性", en: "Repository Publicity" },
  "trust.repoPublicity.desc": {
    ja: "公開リポジトリは外部検証が可能",
    en: "Public repos are externally verifiable",
  },
  "trust.contributorDiversity": {
    ja: "コントリビュータ多様性",
    en: "Contributor Diversity",
  },
  "trust.contributorDiversity.desc": {
    ja: "社会構造は偽装しにくい",
    en: "Social structures are hard to fake",
  },
  "trust.crossRepoValidation": {
    ja: "クロスリポ検証",
    en: "Cross-Repo Validation",
  },
  "trust.crossRepoValidation.desc": {
    ja: "複数リポジトリでの一貫したパターン",
    en: "Consistent patterns across repos",
  },
  "trust.temporalConsistency": {
    ja: "時間的一貫性",
    en: "Temporal Consistency",
  },
  "trust.temporalConsistency.desc": {
    ja: "時間経過に対するシグナルの安定性",
    en: "Stable signals over time",
  },
  "trust.organicPattern": { ja: "有機的パターン", en: "Organic Pattern" },
  "trust.organicPattern.desc": {
    ja: "自然な開発パターン",
    en: "Natural development patterns",
  },
  "trust.tier.high": { ja: "高信頼度", en: "High Confidence" },
  "trust.tier.moderate": { ja: "中信頼度", en: "Moderate Confidence" },
  "trust.tier.low": { ja: "低信頼度", en: "Low Confidence" },
  "trust.tier.insufficient": { ja: "データ不足", en: "Insufficient Data" },
  "trust.flag.verifiedPublic": { ja: "公開検証済", en: "Public Verified" },
  "trust.flag.multiContributor": {
    ja: "複数コントリビュータ",
    en: "Multi-Contributor",
  },
  "trust.flag.longTermObserved": { ja: "長期観測済", en: "Long-Term Observed" },
  "trust.flag.lowConfidence": { ja: "低信頼度", en: "Low Confidence" },
  "trust.flag.isolatedEnvironment": {
    ja: "孤立環境",
    en: "Isolated Environment",
  },
  "trust.flag.syntheticPattern": {
    ja: "合成パターン検出",
    en: "Synthetic Pattern",
  },

  // Four Forces
  "force.members": { ja: "名のメンバー", en: "member(s)" },
  "force.noMembers": {
    ja: "この象限にメンバーなし",
    en: "No members in this quadrant",
  },
  "force.more": { ja: "名以上", en: "more" },

  // Observatory
  "observatory.loading": {
    ja: "Observatory を読み込み中...",
    en: "Loading observatory...",
  },
  "observatory.noObservations": {
    ja: "まだ観測データがありません。観測を実行してください。",
    en: "No observations yet. Run an observation to begin.",
  },
  // Shown to a read-only Shared-Galaxies guest on an un-observed galaxy: they
  // can't run an observation, so the owner-facing "run one" copy would be wrong
  // (and a guest with no CTA previously saw a blank screen).
  "observatory.guestNoObservations": {
    ja: "まだ観測データがありません。オーナーが観測を実行すると、ここに表示されます。",
    en: "No observations yet. Data will appear here once the galaxy owner runs one.",
  },
  "observatory.timeline": { ja: "タイムライン", en: "Timeline" },
  "observatory.backTo": { ja: "Observatory に戻る", en: "Back to Observatory" },

  // Constellation
  "constellation.subtitle": {
    ja: "チームの累積を並べる場 — 役割バランス、影響マップ、各メンバーの累積プロファイル",
    en: "Where the team's accumulated footprint is laid out — role balance, influence map, and each member's accumulated profile.",
  },
  "constellation.loading": {
    ja: "Constellation を読み込み中...",
    en: "Loading constellation...",
  },
  "constellation.noData": {
    ja: "Constellation データがありません。まず観測を実行してください。",
    en: "No constellation data yet. Run an observation first.",
  },
  "constellation.roleTimelineSub": {
    ja: "チームの役割バランスがどう変化したか",
    en: "How the team's role balance evolved over time",
  },
  "constellation.generational": { ja: "世代交代", en: "Generational Change" },
  "constellation.heatmapSub": {
    ja: "重力ヒートマップ — 明るいほど構造的影響力が強い",
    en: "Gravity heatmap — brighter = stronger structural influence",
  },
  "constellation.heatmapLegend": {
    ja: "数値がある期間は Architect / Anchor として重力を担っていた瞬間。空欄はその役割ではなかった、または観測外の期間。",
    en: "Cells with numbers mark periods when the author carried Architect / Anchor gravity. Blank = not in that role, or outside the observation window.",
  },
  "constellation.name": { ja: "名前", en: "Name" },

  // Logbook
  "logbook.loading": {
    ja: "タイムラインを読み込み中...",
    en: "Loading timeline...",
  },
  "logbook.noData": {
    ja: "タイムラインデータがありません。まず観測を実行してください。",
    en: "No timeline data yet. Run an observation first.",
  },
  "logbook.periods": { ja: "期間", en: "periods" },
  "logbook.topologyShift": { ja: "トポロジー変化", en: "Topology shift" },
  "logbook.teamHealthOverTime": {
    ja: "チームヘルスの推移",
    en: "Team Health Over Time",
  },

  // Star Detail
  "star.focusing": {
    ja: "{name} にフォーカス中...",
    en: "Focusing on {name}...",
  },
  "star.noScore": {
    ja: "のシグナルデータが見つかりません。",
    en: "No signal data found for",
  },
  "star.noTimeline": {
    ja: "タイムラインデータがありません。",
    en: "No timeline data available.",
  },
  "star.evolutionTitle": { ja: "軌道遷移", en: "Orbital Transition" },
  "star.evolutionSub": {
    ja: "観測期間を通じて総合シグナルがどう推移したか",
    en: "How the overall signal moved across observed periods",
  },
  "star.shifted": { ja: "変化", en: "shifted" },
  "star.teamAverage": { ja: "チーム平均", en: "Team avg" },
  "star.insightsSub": {
    ja: "パターン検出とコーチング推奨",
    en: "Pattern detection and coaching recommendations",
  },
  "star.evolutionPath": { ja: "進化の道", en: "Evolution Path" },
  "star.nextRole": { ja: "次の目標ロール:", en: "Next target role:" },

  // Insight labels
  "insight.churnRisk": { ja: "チャーンリスク大", en: "High churn risk" },
  "insight.churnRisk.text": {
    ja: "高い生産量だがSurvivalが低い — コードがすぐに置き換えられている。テストカバレッジを高め、耐久性のあるコードを意識する。",
    en: "High production but low survival — code is being replaced quickly. Focus on writing durable code with better test coverage.",
  },
  "insight.growingArchitect": {
    ja: "Architect に近づいている",
    en: "Growing toward Architect",
  },
  "insight.growingArchitect.text": {
    ja: "Design シグナルが上昇中。アーキテクチャへの影響力が拡大している。",
    en: "Design signal is increasing. Architectural influence is expanding.",
  },
  // Shown instead of growingArchitect when the member is ALREADY an Architect —
  // "approaching Architect" contradicts a role they already hold, so this frames
  // the same rising-Design signal as deepening influence, not arriving.
  "insight.architectDeepening": {
    ja: "設計影響力が拡大中",
    en: "Architectural influence deepening",
  },
  "insight.architectDeepening.text": {
    ja: "Design シグナルが上昇中。Architect として設計への影響力をさらに広げている。",
    en: "Design signal is rising. As an Architect, your influence over design is widening further.",
  },
  "insight.anchorMaterial": { ja: "Anchor 適性", en: "Anchor material" },
  "insight.anchorMaterial.text": {
    ja: "他者が積み上げる強い土台と負債清掃力 — コードベースを安定させるチームアンカーのプロファイル。",
    en: "A strong foundation others build on, combined with strong debt cleanup — the profile of a team anchor who stabilizes the codebase.",
  },
  "insight.legacyCode": { ja: "レガシーコードあり", en: "Legacy code exists" },
  "insight.legacyCode.text": {
    ja: "このコントリビューターはアクティブなコードベースから離れた。生存しているコードの所有権移転が必要。知識の劣化を防ぐ。",
    en: "This contributor has left the active codebase. Their surviving code needs ownership transfer to prevent knowledge decay.",
  },
  "insight.durability": {
    ja: "コードの耐久性が向上",
    en: "Code durability improving",
  },
  "insight.durability.text": {
    ja: "Survival が上昇 — コードがより長く残るようになっている。良い設計のサイン。",
    en: "Survival is increasing — code is lasting longer. Good design signal.",
  },
  "insight.busFactor": { ja: "バス係数リスク", en: "Bus factor risk" },
  "insight.busFactor.text": {
    ja: "重要なモジュールにおける知識の集中度が高い。ペアプログラミングとドキュメント化でリスクを緩和する。",
    en: "High concentration of knowledge in critical modules. Pair programming and documentation can mitigate this risk.",
  },
  "insight.seniorImpact": {
    ja: "シニアレベルのインパクト",
    en: "Senior-level impact",
  },
  "insight.seniorImpact.text": {
    ja: "7つの直交する軸で合計シグナルが40を超えている — これは本質的に達成困難。メンタリングとインパクトの増幅に注力を。",
    en: "Total signal exceeds 40 across 7 orthogonal axes — this is genuinely difficult to achieve. Focus on mentoring and multiplying impact.",
  },

  // Coaching (i18n)
  "coaching.senior": {
    ja: "シニアレベルのインパクト。メンタリングとアーキテクチャ判断に注力を。",
    en: "Senior-level impact. Focus on mentoring and architectural decisions.",
  },
  "coaching.churn": {
    ja: "高い生産量だがSurvivalが低い — コードが置き換えられている可能性。テスト付きの耐久性のあるコードを意識する。",
    en: "High production but low survival — code may be getting replaced. Focus on writing more durable code with tests.",
  },
  "coaching.lowDesign": {
    ja: "Designシグナルが低い — アーキテクチャファイル（設定、スキーマ、CI/CD）への貢献で影響範囲を広げる。",
    en: "Low design signal — contribute to architecture files (configs, schemas, CI/CD) to broaden impact.",
  },
  "coaching.lowCatalysis": {
    ja: "Catalysisが60未満 — 他者が自分の土台の上に積めていない。共有しやすい設計・ドキュメント・境界づくりで「乗れる土台」を増やす余地がある。",
    en: "Catalysis below 60 — others aren't building on your foundation. Shareable design, docs, and clear boundaries make your code something others can build on.",
  },
  "coaching.debtCleanup": {
    ja: "負債清掃力が高い — 他者のコードを積極的に改善している。チームへの貴重な貢献。",
    en: "Strong debt cleanup — you're actively improving others' code. This is valuable team contribution.",
  },
  "coaching.singleRepo": {
    ja: "単一リポジトリのみの活動 — 隣接リポジトリへの貢献でBreadthを広げることを検討。",
    en: "Single-repo contributor — consider contributing to adjacent repositories to increase breadth.",
  },
  "coaching.busFactor": {
    ja: "Indispensabilityが高い（バス係数リスク）— 重要モジュールの知識をチームメイトと共有し、ペアプロを推進。",
    en: "High indispensability (bus factor risk) — share knowledge and pair with teammates on key modules.",
  },
  "coaching.architect": {
    ja: "Architectロール: あなたの設計判断がコードベースの方向を決めている。アーキテクチャ改善への投資を継続。",
    en: "Architect role: your design decisions shape the codebase. Keep investing in architectural improvements.",
  },
  "coaching.churnStyle": {
    ja: "Churnスタイル検出: 高い生産量だがSurvivalが低い。ペースを落として残るコードに集中。",
    en: "Churn style detected: high production but low survival. Slow down and focus on code that lasts.",
  },
  "coaching.growing": {
    ja: "Growingフェーズ: 他者が土台にし始めている。この軌跡を維持し、より複雑なタスクに挑戦を。",
    en: "Growing state: others are starting to build on your foundation. Keep this trajectory — consider tackling more complex tasks.",
  },
  "coaching.former": {
    ja: "Formerコントリビューター: 過去のコードにまだ影響力がある。しかし最近の活動は少ない。",
    en: "Former contributor: your past code still has impact but recent activity is low.",
  },
  "coaching.default": {
    ja: "複数の軸で一貫した貢献を続けることで、バランスのとれた成長を目指す。",
    en: "Keep up consistent contributions across multiple axes for balanced growth.",
  },

  // Evolution paths (i18n)
  "evolution.architect": { ja: "Architect", en: "Architect" },
  "evolution.anchor": { ja: "Anchor", en: "Anchor" },
  "evolution.builder": { ja: "Builder", en: "Builder" },
  "evolution.producerToArchitect": {
    ja: "Producer → Architect: 設計ファイル（アーキテクチャファイル、設定）への貢献を増やす",
    en: "Producer → Architect: increase design contributions (architecture files, configs)",
  },
  "evolution.specialistToAnchor": {
    ja: "Specialist → Anchor: 品質を維持しつつ、より多くのリポジトリに展開する",
    en: "Specialist → Anchor: expand to more repositories while maintaining quality",
  },
  "evolution.massToBuilder": {
    ja: "Mass → Builder: コード品質を改善し、生産アウトプットをより耐久性のあるものにする",
    en: "Mass → Builder: improve code quality to make production output more durable",
  },
  "evolution.balancedToBuilder": {
    ja: "Balanced → Builder: ProductionとDesignを高めてレベルアップする",
    en: "Balanced → Builder: push production + design higher to level up",
  },

  // Former Legacy Panel
  "legacy.title": { ja: "レガシーの足跡", en: "Legacy Footprint" },
  "legacy.subtitle": {
    ja: "作者を超えて生き残るコード — 持続する構造的影響力",
    en: "Code that outlives its author — structural influence that persists",
  },
  "legacy.rawSurvival": { ja: "ピーク時 Survival", en: "Peak Survival" },
  "legacy.currentSurvival": { ja: "現在の Survival", en: "Current Survival" },
  "legacy.decayRate": { ja: "減衰率", en: "Decay rate" },
  "legacy.dissolved": { ja: "溶解", en: "dissolved" },
  "legacy.fullyIntact": { ja: "全て生存・減衰なし", en: "fully intact · no decay" },
  "legacy.surviving": { ja: "生存中", en: "surviving" },
  "legacy.dissolvedIntoDebt": { ja: "負債に溶解", en: "dissolved into debt" },
  "legacy.busFactorAssessment": {
    ja: "バス係数リスク評価",
    en: "Bus Factor Risk Assessment",
  },
  "legacy.highKnowledge": {
    ja: "高い知識集中度 — 重要なモジュールに代替のメンテナーがいない可能性。",
    en: "High knowledge concentration — critical modules may lack alternative maintainers.",
  },
  "legacy.architecturalDNA": {
    ja: "アーキテクチャDNAがこのコードベースを今も形作っている",
    en: "Architectural DNA still shapes this codebase",
  },
  "legacy.gravityField": {
    ja: "重力場がまだ残存 — この人の構造的影響力は完全には消えていない。",
    en: "Gravity field still present — this member's structural influence hasn't fully dissipated.",
  },
  "legacy.manageable": {
    ja: "レガシーの足跡は管理可能 — 重大なリスクは検出されていない。",
    en: "Legacy footprint is manageable — no critical risk detected.",
  },

  // Module Topology
  "nav.modules": { ja: "モジュール", en: "Modules" },
  "nav.logbook": { ja: "観測日誌", en: "Logbook" },
  "nav.runs": { ja: "観測履歴", en: "Observation Runs" },
  "nav.embeds": { ja: "バッジ＆グラフ", en: "Badges & Graphs" },
  "nav.career": { ja: "キャリア", en: "Career" },
  "nav.oss-research": { ja: "公開研究協力", en: "Open research" },
  "nav.members": { ja: "メンバー", en: "Members" },

  // Career Orbit — cross-galaxy consumption of the subject's own certificates.
  "career.orbit.title": { ja: "キャリア", en: "Career Orbit" },
  // Subtitle — was "galaxies you've LEFT", but Career Orbit gathers a person's
  // record across EVERY org they belonged to (past AND present), not only the
  // ones they left. Honest framing: this is your own record, keyed to your
  // identity, independent of whichever galaxy frames the page.
  "career.orbit.subtitle": {
    ja: "所属した組織で観測された自分の軌道が、銀河を横断してここに集まる",
    en: "Your trajectories, observed across every org you belonged to, gathered here",
  },
  // Career Orbit empty state (S0/S1) — see docs/design/career-gravity-certificate.md
  // "Empty state — the career timeline reveals itself". Telescope framing: the
  // record is uncovered, not authored. Never an upsell — an invitation.
  // S0 — fully empty (no observed affiliations). Verbatim canonical microcopy.
  "career.orbit.emptyState.heading": {
    ja: "まだ観測されていない夜空",
    en: "A sky not yet observed",
  },
  // Lead — one line: what it is + it's empty + here's how. The mechanics (retro
  // observation, GitHub-id binding, seal-per-affiliation) are folded into the two
  // action outcomes below and progressively disclosed once the timeline has data.
  "career.orbit.emptyState.body": {
    ja: "あなたのキャリア重力年表はまだ空です。次のどちらかで観測を始めましょう。",
    en: "Your career gravity timeline is empty. Start observing one of two ways.",
  },
  // Action ① — act now: public work lights up immediately, mintable into a free cert.
  "career.orbit.emptyState.ctaObserve": {
    ja: "公開リポジトリを今すぐ観測",
    en: "Observe public work now",
  },
  "career.orbit.emptyState.ctaObserveHint": {
    ja: "公開の仕事は即座に点灯。組織の接続なしで、無料の証明書に。",
    en: "Public work lights up instantly — mintable into a free certificate, no org needed.",
  },
  // Action ② — the viral seed: connect a past/present org so the tenure back-fills.
  "career.orbit.emptyState.ctaInvite": {
    ja: "過去・現在の所属を招待",
    en: "Invite a past or present org",
  },
  "career.orbit.emptyState.ctaInviteHint": {
    ja: "接続すると、在籍期間のあなたの重力が遡って点灯。",
    en: "Connect one and your tenure-window gravity back-fills automatically.",
  },
  // Trust footnote — the boundary in one line, replacing the paragraph's tail.
  "career.orbit.emptyState.boundary": {
    ja: "見えるのはあなた自身の在籍記録だけ。他メンバーは集合体として匿名化されます。",
    en: "You only ever see your own tenure — other members stay anonymized in aggregate.",
  },
  // S1 — partially lit (≥1 galaxy lit): continuation message + invite CTA.
  "career.orbit.partial.heading": {
    ja: "夜空はまだ広がる",
    en: "The sky is still expanding",
  },
  "career.orbit.partial.body": {
    ja: "刻まれているのは、証明書として封をした所属だけです。過去・現在の所属を Ace に招待すると在籍期間のあなたの重力が遡って観測され、所属ごとに証明書を封じてこの年表に加えられます。",
    en: "Only the affiliations you've sealed as certificates appear here. Invite a past or present org to Ace and your tenure-window gravity is observed retroactively — then seal each affiliation into a certificate to add it to this timeline.",
  },
  "career.orbit.partial.ctaInvite": {
    ja: "他の所属を招待",
    en: "Invite your other orgs",
  },
  // Mint surface ON Career Orbit (was buried in the GalaxySwitcher Archive). Lists
  // departed affiliations not yet sealed into a certificate, so "Career Orbit is
  // the home of certificates" is true in the UI, not just in copy. Seal = freeze a
  // tenure (W-01: a record uncovered, not authored — never "create"/"generate").
  "career.orbit.mint.title": {
    ja: "封をできる所属",
    en: "Affiliations you can seal",
  },
  "career.orbit.mint.body": {
    ja: "退職した所属を署名付き Gravity Certificate として封じ、この年表に刻みます。封をする瞬間に在籍が凍結され、以後は減衰しません。実名はあなた自身だけ、他メンバーは集合体として匿名化されます。",
    en: "Seal a departed affiliation into a signed Gravity Certificate and etch it onto this timeline. Sealing freezes that tenure at the moment you mint it; only your own name is identified, every other member stays anonymized in aggregate.",
  },
  // Action verb — "Seal" (封をする), not "create"/"issue", to keep the observation
  // framing: a certificate seals an observed tenure, it doesn't author a score.
  "career.orbit.mint.seal": { ja: "証明書を封じる", en: "Seal certificate" },
  "career.orbit.mint.sealing": { ja: "封じています…", en: "Sealing…" },
  "career.orbit.mint.error": {
    ja: "証明書を封じられませんでした。",
    en: "Could not seal the certificate.",
  },
  // Current-orbit SNAPSHOT group — galaxies you STILL belong to. Distinct from the
  // sealed-affiliation group above: a current orbit is not frozen, its survival is
  // still decaying toward today, so the artifact is a DATED snapshot ("as of {month}"),
  // not a sealed record. Same boundary (self-only, aggregate-anonymous, k≥5) so an
  // active member can share their period orbit in a 1:1 without changing any
  // disclosure setting.
  "career.orbit.snapshot.title": {
    ja: "現在の軌道をスナップショット",
    en: "Snapshot your current orbit",
  },
  "career.orbit.snapshot.body": {
    ja: "今いる銀河での自分の軌道を、その時点のスナップショットとして発行・共有します。退職時に封じる証明書と違い、現役の軌道は生存が現在へ減衰し続けるため「{日付}時点の観測」として記録されます。実名はあなた自身だけ、他メンバーは集合体として匿名化されます（開示設定は変わりません）。",
    en: "Issue and share your own orbit in a galaxy you still belong to, as a snapshot taken now. Unlike a departure certificate, a current orbit's survival keeps decaying toward today, so it is recorded as an observation as of this month — not a sealed record. Only your own name is identified; every other member stays anonymized in aggregate, and none of your disclosure settings change.",
  },
  // Snapshot date column: "joined → now" (the snapshot is dated, not closed).
  "career.orbit.snapshot.now": { ja: "現在", en: "now" },
  "career.orbit.snapshot.action": {
    ja: "現在の軌道を発行",
    en: "Snapshot current orbit",
  },
  "career.orbit.snapshot.minting": {
    ja: "発行しています…",
    en: "Snapshotting…",
  },
  // Re-snapshot — refresh an ACTIVE member's already-issued snapshot to this month,
  // from the button in that card's header (beside Share). Only active memberships
  // carry the button; a departure certificate is sealed and never re-pulled.
  "career.orbit.resnapshot.action": { ja: "最新で撮り直す", en: "Re-snapshot" },
  "career.orbit.resnapshot.minting": {
    ja: "撮り直しています…",
    en: "Re-snapshotting…",
  },
  // Success toast after a card-header re-snapshot (the action moved from the old
  // separate section onto each active card, beside Share — #1075 → on-card).
  "career.orbit.resnapshot.done": {
    ja: "最新の軌道で撮り直しました。",
    en: "Re-snapshotted to your latest orbit.",
  },
  // Gravity Certificate — subject-pull mint on the affiliation record.
  "career.certificate.issue": {
    ja: "Gravity Certificate を発行",
    en: "Issue Gravity Certificate",
  },
  "career.certificate.issuing": { ja: "発行中…", en: "Issuing…" },
  "career.certificate.issued": {
    ja: "証明書を発行しました。Career で確認できます。",
    en: "Certificate issued — you can view it in Career Orbit.",
  },
  "career.certificate.viewInCareer": {
    ja: "Career で見る",
    en: "View in Career Orbit",
  },
  "career.certificate.intro": {
    ja: "この在籍記録を、退職時点で凍結した署名付きの Gravity Certificate として発行します。あなた自身の軌道のみが実名で、他のメンバーは匿名化されます。冪等なので何度押しても同じ証明書が返ります。",
    en: "Freeze this tenure into a platform-signed Gravity Certificate, fixed at departure. Only your own trajectory is identified; every other member stays anonymized. Idempotent — re-issuing returns the same certificate.",
  },
  "career.certificate.notAffiliated": {
    ja: "この銀河の在籍記録が見つかりません。",
    en: "No affiliation record found for this galaxy.",
  },
  "career.certificate.error": {
    ja: "証明書を発行できませんでした。",
    en: "Could not issue the certificate.",
  },
  // Public embed-card tile name for the Gravity Certificate (Cards & graphs).
  "career.certificate.card": { ja: "重力証明書", en: "Gravity Certificate" },
  // Career Orbit chrome — pending/accepted observation rows.
  "career.orbit.pendingTitle": {
    ja: "承認待ちの観測",
    en: "Pending Observations",
  },
  "career.orbit.accept": { ja: "承認して接続", en: "Accept & Connect" },
  "career.orbit.trajectoryTitle": {
    ja: "キャリア軌跡",
    en: "Career Trajectory",
  },
  "career.orbit.verified": { ja: "検証済み", en: "Verified" },
  "career.orbit.domains": { ja: "ドメイン", en: "Domains" },
  "career.orbit.darkMatter": {
    ja: "この観測結果はコードレベルのシグナルのみを反映しています。メンタリング、文化構築、リーダーシップなど、git に記録されない貢献はここには含まれません。",
    en: "This observation reflects code-level signals only. Contributions invisible to git — mentoring, culture, leadership — are not captured here.",
  },
  // Issued certificate card on the affiliation record.
  "career.certificate.title": {
    ja: "Gravity Certificate",
    en: "Gravity Certificate",
  },
  "career.certificate.issuedOn": { ja: "発行日", en: "Issued" },
  "career.certificate.period": { ja: "在籍期間", en: "Tenure" },
  "career.certificate.empty": {
    ja: "この銀河ではまだ観測された貢献がありません。",
    en: "No observed contributions in this galaxy yet.",
  },
  "career.certificate.universe": {
    ja: "観測された宇宙",
    en: "Observed universe",
  },
  "career.certificate.contributors": { ja: "人の貢献者", en: "contributors" },
  "career.certificate.totalGravity": { ja: "総重力", en: "total gravity" },
  "career.certificate.topShare": { ja: "最大集中度", en: "top concentration" },
  "career.certificate.trajectory": {
    ja: "アーキタイプの推移",
    en: "Archetype trajectory",
  },
  "career.certificate.archetype": { ja: "アーキタイプ", en: "Archetype" },
  "career.certificate.archetypeMonths": { ja: "ヶ月", en: "mo" },
  "career.certificate.metrics": {
    ja: "シグナルと重力",
    en: "Signal & gravity",
  },
  "career.certificate.avgGravity": { ja: "平均重力", en: "Avg gravity" },
  "career.certificate.peakGravity": { ja: "最大重力", en: "Peak gravity" },
  "career.certificate.accumGravity": { ja: "累計重力", en: "Total gravity" },
  "career.certificate.avgSignal": { ja: "平均シグナル", en: "Avg signal" },
  "career.certificate.peakSignal": { ja: "最大シグナル", en: "Peak signal" },
  "career.certificate.accumSignal": { ja: "累計シグナル", en: "Total signal" },
  "career.certificate.activePeriod": { ja: "活動期間", en: "Active tenure" },
  "career.certificate.orbit": { ja: "在籍期間の軌道", en: "Tenure orbit" },
  "career.certificate.orbitSignal": { ja: "個人シグナル", en: "signal" },
  "career.certificate.orbitGravity": { ja: "個人重力", en: "gravity" },
  "career.certificate.signedBy": {
    ja: "OrbitLens が署名",
    en: "Signed by OrbitLens",
  },
  "career.certificate.publicShareHint": {
    ja: "公開の検証ページを共有できます（URL を知っている人だけが閲覧でき、あなたの凍結値のみを含みます）。",
    en: "Share a public verification page (visible only to those with the URL, your frozen figures only).",
  },
  "career.certificate.copyLink": {
    ja: "検証リンクをコピー",
    en: "Copy verification link",
  },
  "career.certificate.linkGenerating": { ja: "生成中…", en: "Generating…" },
  "career.certificate.linkCopied": { ja: "コピー済", en: "Copied" },
  "career.certificate.openVerification": {
    ja: "検証ページを開く",
    en: "Open verification page",
  },
  // Share popover: one trigger opens a destination menu. downloadImage saves the
  // public, token-only card image (the same SVG the /cert/:token page uses as its
  // og:image) — the aggregate-anonymized artifact, never a raw observation.
  "career.certificate.share": { ja: "共有", en: "Share" },
  "career.certificate.downloadImage": {
    ja: "画像をダウンロード",
    en: "Download image",
  },
  // Social share intents. They open the SAME public /cert/:token verification
  // page minted by mintShareUrl — the only artifact cleared for sharing (self's
  // frozen figures + anonymized universe scalars; no 7-axis raw scores, no rank,
  // no other members). shareText carries telescope/identity vocabulary only: NO
  // numeric score, NO ranking, NO vanity (Badge #11 / anti-vanity).
  "career.certificate.shareText": {
    ja: "Gravity Certificate — 生き残ったコードの記録",
    en: "Gravity Certificate — the record of the code that survived",
  },
  "career.certificate.shareNative": { ja: "共有…", en: "Share…" },
  "career.certificate.shareOnX": { ja: "X で共有", en: "Share on X" },
  "career.certificate.shareOnFacebook": {
    ja: "Facebook で共有",
    en: "Share on Facebook",
  },
  "career.certificate.shareOnLinkedin": {
    ja: "LinkedIn で共有",
    en: "Share on LinkedIn",
  },
  "career.certificate.revoked": {
    ja: "この証明書は無効化されています。",
    en: "This certificate has been revoked.",
  },
  "career.certificate.statusAccepted": { ja: "有効", en: "Accepted" },
  "career.certificate.statusRevoked": { ja: "無効", en: "Revoked" },
  // size_bucket / collapse_state value labels (BE enums).
  "career.certificate.size.solo": { ja: "単独", en: "solo" },
  "career.certificate.size.small": { ja: "小規模", en: "small" },
  "career.certificate.size.team": { ja: "チーム", en: "team" },
  "career.certificate.size.large": { ja: "大規模", en: "large" },
  "career.certificate.size.massive": { ja: "巨大", en: "massive" },
  "career.certificate.collapse.stable": { ja: "安定軌道", en: "stable" },
  "career.certificate.collapse.thinning": { ja: "希薄化", en: "thinning" },
  "modules.title": { ja: "モジュールトポロジー", en: "Module Topology" },
  "sludge.title": {
    ja: "構造債（所有者不在の質量）",
    en: "Structural Debt (unowned mass)",
  },
  "sludge.subtitle": {
    ja: "生存するが誰も支えていないコード",
    en: "Surviving code no one sustains",
  },
  "sludge.massLine": {
    ja: "{sludge} / {surviving} 加重質量が所有者不在（{count} モジュール）",
    en: "{sludge} of {surviving} weighted mass is unowned ({count} modules)",
  },
  "sludge.worst": { ja: "寄与の大きいモジュール", en: "Top contributors" },
  "sludge.note": {
    ja: "所有権＋生存からの v1 残高。整形だけの'貢献'はまだ割引かない。",
    en: "v1 balance from ownership + survival; cosmetic 'contest' touches not yet discounted.",
  },
  "sludge.dir.rising": { ja: "増加中", en: "rising" },
  "sludge.dir.falling": { ja: "減少中", en: "falling" },
  "sludge.dir.flat": { ja: "横ばい", en: "flat" },
  "modules.subtitle": {
    ja: "モジュールの耐久性・変化圧・脆さ・所有集中を観測する。人軸は Constellation 側で扱う。",
    en: "Observe each module's durability, change pressure, fragility, and ownership concentration. People-level views live on Constellation.",
  },
  "modules.moduleCountUnit": { ja: "モジュール", en: "modules" },
  "modules.attention": { ja: "注視度", en: "Attention" },
  "modules.survival": { ja: "耐久性", en: "Survival" },
  "modules.survival.desc": {
    ja: "月単位で残り続けているコードの割合。低いほど置き換わりが速い。",
    en: "Fraction of code surviving month-over-month. Lower means faster churn.",
  },
  "modules.changePressure": { ja: "変化圧", en: "Change Pressure" },
  "modules.changePressure.desc": {
    ja: "単位期間あたりの変更量。高いほど改修が集中している。",
    en: "Change volume per period. Higher means concentrated rework.",
  },
  "modules.fragility": { ja: "脆さ", en: "Fragility" },
  "modules.fragility.desc": {
    ja: "小さな変更が周囲に連鎖しやすい度合い。高いほど壊れやすい。",
    en: "How easily small changes ripple across the code. Higher is more brittle.",
  },
  "modules.loading": {
    ja: "Module Topology を読み込み中...",
    en: "Loading module topology...",
  },
  "modules.noData": {
    ja: "モジュールデータがありません。まず観測を実行してください。",
    en: "No module data yet. Run an observation first.",
  },
  "modules.ownershipDistribution": {
    ja: "所有権分布",
    en: "Ownership Distribution",
  },
  "modules.soleOwner": { ja: "単独所有", en: "Sole Owner" },
  "modules.concentrated": { ja: "集中", en: "Concentrated" },
  "modules.healthy": { ja: "健全", en: "Healthy" },
  "modules.fragmented": { ja: "断片化", en: "Fragmented" },
  "modules.ownershipTitle": { ja: "モジュール所有", en: "Module Ownership" },
  "modules.ownershipSubtitle": {
    ja: "各モジュールの生存重力を誰が支えているか。赤は単独の担い手に依存し、その人が去るとモジュールが崩壊しうる状態（バスファクター=1）の観測。人の序列ではない。",
    en: "Who sustains each module's surviving code. Red marks a module held by a single steward — if they leave, the module risks collapse (bus factor = 1). This observes the code's fragility, not a person's rank.",
  },
  "modules.collapseRisk": { ja: "崩壊リスク", en: "Collapse risk" },
  // Orphaned Gravity — surviving gravity held by owners who have left the galaxy.
  "modules.departed": { ja: "退職", en: "Departed" },
  "modules.orphaned": { ja: "孤立", en: "Orphaned" },
  "modules.atRisk": { ja: "崩壊予備", en: "At Risk" },
  "modules.showDeparted": {
    ja: "退職者の保有を強調",
    en: "Show departed holdings",
  },
  "modules.orphanedTooltip": {
    ja: "生存重力の過半を、銀河を去った担い手が保持している。コードは生きているが担い手はいない＝崩壊リスクの観測。",
    en: "Over half of the surviving gravity is held by owners who left the galaxy — the code lives on with no resident steward (collapse risk).",
  },
  "modules.atRiskTooltip": {
    ja: "生存重力の一部を、銀河を去った担い手が保持している。bus factor の侵食を観測。",
    en: "Part of the surviving gravity is held by owners who left the galaxy — bus-factor erosion.",
  },
  "modules.departedTooltip": {
    ja: "この担い手は銀河を去っている（退職、または現GitHub org に不在）。その生存重力は孤立している。",
    en: "This owner has left the galaxy (departed, or absent from the live GitHub org). Their surviving gravity is orphaned.",
  },
  "modules.owners": { ja: "担い手", en: "Stewards" },
  "modules.maskedNote": {
    ja: "開示レベルにより匿名表示",
    en: "Anonymized by disclosure level",
  },
  "modules.peripheralTitle": {
    ja: "周辺（モジュール未認定）",
    en: "Peripheral (not yet a module)",
  },
  "modules.peripheralSubtitle": {
    ja: "区別された月数が足りず、まだモジュールとして認定されていないディレクトリ（リリース時のDML等）。観測は保持されている。",
    en: "Directories that haven't earned module-hood — touched in too few distinct months (e.g. release-time DML). Observation is preserved.",
  },
  "modules.ownershipEmpty": {
    ja: "観測待ち — observation を回すとモジュール所有が出ます。",
    en: "Awaiting observation — module ownership appears once an observation runs.",
  },
  "modules.you": { ja: "あなた", en: "you" },
  "modules.moduleList": { ja: "モジュール一覧", en: "Module List" },
  "modules.moduleName": { ja: "モジュール", en: "Module" },
  "modules.ownership": { ja: "所有集中", en: "Ownership Concentration" },
  "modules.ownership.desc": {
    ja: "1人 / 少数にコミットが集中している度合い。高いほど bus factor リスクが大きい。",
    en: "How concentrated commits are in a single / small set of hands. Higher means greater bus-factor risk.",
  },
  "modules.topAuthor": { ja: "トップ著者", en: "Top Author" },
  "modules.topShare": { ja: "占有率", en: "Top Share" },
  "modules.survivalRate": { ja: "生存率", en: "Survival Rate" },
  "modules.lines": { ja: "行数", en: "Lines" },
  "modules.authors": { ja: "著者数", en: "Authors" },
  "modules.commits": { ja: "コミット", en: "Commits" },
  "modules.structuralCoupling": { ja: "構造的結合", en: "Structural Coupling" },
  "modules.structuralCouplingSub": {
    ja: "同時変更の頻度が高いモジュールペア — 暗黙の依存関係を示す",
    en: "Module pairs frequently changed together — revealing implicit dependencies",
  },
  "modules.couplingStrength": { ja: "結合強度", en: "Coupling Strength" },
  "modules.cochangeCount": { ja: "同時変更回数", en: "Co-change Count" },
  "modules.noCoupling": {
    ja: "結合データがありません",
    en: "No coupling data available",
  },

  // ── Collapse Risk (executive summary) ──
  "collapse.title": { ja: "崩壊リスク", en: "Collapse Risk" },
  "collapse.subtitle": {
    ja: "誰かが抜けたら、どのモジュールが保守不能になるか。単独所有・退職による崩壊リスクを1枚で観測する。",
    en: "If someone leaves, which modules become unmaintainable. A single-screen reading of single-owner and departure-driven collapse risk.",
  },
  "collapse.spofCount": {
    ja: "単独障害点モジュール",
    en: "Single-point-of-failure modules",
  },
  "collapse.spofHint": {
    ja: "1人の担い手が生存重力の大半を握るモジュール（bus factor 1）",
    en: "Modules where one steward holds most of the surviving gravity (bus factor 1)",
  },
  "collapse.atRiskCount": {
    ja: "崩壊リスクのモジュール",
    en: "Modules at collapse risk",
  },
  "collapse.atRiskHint": {
    ja: "退職者が生存重力を握る、孤立・崩壊予備のモジュール",
    en: "Orphaned or at-risk modules whose surviving gravity is held by departed owners",
  },
  "collapse.concentrationStat": {
    ja: "重力の集中度",
    en: "Gravity concentration",
  },
  "collapse.concentrationHint": {
    ja: "観測対象の生存重力のうち、単独所有モジュールに集まっている割合",
    en: "Share of surviving gravity that sits in single-owner modules",
  },
  "collapse.rankingTitle": {
    ja: "リスク順モジュール",
    en: "Modules by collapse risk",
  },
  "collapse.rankingEmpty": {
    ja: "崩壊リスクのあるモジュールは観測されていません。",
    en: "No collapse-risk modules observed.",
  },
  "collapse.colModule": { ja: "モジュール", en: "Module" },
  "collapse.colRisk": { ja: "リスク", en: "Risk" },
  "collapse.colState": { ja: "状態", en: "State" },
  "collapse.colTopShare": { ja: "占有率", en: "Top share" },
  "collapse.colDeparted": { ja: "退職保有", en: "Departed" },
  "collapse.colSteward": { ja: "主担い手", en: "Top steward" },
  "collapse.simTitle": {
    ja: "離脱シミュレーション",
    en: "Departure simulation",
  },
  "collapse.simSubtitle": {
    ja: "担い手を1人選ぶと、その人が抜けたとき担い手を失う（孤児化 or bus factor 1）モジュールを観測する。",
    en: "Pick a steward to observe which modules lose their steward (orphaned or bus factor 1) if they leave.",
  },
  "collapse.simSelect": { ja: "担い手を選ぶ", en: "Select a steward" },
  "collapse.simNone": {
    ja: "この担い手の離脱で担い手を失うモジュールはありません。",
    en: "No modules lose their steward if this person leaves.",
  },
  "collapse.simResult": {
    ja: "が抜けると、次のモジュールが担い手を失います",
    en: "leaving leaves these modules without a steward",
  },
  "collapse.simModuleUnit": { ja: "モジュール", en: "modules" },
  "collapse.simSuccessor": { ja: "後継候補:", en: "Successor:" },
  "collapse.simNoSuccessor": {
    ja: "内部後継なし（引き継ぐ人がいない）",
    en: "No internal successor",
  },
  "collapse.simOrphaned": { ja: "孤児化", en: "orphaned" },
  // Nova gate teaser
  "collapse.gateTitle": {
    ja: "Nova で組織の崩壊リスクを観測",
    en: "Observe organizational collapse risk with Nova",
  },
  "collapse.gateBody": {
    ja: "崩壊リスクの要約は組織の解釈です。Nova で単独障害点・退職による孤立・離脱シミュレーションを観測できます。",
    en: "The collapse-risk rollup is an organizational interpretation. Nova surfaces single points of failure, departure-driven orphaning, and departure simulation.",
  },
  "collapse.gateCta": { ja: "Nova にアップグレード", en: "Upgrade to Nova" },

  // ── Collapse Risk dedicated triage page ──
  // In-context drill-in: the summary card links here for the full, all-modules
  // triage view (the summary caps at 8 rows; this page shows every risk module).
  "collapse.viewAll": {
    ja: "すべての崩壊リスクを見る →",
    en: "See all collapse risk →",
  },
  "collapse.page.title": {
    ja: "崩壊リスク・トリアージ",
    en: "Collapse Risk Triage",
  },
  "collapse.page.subtitle": {
    ja: "単独所有と退職による崩壊リスクを、全モジュールぶんリスク順に観測する。行を選ぶと担い手の内訳と離脱シミュレーションが出る。",
    en: "Every module carrying collapse risk from single ownership or departure, ranked. Select a row to drill into its owners and a departure simulation.",
  },
  "collapse.page.back": { ja: "← モジュールに戻る", en: "← Back to modules" },
  "collapse.page.filterConcentration": { ja: "集中度", en: "Concentration" },
  "collapse.page.filterAll": { ja: "すべて", en: "All" },
  "collapse.page.filterDomain": { ja: "ドメイン", en: "Domain" },
  "collapse.page.filterAllDomains": { ja: "すべて", en: "All domains" },
  "collapse.page.orphanedCount": {
    ja: "孤立モジュール",
    en: "Orphaned modules",
  },
  "collapse.page.atRiskOnlyCount": {
    ja: "崩壊予備モジュール",
    en: "At-risk modules",
  },
  "collapse.page.colOwnerCount": { ja: "担い手数", en: "Owners" },
  "collapse.page.ownerBreakdown": { ja: "担い手の内訳", en: "Owner breakdown" },
  "collapse.page.simHint": {
    ja: "この担い手が抜けると担い手を失う（孤児化 or bus factor 1）モジュール",
    en: "Modules that lose their steward (orphaned or bus factor 1) if this person leaves",
  },
  "collapse.page.selectRow": {
    ja: "行を選ぶと、その担い手の内訳と離脱シミュレーションを表示します。",
    en: "Select a row to see its owner breakdown and departure simulation.",
  },
  "collapse.recordHandover": { ja: "引き継ぎを記録", en: "Record handover" },
  "collapse.recordHandoverHint": {
    ja: "退職者の生存重力を年表に引き継ぎ記録として残します（編集前提のひな形を開きます）。",
    en: "Record this departed owner's surviving gravity as a handover in the chronicle (opens an editable skeleton).",
  },

  // Module Durability — GET /modules/durability, the compact "is this module
  // safe to change" read (survival + ownership concentration fused into a
  // fragile/moderate/robust label). Unlike Collapse Risk, this is NOT gated —
  // every plan sees the full list; only collapse_risk (shown as a subtle
  // chip) is Nova-only, and the backend simply omits it for Free/Pro.
  "durability.title": { ja: "モジュール耐久性", en: "Module Durability" },
  "durability.subtitle": {
    ja: "各モジュールの変更しやすさを、生存性と所有集中度から読む。脆弱→堅牢の順。全プランで表示。",
    en: "How safe each module is to change, read from survival and ownership concentration. Fragile → robust. Visible on every plan.",
  },
  "durability.empty": {
    ja: "耐久性の観測データがまだありません。",
    en: "No durability data observed yet.",
  },
  "durability.fragile": { ja: "脆弱", en: "Fragile" },
  "durability.moderate": { ja: "普通", en: "Moderate" },
  "durability.robust": { ja: "堅牢", en: "Robust" },
  "durability.colSurvival": { ja: "生存性", en: "Survival" },
  "durability.colConcentration": { ja: "所有集中", en: "Concentration" },
  "durability.maintainerCritical": { ja: "後継不在", en: "Bus factor 1" },
  "durability.maintainerElevated": { ja: "後継薄", en: "Thin coverage" },
  "durability.maintainerRisk": {
    ja: "保守中だが担い手が少ない：保守が止まり放置されるリスク",
    en: "Actively maintained but few contributors — at risk of maintenance stopping and being left to decay",
  },

  // ── Structural Summary ──
  "summary.title": { ja: "構造的サマリー", en: "Structural Summary" },
  "summary.principle": {
    ja: "シグナルは信号であり、判定ではない。この文脈は必須です。",
    en: "These are signals, not judgments. This context is mandatory.",
  },
  "summary.limitsTitle": { ja: "観測の限界", en: "Observation Limits" },
  "summary.limitsText": {
    ja: "EISはgit commitに記録された痕跡のみを観測します。コードレビュー、設計議論、メンタリング、プランニングなどcommitに現れない貢献（ダークマター）は観測できません。シグナルが低い＝貢献が少ないではありません。",
    en: "EIS observes only traces recorded in git commits. Code reviews, design discussions, mentoring, planning — contributions that don't appear in commits (dark matter) cannot be observed. Low signal ≠ low contribution.",
  },
  "summary.roleHeading": { ja: "チームにおける役割", en: "Role in Team" },
  "summary.trajectoryHeading": { ja: "軌跡と方向", en: "Trajectory" },
  "summary.role.architectBuilder": {
    ja: "{author}は設計し、自ら実装する — Architect/Builder。コードベースの構造を作りながら自分の手で重力を生んでいる。",
    en: "{author} designs and implements — Architect/Builder. Creating codebase structure while generating gravity with their own hands.",
  },
  "summary.role.architect": {
    ja: "{author}はArchitectとして構造の中心にいる。設計判断がコードベースの方向を定めている。",
    en: "{author} is at the structural center as Architect. Design decisions are shaping the direction of the codebase.",
  },
  "summary.role.anchor": {
    ja: "{author}はAnchorとしてチームの土台を支えている。これはダークマターに近い — 目立たないが、いなければ構造が崩れる。",
    en: "{author} supports the team's foundation as Anchor. This is close to dark matter — invisible but essential.",
  },
  "summary.role.producer": {
    ja: "{author}はProducerとして既存構造の上で生産している。量の貢献は構造が正しい前提で価値を持つ。",
    en: "{author} produces on top of existing structure as Producer. Volume contributions hold value when the structure is sound.",
  },
  "summary.role.cleaner": {
    ja: "{author}はCleanerとして他者が残した負債を清掃している。この仕事はエントロピーとの戦いそのもの。",
    en: "{author} cleans up debt left by others as Cleaner. This work is the fight against entropy itself.",
  },
  "summary.role.specialist": {
    ja: "{author}はSpecialistとして特定領域に深い影響を持つ。属人化リスクと専門性の価値は表裏一体。",
    en: "{author} has deep impact in a specific domain as Specialist. Bus factor risk and expertise value are two sides of the same coin.",
  },
  "summary.role.none": {
    ja: "{author}は現時点で明確なRoleが検出されていない。これは活動が少ないか、多軸に分散している可能性がある。",
    en: "{author} has no clear Role detected. This may indicate limited activity or widely dispersed contributions.",
  },
  // Role guide — "what makes someone this role". Criteria mirror the eis
  // scorer (internal/scorer/archetype.go); Ace only explains the role the
  // CLI decided, it never re-derives it.
  "roleGuide.title": { ja: "ロール判定ガイド", en: "Role guide" },
  "roleGuide.subtitle": {
    ja: "どの観測値が揃うとどのロールになるか",
    en: "Which observations make each role",
  },
  "roleGuide.active": { ja: "現在のロール", en: "current role" },
  "roleGuide.driver": { ja: "決め手", en: "driver" },
  "roleGuide.matchNote": {
    ja: "一致度は各ロールの条件のうち最も弱い軸で決まる（決め手）。最も一致したロールが現在のロール。",
    en: "Match is set by a role's weakest condition (its driver); the best-matching role is the current one.",
  },
  "roleGuide.level.high": { ja: "高", en: "high" },
  "roleGuide.level.mid": { ja: "中", en: "solid" },
  "roleGuide.level.low": { ja: "低", en: "low" },
  "roleGuide.criteria.architect": {
    ja: "設計力が高く、コードが圧力下でも生き残り（生存力が高い）、複数の領域に存在する（領域幅が中以上）。構造の中心。",
    en: "High Design influence, code that survives under pressure (high Survival), present across areas (solid Breadth). The structural center.",
  },
  "roleGuide.criteria.anchor": {
    ja: "触媒の値が高い — 自分のまだ生きているコードの上に、他者が後から生き残るコードを積んでいる。加えて自身の出力もある（生産量が中以上）。",
    en: "High Catalysis — others built surviving code on top of your still-living code — together with real output of your own (solid Production).",
  },
  "roleGuide.criteria.cleaner": {
    ja: "コードが生き残り（生存力が高い）、かつ他者の負債を能動的に清掃している（負債清掃が高い）。エントロピーと戦う役割。",
    en: "Durable code (high Survival) plus active cleanup of others' debt (high Debt Cleanup). The role that fights entropy.",
  },
  "roleGuide.criteria.producer": {
    ja: "意味のある生産量がある（生産量が中以上）。まだ Architect / Anchor などの条件には達していない段階。",
    en: "Meaningful output (solid Production), without yet meeting the Architect / Anchor conditions.",
  },
  "roleGuide.criteria.specialist": {
    ja: "特定領域に深く、生存は高いが横断は狭い（生存力が高く、領域幅が低い）。専門性と属人化は表裏一体。",
    en: "Deep in one area: high Survival but narrow Breadth. Expertise and bus-factor risk are two sides of the same coin.",
  },
  "roleGuide.none": {
    ja: "現時点で明確なロールは検出されていない。活動が少ないか、複数の軸に分散している可能性がある。",
    en: "No clear role yet — limited activity, or contributions spread across several axes.",
  },
  "roleGuide.note": {
    ja: "複数の条件に当てはまる場合、最も強く満たしたロールが表示される（優先度: Architect → Anchor → Cleaner → Producer → Specialist）。ロールは EIS が git から判定し、Ace はそれを解説するだけ。",
    en: "When several conditions match, the most strongly met role is shown (precedence: Architect → Anchor → Cleaner → Producer → Specialist). The role is decided by EIS from git; Ace only explains it.",
  },
  // Galaxy-morphology guide — the org-state legend that explains the
  // HealthHero's classification (Spiral / Irregular / …). Descriptions are
  // condensed from public/galaxy-guide.html; keep them in sync by hand.
  "galaxyGuide.title": { ja: "組織状態ガイド", en: "Galaxy guide" },
  "galaxyGuide.subtitle": {
    ja: "どの観測形状がどの分類になるか",
    en: "Which observed shape maps to each classification",
  },
  "galaxyGuide.active": { ja: "現在の形", en: "current shape" },
  "galaxyGuide.note": {
    ja: "銀河形態は EIS が観測した組織の構造的な「形」。望遠鏡は形を記述するだけで、良し悪しを判定しない。同じ形でも文脈によって意味は変わる。",
    en: "The galaxy classification is the structural shape EIS observes. The telescope describes the shape; it does not judge it — the same shape can mean different things in different contexts.",
  },
  "galaxyGuide.desc.spiral": {
    ja: "設計の重力核が生産を駆動している。Architect が構造を作り、その上で Producer / Anchor が星を形成する。構造と生産が両立した状態。",
    en: "Design's gravitational core drives production — Architects build structure, Producers and Anchors form stars on top. Structure and production coexist.",
  },
  "galaxyGuide.desc.elliptical": {
    ja: "成熟し変化に強い。構造は堅固でエントロピーは低いが、新しい構造の創造は少ない。安定と引き換えに革新性が下がりうる。",
    en: "Mature and change-resistant. Solid structure, low entropy — but little new structure is created. Stability may trade off against innovation.",
  },
  "galaxyGuide.desc.starburst": {
    ja: "急速に新領域へ拡張中。エネルギーは高いが構造はまだ形成途中。成長期に多く、ここから Spiral へ進化しうる。",
    en: "Rapidly expanding into new territory. High energy, structure still forming. Common in a growth phase; can evolve toward Spiral.",
  },
  "galaxyGuide.desc.nebula": {
    ja: "次世代のエンジニアが育つ環境。Builder のロールモデルがいて、Growing メンバーが学べる。星形成の条件が整っている。",
    en: "A nursery for the next generation. Builder role models exist and Growing members can learn — the conditions for star formation are present.",
  },
  "galaxyGuide.desc.irregular": {
    ja: "高出力だが構造的方向性がない。Architect が不在で、各メンバーがそれぞれの方向にコードを書いている状態。",
    en: "High output but no structural direction. No Architect present — each member codes in their own direction.",
  },
  "galaxyGuide.desc.cluster": {
    ja: "生産的だが束ねる構造がない。星は密集するが銀河の重力場を成していない。デリバリーは速いが設計の一貫性は弱い。",
    en: "Productive but nothing holds it together. Dense stars, no galactic field. Fast delivery, weak design coherence.",
  },
  "galaxyGuide.desc.collision": {
    ja: "2 つの力が衝突し、構造が乱れエネルギーが散逸している。常に火消しに追われる状態。衝突から Starburst が生まれることもある。",
    en: "Two forces colliding — structure disrupted, energy scattered. Constant firefighting, though Starburst can emerge from collision.",
  },
  "galaxyGuide.desc.dwarf": {
    ja: "コンパクトで安定。最小限のリソースで品質と秩序を維持している。派手さはないが崩壊しにくい。",
    en: "Compact and stable. Maintains quality and order with minimal resources — not flashy, but resistant to collapse.",
  },
  "galaxyGuide.desc.filament": {
    ja: "到達範囲は広いが深度は薄い。多くの領域に触れているが、どこにも重力の中心がない。探索フェーズに多い。",
    en: "Broad reach, thin depth. Touches many areas with no gravitational center anywhere. Common in an exploration phase.",
  },
  "galaxyGuide.desc.lenticular": {
    ja: "突出した特徴がない。バランスが取れているとも、特徴が形成途中とも言える。Spiral にも Elliptical にも進化しうる。他のどの分類にも当てはまらない場合のデフォルト。",
    en: "No dominant characteristic — balanced, or still forming an identity. Can evolve toward Spiral or Elliptical. The default when nothing else matches.",
  },
  "summary.total.elite": {
    ja: "代替不可能なレベル。",
    en: "Irreplaceable level.",
  },
  "summary.total.senior": {
    ja: "Total {signal}はシニア水準。EISで40超えは7軸すべてで一定以上を要求するため、簡単には到達しない。",
    en: "Total {signal} is senior level. Exceeding 40 in EIS requires solid performance across all 7 axes — not easy to achieve.",
  },
  "summary.total.mid": {
    ja: "Total {signal}はミドル水準。成長の途上にある。",
    en: "Total {signal} is mid-level. On the path of growth.",
  },
  "summary.total.junior": {
    ja: "Total {signal}はジュニア〜成長中。シグナルが低い原因は個人だけでなく、環境（仕様の不安定さ、設計の不在）にもあり得る。",
    en: "Total {signal} is junior to growing. Low signals can be caused not just by the individual, but by environment (unstable specs, lack of design).",
  },
  "summary.trajectory.up": {
    ja: "Total が {delta} 上昇。成長トレンドにある。",
    en: "Total increased by {delta}. On a growth trend.",
  },
  "summary.trajectory.down": {
    ja: "Total が {delta} 低下。一時的な変動か、構造的な変化か、文脈の確認が必要。",
    en: "Total decreased by {delta}. Whether temporary or structural, context verification is needed.",
  },
  "summary.trajectory.stable": {
    ja: "Total は安定している。",
    en: "Total is stable.",
  },
  "summary.trajectory.survivalUp": {
    ja: "Survival が大幅上昇（+{delta}）。書いたコードが残り始めている — 設計力の成長を示す。",
    en: "Survival significantly increased (+{delta}). Code is starting to survive — indicates growing design capability.",
  },
  "summary.trajectory.survivalDown": {
    ja: "Survival が低下（{delta}）。リファクタリングや仕様変更の影響の可能性がある。",
    en: "Survival decreased ({delta}). May reflect refactoring or spec changes.",
  },
  "summary.trajectory.designUp": {
    ja: "Design が上昇（+{delta}）。設計判断への関与が増えている。",
    en: "Design increased (+{delta}). Growing involvement in design decisions.",
  },
  "summary.trajectory.roleTransition": {
    ja: "Role が {from} → {to} に遷移。",
    en: "Role transitioned from {from} to {to}.",
  },
  "summary.relativity.strongTeamLow": {
    ja: "チーム平均 {avg} の中で {signal} は相対的に低いが、このチーム自体が高水準（Architect在籍、平均シニア水準）。強い重力場の中でのシグナルは、弱い重力場での同シグナルより難しい。",
    en: "{signal} is relatively low against the team average of {avg}, but this team itself is high-caliber (Architect present, senior-level average). Signals in a strong gravity field are harder to achieve than the same signals in a weak one.",
  },
  "summary.relativity.weakTeamHigh": {
    ja: "Architect不在のチームでの{signal}。設計の基準線がないため、相対シグナルが実力より高く出ている可能性がある。これはこの人の問題ではなく、チーム構造の問題。",
    en: "{signal} in a team without Architects. Without a design baseline, relative signals may be inflated. This is not an individual issue — it's a structural one.",
  },

  // ── Signal Explainability ──
  "explain.title": { ja: "シグナル解説", en: "Signal Explainability" },
  "explain.subtitle": {
    ja: "各軸が何を測り、なぜこの値になったかの解釈。半透明設計 — アルゴリズムの完全公開はゲーム化を招くため、意図と解釈を伝える。",
    en: "What each axis measures and why it signaled this way. Semi-transparent design — full algorithm disclosure invites gaming, so we communicate intent and interpretation.",
  },
  "explain.whatLabel": { ja: "何を測っているか", en: "What it measures" },
  "explain.howLabel": { ja: "どう算出されるか", en: "How it's calculated" },
  "explain.whyLabel": { ja: "この値の解釈", en: "Interpreting this value" },
  "explain.caveatLabel": { ja: "注意点", en: "Caveat" },
  "explain.totalLabel": {
    ja: "Total は7軸の加重平均（Survival 25%, Design 20%, Catalysis 15%, Debt Cleanup 15%, Production 10%, Breadth 10%, Indispensability 5%）。100点満点だが、7軸すべてで同時に高水準を出すのは構造的に難しく、40でシニア相当。この厳しさは意図的な設計。",
    en: "Total is a weighted average of 7 axes (Survival 25%, Design 20%, Catalysis 15%, Debt Cleanup 15%, Production 10%, Breadth 10%, Indispensability 5%). 100-point scale, but achieving high signals across all 7 axes simultaneously is structurally difficult — 40 equals senior level. This strictness is intentional.",
  },
  "explain.survival.what": {
    ja: "書いたコードが今も残っているか。時間減衰付き — 2年前のコードは重み0.02まで下がる。「今も残る設計」を最も重視する。",
    en: "Whether code you wrote still survives. Time-decayed — code from 2 years ago weighs only 0.02. Emphasizes 'designs that endure today'.",
  },
  "explain.survival.how": {
    ja: "git blameで現存する行を著者に帰属させ、指数減衰（τ=180日）で重み付け。ドメイン内の最大値で正規化。",
    en: "Attributes surviving lines via git blame, weighted by exponential decay (τ=180 days). Normalized against the domain maximum.",
  },
  "explain.survival.caveat": {
    ja: "Survivalが高くても、誰も触っていないから残っているだけ（Dormant）の可能性がある。Robust Survivalと合わせて解釈する必要がある。",
    en: "High Survival may simply mean nobody touched the code (Dormant). Must be interpreted alongside Robust Survival.",
  },
  "explain.survival.high": {
    ja: "書いたコードの大部分が現在も残っている。設計の耐久性が高い。",
    en: "Most code written still survives. High design durability.",
  },
  "explain.survival.mid": {
    ja: "一定量のコードが残っている。リファクタリングや仕様変更による一部置換もある。",
    en: "Fair amount of code survives. Some replacement from refactoring or spec changes.",
  },
  "explain.survival.lowWithProduction": {
    ja: "生産量の割にコードが残っていない。書いたコードが他のメンバーに置き換えられている可能性がある。ただしRescueスタイル（レガシー書き換え）の場合は低くて正常。",
    en: "Low for the production level. Code may be getting replaced. However, if this is a Rescue style (legacy rewrite), low Survival is expected.",
  },
  "explain.survival.low": {
    ja: "コードの残存量が少ない。活動量自体が少ないか、書いたコードが早期に置換された。",
    en: "Little code survives. Either low activity or early replacement.",
  },
  "explain.design.what": {
    ja: "アーキテクチャファイル（インターフェース定義、DI、ルーティング、スキーマ等）へのコミット数。設計判断への関与度の近似値。",
    en: "Commit count to architecture files (interfaces, DI, routing, schemas). An approximation of involvement in design decisions.",
  },
  "explain.design.how": {
    ja: "設定で定義されたアーキテクチャパターン（repository/*interface*, di/*.go, router.go等）に該当するファイルへのコミットを計数。ドメイン内最大値で正規化。",
    en: "Counts commits to files matching architecture patterns defined in config. Normalized against domain maximum.",
  },
  "explain.design.caveat": {
    ja: "「設計力」というより「設計関与度」の近似値。パターン定義がプロジェクトに合っていないと精度が出ない。逆に、パターンを定義する行為自体がチームの設計思想を言語化するプロセスになる。",
    en: "More 'design involvement' than 'design skill'. Accuracy depends on pattern definitions matching the project. Defining patterns itself becomes the process of articulating the team's design philosophy.",
  },
  "explain.design.high": {
    ja: "アーキテクチャファイルへのコミットが非常に多い。構造の中心にいる。",
    en: "Very high commits to architecture files. At the structural center.",
  },
  "explain.design.mid": {
    ja: "設計ファイルへの一定の関与がある。",
    en: "Moderate involvement in design files.",
  },
  "explain.design.low": {
    ja: "設計ファイルへの関与が少ない。構造の上で実装する側にいる。",
    en: "Limited involvement in design files. Operating on top of structure rather than creating it.",
  },
  "explain.production.what": {
    ja: "1日あたりの変更行数（insertions + deletions）。絶対値シグナル — 組織間で比較可能。",
    en: "Lines changed per day (insertions + deletions). Absolute scale — comparable across organizations.",
  },
  "explain.production.how": {
    ja: "著者の最初のコミットから最後のコミットまでの期間で総変更行数を割り、基準値（デフォルト1000行/日）で割って0-100にシグナル化。",
    en: "Total lines changed divided by active period, then divided by reference value (default 1000 lines/day) to scale 0-100.",
  },
  "explain.production.caveat": {
    ja: "生産量はエントロピーを増やす行為でもある。Productionだけ高くてSurvivalが低い場合、コードが残らない大量生産（Mass/Churn）の可能性がある。",
    en: "Production also increases entropy. High Production with low Survival may indicate Mass/Churn — code that doesn't last.",
  },
  "explain.production.high": {
    ja: "非常に高い変更量。コードベースへの物量的影響が大きい。",
    en: "Very high change volume. Significant quantitative impact on the codebase.",
  },
  "explain.production.mid": {
    ja: "安定した変更量。",
    en: "Steady change volume.",
  },
  "explain.production.low": {
    ja: "変更量が少ない。レビューや設計議論など、commit外の貢献（ダークマター）がある可能性。",
    en: "Low change volume. May have non-commit contributions (dark matter) like reviews or design discussions.",
  },
  "explain.debtCleanup.what": {
    ja: "他者が書いたコードの修正量。fixコミットで変更された行の元のblameを追跡し、「誰のコードを誰が直したか」を計測。",
    en: "How much code written by others you've fixed. Tracks original blame of lines changed in fix commits.",
  },
  "explain.debtCleanup.how": {
    ja: "fix系コミットで変更された行の変更前blameを調べ、他者のコードを直した比率を算出。50が中立（自分の負債と他者の負債が同量）。",
    en: "Examines pre-change blame of lines in fix commits. 50 is neutral (equal own vs others' debt).",
  },
  "explain.debtCleanup.caveat": {
    ja: "サンプル数に依存。fixコミットが10件未満の場合は参考値。コミットメッセージのfix/修正の判定精度にも影響される。",
    en: "Sample-dependent. Reference value only if fewer than 10 fix commits. Also affected by keyword detection accuracy.",
  },
  "explain.debtCleanup.high": {
    ja: "他者のコードを積極的に修正している。チームのエントロピーと戦う存在。",
    en: "Actively fixing others' code. Fighting team entropy.",
  },
  "explain.debtCleanup.mid": {
    ja: "自分の負債と他者の負債をバランスよく処理している。",
    en: "Balanced handling of own and others' debt.",
  },
  "explain.debtCleanup.low": {
    ja: "他者のコード修正は少ない。自分のコードの品質に集中しているか、負債清掃の機会がない。",
    en: "Limited fixing of others' code. Either focused on own quality or lacking cleanup opportunities.",
  },
  "explain.catalysis.what": {
    ja: "自分のまだ生存しているコードの上に、後から来た他者が積んだ生存貢献の量。「他者の土台になった度合い」。",
    en: "How much surviving work later contributors built on top of your still-living code — the degree to which you became a foundation for others.",
  },
  "explain.catalysis.how": {
    ja: "自分のコードがまだ生存するファイルで、自分より後に初貢献した著者の生存質量を合算。git log（初貢献日）+ git blame（生存）から算出。相対正規化。",
    en: "For each file where your own code still survives, sum the surviving mass of authors who first contributed after you. From git log (first-contribution dates) + git blame (survival). Relative scale.",
  },
  "explain.catalysis.caveat": {
    ja: "ファイル単位の先行性で測るため、著者同一性（未統合のエイリアス）に依存する。squash マージは初貢献日をぼかしうる。Survival/Indispensability と合わせて読む。",
    en: "Measured by file-level precedence, so it depends on consistent author identity (unmerged aliases split credit). Squash-merge can blur first-contribution dates. Read alongside Survival / Indispensability.",
  },
  "explain.catalysis.high": {
    ja: "他者が積極的に自分の土台の上に築き、その仕事が生き残っている。",
    en: "Others actively built on your foundation, and that work survives.",
  },
  "explain.catalysis.mid": { ja: "一般的な水準。", en: "Typical level." },
  "explain.catalysis.low": {
    ja: "他者が自分のコードの上に積んでいない、または自分が他者の土台の上で働いている。新規参画なら自然。Survival/Indispensability と合わせて孤立か新規かを判断。",
    en: "Either no one builds on your code, or you work atop others' foundations. Natural for newcomers. Compare with Survival / Indispensability to tell isolation from newness.",
  },
  "explain.breadth.what": {
    ja: "全リポジトリのうち何個にコミットがあるか。横断的に動ける力を測る。",
    en: "How many of the total repositories you've committed to. Measures cross-functional ability.",
  },
  "explain.breadth.how": {
    ja: "コミットが存在するリポジトリ数 / 全リポジトリ数 × 100。ドメイン内で正規化。",
    en: "Repos with commits / total repos × 100. Normalized within domain.",
  },
  "explain.breadth.caveat": {
    ja: "Breadthだけ高くて他が低い場合（Spreadスタイル）、広く浅く触っているだけの可能性がある。",
    en: "High Breadth with low everything else (Spread style) may indicate surface-level touching without deep contribution.",
  },
  "explain.breadth.high": {
    ja: "複数リポジトリに横断的にコミットしている。チーム全体を見渡す動きがある。",
    en: "Commits across multiple repositories. Shows cross-functional awareness.",
  },
  "explain.breadth.low": {
    ja: "限られたリポジトリでの活動。専門性の裏返しか、横断的な機会がないか。",
    en: "Activity in limited repositories. May reflect specialization or lack of cross-functional opportunity.",
  },
  "explain.indispensability.what": {
    ja: "自分だけが触ったモジュール数。影響度とバス係数リスクの両面を持つ。",
    en: "Number of modules only you've touched. Represents both impact and bus factor risk.",
  },
  "explain.indispensability.how": {
    ja: "各モジュールのblame行分布を調べ、80%以上を一人が占めるモジュール数を計測。ドメイン内で正規化。",
    en: "Examines blame distribution per module. Counts modules where one person holds 80%+. Normalized within domain.",
  },
  "explain.indispensability.caveat": {
    ja: "重みが5%と最も低いのは、「強いから必要」と「引き継がれていないから独占」のシグナルが混在するため。不可欠度は評価指標であると同時に、引き継ぎ優先度を示すアラート。",
    en: "Lowest weight at 5% because 'needed because strong' and 'monopolized because never handed off' are mixed signals. Both an evaluation metric and a handoff priority alert.",
  },
  "explain.indispensability.high": {
    ja: "複数のモジュールを独占的に所有している。強みであると同時にリスク。",
    en: "Exclusively owns multiple modules. Both a strength and a risk.",
  },
  "explain.indispensability.low": {
    ja: "モジュール所有は分散している。バス係数リスクは低い。",
    en: "Module ownership is distributed. Bus factor risk is low.",
  },

  // Gravity
  "explain.gravity.what": {
    ja: "コードベースに残した構造的影響力の総合指標。生き残るコードだけが重力を持つ。",
    en: "Aggregate measure of structural influence left on the codebase. Only surviving code carries gravity.",
  },
  "explain.gravity.how": {
    ja: "7軸の加重平均。survival(25%) + design(20%) + catalysis(15%) + debt_cleanup(15%) + production(10%) + breadth(10%) + indispensability(5%)",
    en: "Weighted average of 7 axes: survival(25%) + design(20%) + catalysis(15%) + debt_cleanup(15%) + production(10%) + breadth(10%) + indispensability(5%)",
  },
  "explain.gravity.caveat": {
    ja: "重力は活動量ではない。忙しさでは膨らまない。月単位のスケールで偽装できない唯一の指標。",
    en: "Gravity is not activity. It cannot be inflated by busyness. The only metric that cannot be gamed at monthly scale.",
  },
  "explain.gravity.high": {
    ja: "強い重力場を形成している。コードベースに深い構造的影響を残している。",
    en: "Forming a strong gravitational field. Leaving deep structural impact on the codebase.",
  },
  "explain.gravity.mid": {
    ja: "安定した重力を維持。継続的な構造的貢献が観測される。",
    en: "Maintaining stable gravity. Continuous structural contribution observed.",
  },
  "explain.gravity.low": {
    ja: "重力場は弱い。構造的影響がまだ蓄積されていない、または減衰している。",
    en: "Gravitational field is weak. Structural influence has not yet accumulated or is decaying.",
  },

  // General
  "general.loading": { ja: "読み込み中...", en: "Loading..." },
  "general.noData": { ja: "データなし", en: "No data" },
  "general.cancel": { ja: "キャンセル", en: "Cancel" },
  "general.close": { ja: "閉じる", en: "Close" },
  // Memory Layer (Events)
  "event.add": { ja: "ダークマターメモ", en: "Dark Matter Memo" },
  "event.title": { ja: "タイトル", en: "Title" },
  "event.titlePlaceholder": {
    ja: "gitに残らない貢献を記録する...",
    en: "Record what git can't see...",
  },
  "event.manual": { ja: "手動", en: "Manual" },
  "event.auto": { ja: "自動検出", en: "Auto-detected" },
  "event.milestone": { ja: "マイルストーン", en: "Milestone" },
  "event.risk": { ja: "リスク", en: "Risk" },
  "event.personal": { ja: "個人", en: "Personal" },
  "event.team": { ja: "チーム", en: "Team" },
  "event.noEvents": {
    ja: "この期間のイベントなし",
    en: "No events in this period",
  },
  "event.edit": { ja: "編集", en: "Edit" },
  "event.delete": { ja: "削除", en: "Delete" },
  "event.save": { ja: "保存", en: "Save" },
  "event.cancel": { ja: "キャンセル", en: "Cancel" },
  "event.planRequired": {
    ja: "Nova/Enterprise プランが必要です",
    en: "Nova/Enterprise plan required",
  },
  "event.events": { ja: "イベント", en: "Events" },
  "event.memoryLayer": { ja: "Memory Layer", en: "Memory Layer" },
  "event.personalLayer": { ja: "個人レイヤー", en: "Personal Layer" },
  "event.dimLightLayer": { ja: "Dim Light", en: "Dim Light" },
  "event.teamLayer": { ja: "チームレイヤー", en: "Team Layer" },
  "event.systemSignals": { ja: "システムシグナル", en: "System Signals" },
  "event.template": { ja: "テンプレート", en: "Template" },
  "event.tpl.tookOwnership": {
    ja: "モジュールのオーナーシップを取得",
    en: "Took ownership of module",
  },
  "event.tpl.ledRefactor": { ja: "リファクタリングを主導", en: "Led refactor" },
  "event.tpl.firstFeature": {
    ja: "初の主要機能を実装",
    en: "First major feature",
  },
  "event.tpl.stabilized": { ja: "モジュールを安定化", en: "Stabilized module" },
  "event.tpl.refactorStarted": {
    ja: "大規模リファクタリング開始",
    en: "Refactor started",
  },
  "event.tpl.majorLaunch": { ja: "メジャーローンチ", en: "Major launch" },
  "event.tpl.incident": { ja: "インシデント発生", en: "Incident occurred" },
  "event.tpl.archShift": {
    ja: "アーキテクチャシフト",
    en: "Architecture shift",
  },

  "galaxy.select": { ja: "Galaxy を選択", en: "Select Galaxy" },
  "galaxy.switch": { ja: "Galaxy を切り替え", en: "Switch Galaxy" },

  "general.viewConstellation": {
    ja: "Constellation を見る",
    en: "View Constellation",
  },
  "general.addRepos": { ja: "リポジトリを追加", en: "Add Repositories" },
  "general.welcome": {
    ja: "OrbitLens Ace へようこそ",
    en: "Welcome to OrbitLens Ace",
  },
  "general.welcomeSub": {
    ja: "リポジトリを追加して観測を始めましょう",
    en: "Start by adding repositories to observe.",
  },
  "general.startObservation": { ja: "観測を開始", en: "Start Observation" },
  "general.logout": { ja: "ログアウト", en: "Logout" },

  // Coming-soon (feature flags — disabled features in production)
  "common.soon": { ja: "準備中", en: "Soon" },
  "comingSoon.title": { ja: "準備中", en: "Coming Soon" },
  "comingSoon.body": {
    ja: "この機能は現在準備中です。近日公開予定なので、もうしばらくお待ちください。",
    en: "This feature isn't available yet. It's coming soon — stay tuned.",
  },

  // Top-level error boundary (uncaught render error —望遠鏡が一時的に見失った状態)
  "errorBoundary.title": {
    ja: "観測を一時的に見失いました",
    en: "Lost the signal for a moment",
  },
  "errorBoundary.body": {
    ja: "予期しないエラーで画面を表示できませんでした。再観測すると復帰できることがあります。続く場合はページを再読み込みしてください。",
    en: "Something unexpected stopped this view from rendering. Re-focusing often recovers it; if it persists, reload the page.",
  },
  "errorBoundary.retry": { ja: "再観測", en: "Re-focus" },
  "errorBoundary.reload": { ja: "再読み込み", en: "Reload page" },

  // Whole-server outage overlay (ServerWakingOverlay). On stg this is the idle
  // scale-to-0 window waking back up; in prod it would be a real outage. Copy is
  // neutral to both — it reconnects automatically and reloads on recovery.
  "serverWaking.title": {
    ja: "サーバーに接続しています…",
    en: "Reconnecting…",
  },
  "serverWaking.body": {
    ja: "サーバーが応答していません。自動的に再接続を試みています。数十秒ほどで戻ります。",
    en: "The server isn't responding. Reconnecting automatically — this usually takes under a minute.",
  },
  "serverWaking.retry": { ja: "今すぐ再読み込み", en: "Reload now" },

  // Observatory read failed (server is up, but a read errored) — shown instead of
  // the welcome/empty state so a failure never reads as "no repositories yet".
  "observatory.loadErrorTitle": {
    ja: "観測データを読み込めませんでした",
    en: "Couldn't load the observation data",
  },

  // Daily Observation (see docs/ace/daily-observation.md)
  // Observation cadence is monthly, so labels name honest cadences rather
  // than literal calendar days.
  "nav.daily": { ja: "今日の観測", en: "Daily" },
  "daily.title": { ja: "今日の観測", en: "Daily Observation" },
  "daily.subtitle": {
    ja: "構造的に何が変わったか — 望遠鏡の朝の走査",
    en: "What shifted in the structure — the telescope's morning scan",
  },
  "daily.window.latest": { ja: "最新", en: "Latest" },
  "daily.window.recent": { ja: "直近の傾き", en: "Recent slope" },
  "daily.window.trajectory": { ja: "軌跡", en: "Trajectory" },
  "daily.caption.latest": {
    ja: "最新 — 前回観測との差分。介入を要する悪化だけ。",
    en: "Latest — change since the previous observation. Intervention-worthy degradations only.",
  },
  "daily.caption.recent": {
    ja: "直近の傾き — 構造が動き始めたか。遅いシグナルの一階微分。",
    en: "Recent slope — did the structure start moving. First derivative of slow signals.",
  },
  "daily.caption.trajectory": {
    ja: "軌跡 — 耐久性と崩壊の長期推移。月単位で偽装できない真実層。",
    en: "Trajectory — the long run of durability and collapse. The unfakeable truth layer.",
  },
  "daily.focus": { ja: "観測", en: "Focus" },
  "daily.noActivityNote": {
    ja: "活動量はここに出ません（設計上）。survival は月で動くため、短期の差分は活動に化けます。",
    en: "Activity does not appear here (by design). Survival moves monthly, so a short-term delta would collapse into activity.",
  },
  "daily.trendNote": {
    ja: "遅いシグナルの一階微分。動き始めたかを読む粒度。",
    en: "First derivative of slow signals — the grain where movement first reads.",
  },
  "daily.trajectoryNote": {
    ja: "単調減衰の積分。月単位で偽装できない真実層。",
    en: "Integral of monotone decay — unfakeable at monthly scale.",
  },
  "daily.noEvents": {
    ja: "前回観測から、介入を要する構造の悪化は観測されていません。",
    en: "No intervention-worthy structural degradation since the last observation.",
  },
  "daily.noTrend": {
    ja: "傾きを出すには観測期間が足りません。",
    en: "Not enough observation periods to read a slope yet.",
  },
  "daily.noTrajectory": {
    ja: "軌跡を出すには観測期間が足りません。",
    en: "Not enough observation periods to draw a trajectory yet.",
  },
  "daily.loadError": {
    ja: "観測データの読み込みに失敗しました。",
    en: "Failed to load observation data.",
  },
  "daily.needMore": {
    ja: "差分を出すには2期以上の観測が必要です。まず観測を回してください。",
    en: "At least two observation periods are needed to diff. Run an observation first.",
  },
  // Succession — the knowledge-succession risks in the "latest" band. A module
  // that lost its clear steward names the vacated seat and offers a handover CTA.
  "daily.succession.vacatedSeat": {
    ja: "空席になった番人",
    en: "Vacated steward",
  },
  "daily.succession.departed": { ja: "退職", en: "Departed" },
  "daily.succession.recordHandover": {
    ja: "継承を記録",
    en: "Record handover",
  },
  "daily.succession.resolved": { ja: "継承済", en: "Handover recorded" },
  "daily.succession.handoverDesc": {
    ja: "{who} の知識継承 — {repo}/{module}（モジュールの離脱者保有 {departedShare}%）。\n引き継ぎメモ:\n- 設計意図:\n- 注意点:\n- 後任:",
    en: "Knowledge succession for {who} — {repo}/{module} (module departed-held {departedShare}%).\nHandover notes:\n- Design intent:\n- Gotchas:\n- Successor:",
  },

  // Ambient display — the always-on / standup READ surface
  // (docs/ace/ambient-display.md). Structural facts only; no counts, ranks or
  // streaks are ever rendered (anti-vanity gate). The three layers are the slow
  // monthly canvas, the daily discrete pulse, and the quiet present-state status.
  "nav.ambient": { ja: "壁面表示", en: "Ambient" },
  "ambient.title": { ja: "壁面表示", en: "Ambient" },
  "ambient.subtitle": {
    ja: "銀河の構造を一日中映す — 月次のキャンバス、今日のパルス、静かな現状。",
    en: "The galaxy's structure, on the wall all day — the monthly canvas, today's pulse, the quiet present.",
  },
  "ambient.mode.wall": { ja: "壁面", en: "Wall" },
  "ambient.mode.standup": { ja: "朝会", en: "Standup" },
  "ambient.mode.wallHint": {
    ja: "銀河全体。部屋の向こうから glance できる静謐な情報付き screensaver。",
    en: "Whole galaxy. A quiet, information-bearing screensaver you can glance at from across the room.",
  },
  "ambient.mode.standupHint": {
    ja: "チームスコープ。今日のパルスと現状を密に — 5分の朝会の粒度。",
    en: "Team scope. Today's pulse and present state, dense — the grain of a 5-minute standup.",
  },
  "ambient.loadError": {
    ja: "壁面表示の読み込みに失敗しました。",
    en: "Failed to load the ambient view.",
  },

  // Canvas (layer 1) — the slow monthly constellation of modules.
  "ambient.canvas.title": { ja: "構造の全景", en: "The constellation" },
  "ambient.canvas.caption": {
    ja: "モジュールごとの生存（輝度）と重力（質量）。月単位でしか動かない構造の静止画。",
    en: "Per-module survival (brightness) and gravity (mass). A still picture of structure that moves only by the month.",
  },
  "ambient.canvas.empty": {
    ja: "まだモジュールが観測されていません。リポジトリを接続して観測を回してください。",
    en: "No modules observed yet. Connect a repository and run an observation.",
  },

  // Pulse (layer 2) — today's discrete structural facts. This is the ONLY layer
  // that "changes a little every day" (discrete events, never a score delta).
  "ambient.pulse.title": { ja: "今日のパルス", en: "Today's pulse" },
  "ambient.pulse.caption": {
    ja: "前回観測からの構造的変化。担当の空席と、チームが記録した年表。",
    en: "Structural changes since the last observation — vacated seats and the chronicle the team recorded.",
  },
  "ambient.pulse.empty": {
    ja: "今日の構造イベントはありません。",
    en: "No structural events today.",
  },
  "ambient.pulse.emptyNote": {
    ja: "静かな日は壊れた日ではありません — パルスが静かなだけ。",
    en: "A quiet day is not a broken day — the pulse is simply still.",
  },

  // Pulse kind → localized structural sentence (the BE `detail` is English-only;
  // the FE maps kind → ja/en copy so the wall reads in locale). Subjects like
  // "module:auth" pass through unchanged.
  "ambient.kind.first_surviving_code": {
    ja: "新しい貢献者のコードが生存閾値を越えた",
    en: "a new contributor's code crossed the survival threshold",
  },
  "ambient.kind.module_stabilized": {
    ja: "安定相に入った（高生存・低変更圧）",
    en: "entered stable phase (high survival, low change pressure)",
  },
  "ambient.kind.steward_established": {
    ja: "明確な steward が現れた — バスファクターのリスクが解消",
    en: "a clear steward emerged — bus-factor risk resolved",
  },
  "ambient.kind.collapse_risk": {
    ja: "明確な担当が居なくなった — 誰が引き取る？",
    en: "the clear steward is gone — who picks this up?",
  },
  "ambient.actor.you": { ja: "あなた", en: "You" },
  "ambient.actor.departed": { ja: "離脱", en: "departed" },
  "ambient.kind.project_landed": {
    ja: "プロジェクトが着地した",
    en: "a project landed",
  },
  "ambient.kind.chronicle_entry_added": {
    ja: "年表に記録が増えた",
    en: "a chronicle entry landed",
  },

  // Status (layer 3) — the quiet present-state readout. A snapshot, never a
  // streak: no "N days stable" counter is ever shown.
  "ambient.status.title": { ja: "現状", en: "Present state" },
  "ambient.status.collapseRisk": { ja: "崩壊リスク", en: "Collapse risk" },
  "ambient.status.collapseRiskNone": { ja: "なし", en: "None" },
  "ambient.status.allStable": {
    ja: "全モジュール安定",
    en: "All modules stable",
  },
  "ambient.status.yes": { ja: "はい", en: "Yes" },
  "ambient.status.no": { ja: "いいえ", en: "No" },
  "ambient.status.modules": { ja: "モジュール", en: "Modules" },

  // Breadth decomposition (StarDetail — see docs/eis/breadth-redefinition.md)
  "breadthDecomp.title": { ja: "領域幅の分解", en: "Breadth decomposition" },
  "breadthDecomp.effectiveUnit": { ja: "実効モジュール", en: "eff. modules" },
  "breadthDecomp.subtitle": {
    ja: "生存重力を保持する module の実効数。repo の梱包に不変。",
    en: "Effective number of modules holding surviving gravity. Invariant to repo packaging.",
  },
  "breadthDecomp.empty": {
    ja: "module 別の生存重力データがまだありません（observation を回すと出ます）。",
    en: "No per-module gravity yet (run an observation to populate it).",
  },
  "breadthDecomp.emptyMonth": {
    ja: "この月（{month}）はこのメンバーの観測がありません。",
    en: "No observation of this member in {month}.",
  },
  "breadthDecomp.more": { ja: "他 {n} module", en: "+{n} more modules" },

  // Feedback tickets (user ↔ OrbitLens staff)
  "nav.feedback": { ja: "フィードバック", en: "Feedback" },
  "nav.admin": { ja: "運営", en: "Admin" },
  "feedback.title": { ja: "フィードバック", en: "Feedback" },
  "feedback.subtitle": {
    ja: "気づいたこと・引っかかったことを運営に送ってください。一緒に作り上げます。",
    en: "Tell us what you noticed or got stuck on. We build this with you.",
  },
  "feedback.tagline": {
    ja: "私たちは評価しない。観測する。",
    en: "We don't evaluate. We observe.",
  },
  "feedback.new": { ja: "新しいチケット", en: "New ticket" },
  "feedback.empty": {
    ja: "まだチケットはありません。最初の一件を送ってみてください。",
    en: "No tickets yet. Send your first one.",
  },
  "feedback.subject": { ja: "件名", en: "Subject" },
  "feedback.subjectPlaceholder": {
    ja: "何について？",
    en: "What's this about?",
  },
  "feedback.message": { ja: "本文", en: "Message" },
  "feedback.messagePlaceholder": {
    ja: "どの画面で、何を見ていて、何を感じたか",
    en: "Which screen, what you were looking at, what you felt",
  },
  "feedback.kind": { ja: "種類", en: "Kind" },
  "feedback.kind.feedback": { ja: "フィードバック", en: "Feedback" },
  "feedback.kind.bug": { ja: "不具合", en: "Bug" },
  "feedback.kind.question": { ja: "質問", en: "Question" },
  "feedback.send": { ja: "送信", en: "Send" },
  "feedback.sending": { ja: "送信中…", en: "Sending…" },
  "feedback.cancel": { ja: "キャンセル", en: "Cancel" },
  "feedback.reply": { ja: "返信", en: "Reply" },
  "feedback.replyPlaceholder": { ja: "返信を書く…", en: "Write a reply…" },
  "feedback.back": { ja: "← 一覧へ戻る", en: "← Back to list" },
  "feedback.you": { ja: "あなた", en: "You" },
  "feedback.staff": { ja: "運営", en: "OrbitLens" },
  "feedback.notFound": {
    ja: "チケットが見つかりません。",
    en: "Ticket not found.",
  },
  "feedback.loadError": {
    ja: "読み込みに失敗しました。",
    en: "Failed to load.",
  },
  "feedback.status.open": { ja: "オープン", en: "Open" },
  "feedback.status.awaiting_staff": {
    ja: "運営対応待ち",
    en: "Awaiting staff",
  },
  "feedback.status.awaiting_user": { ja: "返信待ち", en: "Awaiting you" },
  "feedback.status.resolved": { ja: "解決済み", en: "Resolved" },
  "feedback.status.closed": { ja: "クローズ", en: "Closed" },
  // Context-attached feedback
  "feedback.contextButton": {
    ja: "このビューにフィードバック",
    en: "Feedback on this view",
  },
  "feedback.attachedContext": { ja: "添付される情報", en: "Attached context" },
  "feedback.sentShort": { ja: "送信しました", en: "Sent" },

  // Feedback admin (staff inbox)
  "admin.tickets.title": { ja: "フィードバック運営", en: "Feedback Admin" },
  "admin.tickets.subtitle": {
    ja: "全ユーザーからのチケット。観測装置を一緒に磨く。",
    en: "Tickets from every user. Sharpening the telescope together.",
  },
  "admin.tickets.filterAll": { ja: "すべて", en: "All" },
  "admin.tickets.empty": { ja: "チケットはありません。", en: "No tickets." },
  "admin.tickets.markResolved": { ja: "解決済みにする", en: "Mark resolved" },
  "admin.tickets.reopen": { ja: "再オープン", en: "Reopen" },
  "admin.tickets.close": { ja: "クローズ", en: "Close" },

  // First-look guide (where to start observing) — observation, not evaluation
  "firstLook.title": {
    ja: "最初に観測する場所",
    en: "Where to start observing",
  },
  "firstLook.subtitle": {
    ja: "これは評価ではなく観測です。まずはこの3つを見てみてください。",
    en: "This is observation, not evaluation. Start by looking at these three.",
  },
  "firstLook.dismiss": { ja: "閉じる", en: "Dismiss" },
  "firstLook.observe": { ja: "観測する", en: "Observe" },
  "firstLook.concentration.title": {
    ja: "知識の集中",
    en: "Knowledge concentration",
  },
  "firstLook.concentration.desc": {
    ja: "重力が誰に集まっているか。属人化の地図を観測します。",
    en: "Who gravity is gathering around — the map of where knowledge concentrates.",
  },
  "firstLook.busfactor.title": { ja: "崩壊リスク", en: "Collapse risk" },
  "firstLook.busfactor.desc": {
    ja: "一人に支えられたモジュール、誰も触れていない孤立領域（バスファクター）。",
    en: "Modules held up by one person, and isolated ones no one touches (bus factor).",
  },
  "firstLook.risk.title": { ja: "組織のリスク", en: "Organization risk" },
  "firstLook.risk.desc": {
    ja: "このページ下のリスクカード — 設計の空白・土台形成の漂流・エントロピー。",
    en: "The risk cards below — design vacuum, catalysis drift, entropy.",
  },

  // "This observation was useful" — discovery signal (Ace's true KPI)
  "useful.prompt": {
    ja: "この観測は役に立った",
    en: "This observation was useful",
  },
  "useful.done": {
    ja: "ありがとう、観測の励みになります",
    en: "Thanks — this guides what we observe",
  },

  // D-05 disclosure — galaxy-view member row labels for members who have
  // NOT opted in. Observation/context members are ACTIVE, not excluded.
  "disclosure.private": { ja: "非開示", en: "Private" },
  "disclosure.signalsPrivate": { ja: "非開示", en: "Private" },
  "disclosure.active": { ja: "稼働中", en: "Active" },
  // Structural core reveal — a non-disclosed but load-bearing (core) member is
  // named with their role and this mark; numbers stay 非開示 (disclosure.private).
  "disclosure.core": { ja: "コア", en: "Core" },
  // Settings — self-service signal-disclosure toggle.
  "disclosure.settings.title": {
    ja: "この銀河での自分の見え方",
    en: "How you appear in this galaxy",
  },
  "disclosure.settings.desc": {
    ja: "この銀河でチームにどう見えるかを1か所で選びます。公開なら名前とシグナルが見え、非公開ならモジュールトポロジーなど構造画面も含めどの画面でも Member-N（名前もスコアも出ず、Active であることだけ伝わります）。席を持っていても非公開にでき、いつでも切り替えられます。",
    en: "Choose how your team sees you in this galaxy, in one place. Public shows your name and signals; private makes you Member-N on every surface — structural views like module topology included — with no name and no scores (only 'active' shows). You can go private even if you hold a seat, and switch back anytime.",
  },
  "disclosure.settings.on": {
    ja: "公開 — 名前とシグナルをチームに表示",
    en: "Public — name and signals shown to the team",
  },
  "disclosure.settings.off": {
    ja: "非公開 — どの画面でも Member-N（名前もスコアも出ません）",
    en: "Private — Member-N everywhere (no name, no scores)",
  },
  "disclosure.settings.openShareFullNote": {
    ja: "この銀河は「オープン共有（フル公開）」が有効です。公開（上をオン）にすると、あなたの実名とシグナルは、この銀河のログインを入力して自分で追加した不特定の第三者にも読み取り専用で見えます（招待された相手だけではありません）。非公開のままなら、どの共有相手にも Member-N で保護されます。",
    en: "This galaxy has Open Share set to Full. If you go public (toggle above), your real name and signals become visible read-only to any third party who self-adds this galaxy by login — not just invited viewers. Staying private keeps you Member-N to every shared viewer.",
  },

  // ── Auth (OAuth callback) ──
  "auth.authenticating": { ja: "認証中…", en: "Authenticating…" },
  "auth.failed": {
    ja: "サインインを完了できませんでした。もう一度お試しください。",
    en: "Couldn't complete sign-in. Please try again.",
  },

  // ── Errors (toUserMessage / errorMessage.ts) ──
  // One friendly, localized line per failure cause. The raw Go/git error is
  // kept in console.error for debugging — never shown to the user. Keep these
  // generic enough to read well wherever an operation fails.
  "errors.generic": {
    ja: "問題が発生しました。少し待ってから再試行してください。",
    en: "Something went wrong. Please wait a moment and try again.",
  },
  "errors.network": {
    ja: "ネットワークに接続できません。接続を確認して再試行してください。",
    en: "Can't reach the network. Check your connection and try again.",
  },
  // HTTP status classes.
  "errors.http.401": {
    ja: "セッションの有効期限が切れています。もう一度サインインしてください。",
    en: "Your session has expired. Please sign in again.",
  },
  "errors.http.403": {
    ja: "この操作を行う権限がありません。",
    en: "You don't have permission to do that.",
  },
  "errors.http.404": {
    ja: "対象が見つかりませんでした。",
    en: "We couldn't find what you were looking for.",
  },
  "errors.http.409": {
    ja: "他の変更と競合しました。最新の状態を確認して再試行してください。",
    en: "This conflicts with another change. Refresh and try again.",
  },
  "errors.http.422": {
    ja: "入力内容を確認してください。",
    en: "Please check your input and try again.",
  },
  "errors.http.429": {
    ja: "リクエストが多すぎます。少し待ってから再試行してください。",
    en: "Too many requests. Please wait a moment and try again.",
  },
  "errors.http.5xx": {
    ja: "サーバー側で問題が発生しました。少し待ってから再試行してください。",
    en: "Something went wrong on our side. Please try again shortly.",
  },
  // Structured BE codes.
  "errors.code.needsSeat": {
    ja: "この操作には有料の席が必要です。",
    en: "This action needs a paid seat.",
  },
  "errors.code.githubTokenExpired": {
    ja: "GitHub の認可が切れています。もう一度サインインしてください。",
    en: "Your GitHub authorization has expired. Please sign in again.",
  },
  "errors.code.noObservedOrbit": {
    ja: "この銀河ではまだ観測された貢献がありません。",
    en: "No observed contributions in this galaxy yet.",
  },
  "errors.code.repoLimitReached": {
    ja: "非公開リポは無料枠の1つまでです。Nova なら無制限に観測できます。",
    en: "The free plan observes up to 1 private repository. Upgrade to Nova for unlimited.",
  },
  "errors.code.repoTooLargeForPlan": {
    ja: "このリポジトリは無料プランには大きすぎます。Nova なら観測できます。",
    en: "This repository is too large for the free plan. Upgrade to Nova to observe it.",
  },
  "errors.code.observationQuotaExceeded": {
    ja: "本日の再観測の上限に達しました。時間をおいて試してください。",
    en: "You've reached today's re-observation limit. Try again later.",
  },
  // Git / worker failure causes (also surfaced via thrown messages).
  "errors.git.notFound": {
    ja: "リポジトリが見つかりません（移管・削除・権限なしの可能性）。",
    en: "Repository not found (it may be transferred, deleted, or out of reach).",
  },
  "errors.git.permission": {
    ja: "アクセス権限がありません。GitHub App の再認可が必要かもしれません。",
    en: "Permission denied — the GitHub App may need re-authorising.",
  },
  "errors.git.timeout": {
    ja: "処理がタイムアウトしました。少し待ってから再試行してください。",
    en: "The operation timed out. Please try again shortly.",
  },
  "errors.git.clone": {
    ja: "リポジトリの取得に失敗しました。",
    en: "Failed to fetch the repository.",
  },
  // Action-specific fallbacks — used as the tail when no structured cause
  // matched, so a failure reads as the action that failed (not "something
  // went wrong"). Pass as toUserMessage(err, locale, "errors.action.save").
  "errors.action.load": {
    ja: "読み込みに失敗しました。",
    en: "Couldn't load this.",
  },
  "errors.action.save": {
    ja: "保存に失敗しました。",
    en: "Couldn't save your changes.",
  },
  "errors.action.delete": {
    ja: "削除に失敗しました。",
    en: "Couldn't delete that.",
  },
  "errors.action.observe": {
    ja: "観測を開始できませんでした。",
    en: "Couldn't start the observation.",
  },
  "errors.action.accept": {
    ja: "受け入れに失敗しました。",
    en: "Couldn't accept that.",
  },
  "errors.action.revoke": {
    ja: "無効化に失敗しました。",
    en: "Couldn't revoke that.",
  },
  "errors.action.upload": {
    ja: "アップロードに失敗しました。",
    en: "Couldn't upload the file.",
  },
  "errors.action.update": {
    ja: "更新に失敗しました。",
    en: "Couldn't update that.",
  },
  "errors.action.sync": {
    ja: "同期に失敗しました。",
    en: "Couldn't sync.",
  },
  "errors.action.suggest": {
    ja: "提案の生成に失敗しました。",
    en: "Couldn't generate suggestions.",
  },
  "errors.invite.noCode": {
    ja: "招待コードがありません。",
    en: "No invite code provided.",
  },
  "errors.invite.accept": {
    ja: "招待の受け入れに失敗しました。",
    en: "Couldn't accept the invitation.",
  },

  // ── Settings → Preferences (language + timezone, client-persisted) ──
  "prefs.title": { ja: "表示設定", en: "Preferences" },
  "prefs.subtitle": {
    ja: "この端末での表示（言語・タイムゾーン）。観測データには影響しません。",
    en: "How things display on this device (language, time zone). Doesn't affect observation data.",
  },
  "prefs.language": { ja: "言語", en: "Language" },
  "prefs.languageDesc": {
    ja: "画面の表示言語。観測の期間ラベルは変わりません。",
    en: "Interface language. Observation period labels stay the same.",
  },
  "prefs.timezone": { ja: "タイムゾーン", en: "Time zone" },
  "prefs.timezoneDesc": {
    ja: "日時の表示に使うタイムゾーン。観測の月バケットは UTC 基準のまま変わりません。",
    en: "Time zone used to display timestamps. Observation month buckets stay UTC-anchored.",
  },
  "prefs.timezoneSystem": { ja: "端末の設定に従う", en: "Match this device" },

  // ── Settings → Preferences: avatar image cache (client-only) ──
  "prefs.avatarCache": {
    ja: "アバター画像キャッシュ",
    en: "Avatar image cache",
  },
  "prefs.avatarCacheDesc": {
    ja: "読み込めたアバターは端末に保存して再表示します。クリアすると全アバターを再取得します（古い・崩れた表示の修復に）。",
    en: "Loaded avatars are stored on this device for instant reuse. Clearing re-fetches them all (fixes stale or broken avatars).",
  },
  "prefs.avatarClearing": { ja: "クリア中…", en: "Clearing…" },
  "prefs.avatarClear": { ja: "キャッシュをクリア", en: "Clear cache" },

  // ── Settings (EIS Config) ──
  "settings.title": { ja: "EIS設定", en: "EIS Config" },
  "settings.subtitle": {
    ja: "この銀河の観測（EIS）の設定",
    en: "EIS observation config for this galaxy.",
  },
  // Auto-observation schedule (Pro/Nova): daily automatic observation.
  "settings.autoObservation.title": {
    ja: "観測の自動実行",
    en: "Automatic observation",
  },
  "settings.autoObservation.desc": {
    ja: "毎日決まった時間帯に、この銀河の観測を自動で実行します（Pro / Nova）。",
    en: "Automatically observe this galaxy at a set hour each day (Pro / Nova).",
  },
  "settings.autoObservation.proOnly": {
    ja: "観測の自動実行は Pro / Nova プランの機能です。",
    en: "Automatic observation is a Pro / Nova feature.",
  },
  "settings.autoObservation.upgrade": {
    ja: "プランをアップグレード",
    en: "Upgrade plan",
  },
  "settings.autoObservation.enableLabel": {
    ja: "毎日この時間帯に自動で観測する",
    en: "Observe automatically at this hour every day",
  },
  "settings.autoObservation.hourLabel": { ja: "実行時刻", en: "Run at" },
  "settings.autoObservation.timezoneLabel": {
    ja: "タイムゾーン",
    en: "Timezone",
  },
  "settings.autoObservation.reservedNote": {
    ja: "1日の観測枠のうち1枠を定期実行に確保します（手動実行は残りの枠で行います）。",
    en: "One of your daily observation slots is reserved for the scheduled run (manual triggers use the rest).",
  },
  "settings.autoObservation.requiresPaid": {
    ja: "観測の自動実行は Pro / Nova プランが必要です。",
    en: "Automatic observation requires a Pro or Nova plan.",
  },
  "settings.autoObservation.saveError": {
    ja: "保存に失敗しました。時間をおいて再度お試しください。",
    en: "Couldn't save the schedule. Please try again.",
  },
  "settings.autoObservation.save": { ja: "保存", en: "Save" },
  "settings.autoObservation.saving": { ja: "保存中…", en: "Saving…" },
  "settings.autoObservation.saved": { ja: "保存しました", en: "Saved" },
  // Observatory: reserved-slot note shown beside the daily quota when a schedule
  // is armed (the exact hour lives on the Settings page).
  "observatory.reservedSlot": {
    ja: "1枠は定期実行用に確保",
    en: "1 slot held for the scheduled run",
  },
  // eis.yaml import/export
  "settings.yaml.desc": {
    ja: "CLIの eis.yaml をアップロードして設定を反映。差分マージは既存設定に追加・上書き、全置換は設定を丸ごと入れ替えます。",
    en: "Upload your CLI's eis.yaml. Merge adds to existing config; Replace overwrites entirely.",
  },
  "settings.yaml.merge": { ja: "差分マージ", en: "Merge" },
  "settings.yaml.replace": { ja: "全置換", en: "Replace" },
  "settings.yaml.upload": { ja: "アップロード", en: "Upload" },
  "settings.yaml.download": { ja: "ダウンロード", en: "Download" },
  "settings.yaml.merged": { ja: "マージしました", en: "Merged!" },
  "settings.yaml.replaced": { ja: "全置換しました", en: "Replaced!" },
  // eis.yaml — generate with an AI agent
  "settings.eisAgent.title": {
    ja: "AI エージェントで eis.yaml を生成",
    en: "Generate eis.yaml with an AI agent",
  },
  "settings.eisAgent.desc": {
    ja: "書き方が分からない場合は、リポジトリ内で動く AI コーディングエージェント（Claude Code など）に下のプロンプトを渡すと、リポを観測して eis.yaml を生成できます。",
    en: "Not sure how to write it? Paste the prompt below into an AI coding agent (Claude Code etc.) running inside your repo — it observes the repo and generates a tailored eis.yaml.",
  },
  "settings.eisAgent.step1": {
    ja: "あなたのリポジトリで Claude Code などの AI エージェントを開く",
    en: "Open an AI agent like Claude Code inside your repository",
  },
  "settings.eisAgent.step2": {
    ja: "下のプロンプトをコピーして貼り付ける",
    en: "Copy the prompt below and paste it",
  },
  "settings.eisAgent.step3": {
    ja: "生成された eis.yaml をリポジトリ直下に保存する",
    en: "Save the generated eis.yaml at the repo root",
  },
  "settings.eisAgent.step4": {
    ja: "このページの「アップロード」から取り込む（差分マージ / 全置換）",
    en: "Import it via Upload on this page (Merge / Replace)",
  },
  "settings.eisAgent.step4Onboarding": {
    ja: "Settings の eis.yaml からアップロードする",
    en: "Upload it from the eis.yaml box in Settings",
  },
  "settings.eisAgent.copy": { ja: "コピー", en: "Copy" },
  "settings.eisAgent.copied": { ja: "コピーしました", en: "Copied!" },
  // Signaling config section group
  "settings.signaling.title": {
    ja: "EIS シグナリング設定",
    en: "EIS Signaling Config",
  },
  "settings.signaling.subtitle": {
    ja: "各セクションを展開して編集できます",
    en: "Expand each section to edit.",
  },
  // Section: author aliases
  "settings.aliases.title": { ja: "著者エイリアス", en: "Author Aliases" },
  "settings.aliases.desc": {
    ja: "git author名の正規化",
    en: "Canonical name mapping",
  },
  // Section: exclude authors
  "settings.excludeAuthors.title": { ja: "除外著者", en: "Exclude Authors" },
  "settings.excludeAuthors.desc": {
    ja: "bot・サービスアカウント等",
    en: "Bots, service accounts",
  },
  "settings.botCoAuthors.title": {
    ja: "AI/bot 共著者パターン",
    en: "AI/bot Co-author Patterns",
  },
  "settings.botCoAuthors.desc": {
    ja: "Co-authored-by の名前/メールに部分一致で共著者を除外（内蔵の Claude/Copilot/Cursor/Devin 等に追加）。squash/ペアの重力配分で人間だけに配る",
    en: "Exclude Co-authored-by contributors by name/email substring (adds to built-in Claude/Copilot/Cursor/Devin…). Keeps squash/pair gravity split to humans",
  },
  // Section: muted authors
  "settings.mutedAuthors.title": { ja: "ミュート著者", en: "Muted Authors" },
  "settings.mutedAuthors.desc": {
    ja: "シグナルに含むが表示は非表示",
    en: "Signal included, hidden from UI",
  },
  // Section: exclude file patterns
  "settings.excludeFiles.title": {
    ja: "除外ファイルパターン",
    en: "Exclude File Patterns",
  },
  "settings.excludeFiles.desc": {
    ja: "vendor, 生成コード等を除外",
    en: "Exclude vendor, generated code",
  },
  // Section: blame extensions
  "settings.blameExt.title": { ja: "Blame対象拡張子", en: "Blame Extensions" },
  "settings.blameExt.desc": {
    ja: "git blame分析の対象ファイル",
    en: "File types for blame analysis",
  },
  // Section: architecture patterns
  "settings.archPatterns.title": {
    ja: "アーキテクチャパターン",
    en: "Architecture Patterns",
  },
  "settings.archPatterns.desc": {
    ja: "アーキテクチャ層のパス識別",
    en: "Path patterns for arch layers",
  },
  // Section: exclude repos
  "settings.excludeRepos.title": { ja: "除外リポジトリ", en: "Exclude Repos" },
  "settings.excludeRepos.desc": {
    ja: "組織分析から除外するリポ",
    en: "Repos to exclude from org analysis",
  },
  // Section: module patterns
  "settings.modulePatterns.title": {
    ja: "モジュールパターン",
    en: "Module Patterns",
  },
  "settings.modulePatterns.desc": {
    ja: "モジュール識別のグロブパターン",
    en: "Glob patterns for module identity",
  },
  "settings.modulePatterns.help": {
    ja: "`*` は 1 つのパス要素にマッチ。例: `services/*` は `services/<X>` を各モジュールとして扱う。最初の「デフォルト」行はモジュールパターンを組織全体に適用。リポを指定した行はそのリポだけ、モジュールパターンに加えて除外・blame 拡張子・アーキテクチャパターン・Tau・サンプルサイズ・負債しきい値も上書きできます（未設定の項目は銀河デフォルトを継承）。デフォルト未設定なら EIS の built-in (`services/*`, `packages/*`, `apps/*`, `modules/*`, `libs/*`) を使用。",
    en: "`*` matches one path component. e.g. `services/*` treats `services/<X>` as a module. The first 'Default' row applies module patterns org-wide. Rows with a repo selected override — for that repo only — module patterns plus exclude lists, blame extensions, architecture patterns, tau, sample size and debt threshold (unset fields inherit the galaxy default). Empty default = EIS built-in (`services/*`, `packages/*`, `apps/*`, `modules/*`, `libs/*`).",
  },
  "settings.modulePatterns.defaultRow": {
    ja: "デフォルト（全リポジトリ）",
    en: "Default (all repos)",
  },
  "settings.modulePatterns.removeOverride": {
    ja: "オーバーライドを削除",
    en: "Remove override",
  },
  "settings.modulePatterns.addRepo": {
    ja: "+ リポジトリを追加",
    en: "+ Add Repo",
  },
  // Per-repo override field labels (repo_overrides). Each repo inherits the
  // galaxy config as the default and overrides only the fields set here.
  "settings.repoOverride.modulePatterns": { ja: "モジュールパターン（置換）", en: "Module patterns (replace)" },
  "settings.repoOverride.excludeModules": { ja: "除外モジュール（追加）", en: "Exclude modules (add)" },
  "settings.repoOverride.excludeFilePatterns": { ja: "除外ファイルパターン（追加）", en: "Exclude file patterns (add)" },
  "settings.repoOverride.blameExtensions": { ja: "blame 対象拡張子（置換）", en: "Blame extensions (replace)" },
  "settings.repoOverride.architecturePatterns": { ja: "アーキテクチャパターン（置換）", en: "Architecture patterns (replace)" },
  "settings.repoOverride.tau": { ja: "Tau（減衰）", en: "Tau (decay)" },
  "settings.repoOverride.sampleSize": { ja: "サンプルサイズ", en: "Sample size" },
  "settings.repoOverride.debtThreshold": { ja: "負債しきい値", en: "Debt threshold" },
  "settings.repoOverride.scalarPlaceholder": { ja: "銀河デフォルト", en: "galaxy default" },
  // Section: custom domains
  "settings.customDomains.title": {
    ja: "カスタムドメイン",
    en: "Custom Domains",
  },
  "settings.customDomains.desc": {
    ja: "シグナリング用ドメイン定義",
    en: "Domain definitions for signaling",
  },
  "settings.customDomains.help": {
    ja: "ここではドメイン名のみ登録します。リポへの割り当ては Repositories ページのドロップダウンから。",
    en: "Register the domain name here. Assign repos via the dropdown on the Repositories page.",
  },
  "settings.customDomains.placeholder": {
    ja: "ドメイン名 (例: ML, CLI)",
    en: "Domain name (e.g. ML, CLI)",
  },
  "settings.customDomains.add": { ja: "ドメインを追加", en: "Add Domain" },
  // API tokens
  "settings.tokens.title": { ja: "APIトークン", en: "API Tokens" },
  "settings.tokens.intro": {
    ja: "GitHub Enterprise や clone を許可しない環境向けに、CLI からシグナルをアップロードするためのトークンです。OrbitLens があなたのコードの内容を読むことはありません。git の履歴（git blame / git log）から算出したシグナルだけが天文台に届きます。",
    en: "A token for uploading signals from the CLI — for GitHub Enterprise or environments where cloning isn't allowed. OrbitLens never reads the contents of your code; only the signals computed from your git history (git blame / git log) reach the observatory.",
  },
  // Delete-token confirm (irreversible: re-issue + CLI/CI reconfig).
  "apiTokens.revoke.title": {
    ja: "このトークンを削除しますか？",
    en: "Delete this token?",
  },
  "apiTokens.revoke.body": {
    ja: "「{subject}」を削除します。",
    en: "Delete the token “{subject}”.",
  },
  "apiTokens.revoke.note": {
    ja: "取り消せません。このトークンでのアップロードは即座に無効になり、再発行と CLI/CI の再設定が必要です。",
    en: "This can't be undone. Uploads with this token stop immediately, and you'll need to reissue it and reconfigure your CLI/CI.",
  },
  "apiTokens.revoke.cancel": { ja: "キャンセル", en: "Cancel" },
  "apiTokens.revoke.confirm": { ja: "削除する", en: "Delete" },
  "settings.tokens.howTo": { ja: "使い方", en: "How to use" },
  "settings.tokens.cliRecommended": {
    ja: "CLI（推奨）",
    en: "CLI (recommended)",
  },
  "settings.tokens.endpoint": { ja: "エンドポイント", en: "Endpoint" },
  // How-to tab labels + the recommended marker on the GitHub Actions tab.
  "settings.tokens.tabGithubActions": {
    ja: "GitHub Actions",
    en: "GitHub Actions",
  },
  "settings.tokens.tabCli": { ja: "CLI", en: "CLI" },
  "settings.tokens.tabApi": { ja: "API", en: "API" },
  "settings.tokens.recommended": { ja: "推奨", en: "Recommended" },
  "settings.tokens.cliHint": {
    ja: "あなたのマシンから実行します。コードは外に出ません — 光（シグナル）だけが天文台に届きます。",
    en: "Run from your machine. Code never leaves it — only light (signals) reaches the observatory.",
  },
  "settings.tokens.enterpriseNote": {
    ja: "GitHub Enterprise や clone を許可しない環境向け。レート制限はプランに準じます。",
    en: "For GitHub Enterprise / no-clone setups. Rate limits follow your plan.",
  },
  "settings.tokens.githubActions": {
    ja: "GitHub Actions",
    en: "GitHub Actions",
  },
  "settings.tokens.githubActionsHint": {
    ja: ".github/workflows に追加するだけで CI からシグナルをアップロードできます。コードは runner から出ません。fetch-depth: 0（全履歴）が必須です。トークンは repo secret（ACE_API_TOKEN）に保存してください。",
    en: "Drop this into .github/workflows to upload signals from CI. Code never leaves the runner. fetch-depth: 0 (full history) is required. Store the token as a repo secret (ACE_API_TOKEN).",
  },
  "settings.tokens.apiDocs": { ja: "APIドキュメント →", en: "API docs →" },
  "settings.tokens.management": { ja: "トークン管理", en: "Token Management" },
  "settings.tokens.countUnit": { ja: "件のトークン", en: "tokens" },
  "settings.tokens.namePlaceholder": {
    ja: "トークン名 (例: my-laptop)",
    en: "Token name (e.g. my-laptop)",
  },
  "settings.tokens.generate": { ja: "生成", en: "Generate" },
  "settings.tokens.showOnce": {
    ja: "このトークンは一度だけ表示されます。コピーして安全に保管してください。",
    en: "This token is shown only once. Copy and store it securely.",
  },
  "settings.tokens.cliUsage": { ja: "CLI使用例:", en: "CLI usage:" },
  "settings.tokens.lastUsed": { ja: "最終使用:", en: "Last used:" },
  "settings.tokens.neverUsed": { ja: "未使用", en: "Never used" },
  "settings.tokens.expires": { ja: "期限:", en: "Expires:" },
  "settings.tokens.delete": { ja: "削除", en: "Delete" },

  // ── AI Integration (MCP) — the OrbitLens MCP context server. Exposes this
  // galaxy's observation data as variables for an AI coding agent, over a
  // dedicated mcp_ bearer token (separate family from the eis_ upload tokens
  // above). Mirrors the ApiTokens page's structure/keys almost verbatim. ──
  "nav.mcp": { ja: "AI 連携", en: "AI Integration" },
  "settings.mcp.title": { ja: "AI 連携", en: "AI Integration" },
  "settings.mcp.intro": {
    ja: "この銀河の観測データを、AI コーディングエージェント向けの変数として公開します。コードを読ませるのではなく、生存・重力・bus factor といった構造データを直接渡します。",
    en: "Expose this galaxy's observation data as variables for an AI coding agent — not by having it read your code, but by handing it structural data directly: survival, gravity, bus factor.",
  },
  // mcp-tokens / mcp-usage are adminOnly server-side (RequireAccountRole). The
  // nav row is hidden for a non-admin, but a direct URL still lands here, so
  // this notice + the gated management section below are defense in depth —
  // mirrors VisibilityMode / GalaxyLanguageSection's admin-only notice.
  "settings.mcp.adminOnly": {
    ja: "この銀河の管理者のみがトークンを発行・管理できます。",
    en: "Only an admin of this galaxy can generate and manage tokens.",
  },
  "settings.mcp.toolsTitle": { ja: "使えるツール", en: "Available tools" },
  "settings.mcp.tool.moduleDurability": {
    ja: "脆いモジュール／堅牢なモジュールを判定",
    en: "Which modules are fragile vs robust",
  },
  "settings.mcp.tool.assessChange": {
    ja: "編集前の pre-flight チェック",
    en: "Pre-flight check before you edit",
  },
  "settings.mcp.tool.knowledgeHolder": {
    ja: "bus factor の保持者を特定",
    en: "Who holds the bus-factor risk",
  },
  "settings.mcp.tool.blastRadius": {
    ja: "変更の波及範囲を見積もる",
    en: "Estimate a change's blast radius",
  },
  "settings.mcp.tool.authorSurvivalProfile": {
    ja: "匿名の生存プロファイル分布",
    en: "Anonymous survival profile distribution",
  },
  // Usage bar
  "settings.mcp.usage.title": { ja: "利用状況", en: "Usage" },
  "settings.mcp.usage.plan": { ja: "{plan} プラン", en: "{plan} plan" },
  "settings.mcp.usage.count": { ja: "{used} / {limit} 回（今時間）", en: "{used} / {limit} calls this hour" },
  "settings.mcp.usage.resetsAt": { ja: "{time} にリセット", en: "resets at {time}" },
  "settings.mcp.usage.upgradeCta": { ja: "プランをアップグレードして上限を上げる", en: "Upgrade for a higher limit" },
  // Propagation Effect — the effect-of-record card: which survival-proven anchors
  // the galaxy's AI-written code built on, and whether it survived.
  "settings.mcp.propagation.title": { ja: "伝播の効果", en: "Propagation Effect" },
  "settings.mcp.propagation.desc": {
    ja: "AIが書いたコードが実際にどの生存実績のあるアンカーの上に築かれ、そして生き残ったかを観測します。",
    en: "Which survival-proven anchors your AI-written code actually built on, and whether it survived.",
  },
  "settings.mcp.propagation.empty": {
    ja: "まだ伝播レシートはありません。MCP で接続した AI エージェントが、築いた土台（アンカー）を record_propagation_receipt で記録すると、生存判定とともにここに表示されます。",
    en: "No propagation receipts yet — when an AI agent connected over MCP records the anchors it builds on (record_propagation_receipt), they show here with a survival verdict.",
  },
  "settings.mcp.propagation.colAnchor": { ja: "アンカー", en: "Anchor" },
  "settings.mcp.propagation.colRate": { ja: "伝播率", en: "Transfer rate" },
  "settings.mcp.propagation.colSurvived": { ja: "生存 / 合計", en: "Survived / total" },
  "settings.mcp.propagation.receiptsTitle": { ja: "レシート", en: "Receipts" },
  "settings.mcp.propagation.verdict.survived": { ja: "生存", en: "survived" },
  "settings.mcp.propagation.verdict.collapsed": { ja: "崩壊", en: "collapsed" },
  "settings.mcp.propagation.verdict.pending": { ja: "保留", en: "pending" },
  // Connect how-to
  "settings.mcp.howTo.title": { ja: "接続方法", en: "How to connect" },
  "settings.mcp.howTo.tabAgent": { ja: "AIエージェント", en: "AI agent" },
  "settings.mcp.howTo.tabJson": { ja: ".mcp.json", en: ".mcp.json" },
  "settings.mcp.howTo.tabCli": { ja: "CLI", en: "CLI" },
  "settings.mcp.howTo.agentHint": {
    ja: "このプロンプトをAIエージェント（Claude Code / Cursor 等）に貼り付けるだけ。エージェントが接続を設定し、各ツールをどんな時に使うかを CLAUDE.md に記録します。手でファイルを保存する必要はありません。",
    en: "Just paste this prompt into your AI agent (Claude Code / Cursor, …). It sets up the connection and records into CLAUDE.md when to use each tool — no file to hand-save.",
  },
  "settings.mcp.howTo.jsonHint": {
    ja: "プロジェクトルートに .mcp.json を追加すると、Claude Code がこの銀河の観測データを変数として参照できます。",
    en: "Add .mcp.json to your project root so Claude Code can read this galaxy's observation data as variables.",
  },
  "settings.mcp.howTo.cliHint": {
    ja: "またはコマンド一発で登録できます。",
    en: "Or register it with one command.",
  },
  "settings.mcp.howTo.tokenNote": {
    ja: "mcp_your_token_here は、下のトークン管理で発行した mcp_ トークンに置き換えてください。",
    en: "Replace mcp_your_token_here with an mcp_ token you generate below.",
  },
  // Token management
  "settings.mcp.management": { ja: "トークン管理", en: "Token Management" },
  "settings.mcp.countUnit": { ja: "件のトークン", en: "tokens" },
  "settings.mcp.namePlaceholder": {
    ja: "トークン名 (例: claude-code)",
    en: "Token name (e.g. claude-code)",
  },
  "settings.mcp.generate": { ja: "生成", en: "Generate" },
  "settings.mcp.showOnce": {
    ja: "このトークンは一度だけ表示されます。コピーして安全に保管してください。",
    en: "This token is shown only once. Copy and store it securely.",
  },
  "settings.mcp.agentPromptUsage": {
    ja: "このプロンプトをAIエージェントに貼り付け（推奨）:",
    en: "Paste this prompt into your AI agent (recommended):",
  },
  "settings.mcp.configUsage": {
    ja: "または、この設定を手で保存:",
    en: "Or save this config by hand:",
  },
  "settings.mcp.lastUsed": { ja: "最終使用:", en: "Last used:" },
  "settings.mcp.neverUsed": { ja: "未使用", en: "Never used" },
  "settings.mcp.expires": { ja: "期限:", en: "Expires:" },
  "settings.mcp.delete": { ja: "削除", en: "Delete" },
  // Delete-token confirm (irreversible: re-issue + agent-side reconfig).
  "mcpTokens.revoke.title": {
    ja: "このトークンを削除しますか？",
    en: "Delete this token?",
  },
  "mcpTokens.revoke.body": {
    ja: "「{subject}」を削除します。",
    en: "Delete the token “{subject}”.",
  },
  "mcpTokens.revoke.note": {
    ja: "取り消せません。このトークンでの MCP 接続は即座に無効になり、AI エージェント側の設定を再発行・更新する必要があります。",
    en: "This can't be undone. MCP access with this token stops immediately, and you'll need to reissue it and update your AI agent's config.",
  },
  "mcpTokens.revoke.cancel": { ja: "キャンセル", en: "Cancel" },
  "mcpTokens.revoke.confirm": { ja: "削除する", en: "Delete" },

  // ── Embeds (Badges & Graphs) ──
  "embeds.title": { ja: "バッジ＆グラフ", en: "Badges & Graphs" },
  "embeds.subtitle": {
    ja: "観測されたシグナルを README に埋め込めます。スニペットをコピーして貼り付けるだけです。",
    en: "Embed your observed signal in a README. Copy a snippet and paste it in.",
  },
  "embeds.previewUnavailable": {
    ja: "プレビューを読み込めません（まだ観測がない可能性があります）。",
    en: "Preview unavailable (no observation yet).",
  },
  "embeds.copied": { ja: "コピーしました", en: "Copied!" },
  "embeds.copy": { ja: "コピー", en: "Copy" },
  "embeds.refresh": { ja: "プレビュー更新", en: "Refresh previews" },
  "embeds.refreshHint": {
    ja: "再観測した直後など、キャッシュを無視して最新のカードを取得します。",
    en: "Fetch the latest cards, bypassing the cache (e.g. right after re-observing).",
  },
  // Tab labels
  "embeds.tab.personal": { ja: "個人", en: "Personal" },
  "embeds.tab.team": { ja: "チーム", en: "Team" },
  "embeds.tab.galaxy": { ja: "銀河", en: "Galaxy" },
  // Aggregate scope nouns (interpolated into card titles/descs below)
  "embeds.aggregate.team": { ja: "チーム", en: "team" },
  "embeds.aggregate.galaxy": { ja: "銀河（組織）", en: "the galaxy" },
  // Member (personal) embed specs
  "embeds.member.gravityBadge.title": { ja: "重力バッジ", en: "Gravity Badge" },
  "embeds.member.gravityBadge.desc": {
    ja: "あなた個人の生き残ったコードの重み（gravity）を示す小さなバッジ。活動量ではなく、残った貢献。ラベルは「eis gravity」。README に貼れます。",
    en: 'A compact badge of YOUR gravity — the code you wrote that is still alive. Not activity; what survived. Labelled "eis gravity". Paste it in your README.',
  },
  "embeds.member.badge.title": { ja: "シグナルバッジ", en: "Signal Badge" },
  "embeds.member.badge.desc": {
    ja: "あなた個人の、7軸を合成した総合シグナルとアーキタイプを示す小さなバッジ。ラベルは「eis signal」。",
    en: 'A compact badge of YOUR composite 7-axis signal and archetype. Labelled "eis signal".',
  },
  "embeds.member.gravity.title": {
    ja: "重力タイムライン",
    en: "Gravity Timeline",
  },
  "embeds.member.gravity.desc": {
    ja: "月ごとの重力（構造的影響力）の推移（直近約1年）。",
    en: "Your month-by-month gravity (structural impact) over the last ~year.",
  },
  "embeds.member.radar.title": { ja: "7軸レーダー", en: "7-axis Radar" },
  "embeds.member.radar.desc": {
    ja: "現在の7軸 EIS シグナル。",
    en: "Your current 7-axis EIS signal.",
  },
  "embeds.member.orbit.title": {
    ja: "シグナル推移（時系列）",
    en: "Signal Trajectory",
  },
  "embeds.member.orbit.desc": {
    ja: "総合シグナルの時系列推移を折れ線で。",
    en: "Your total signal over time, as a line.",
  },
  "embeds.member.archetype.title": { ja: "アーキタイプ", en: "Archetype" },
  "embeds.member.archetype.desc": {
    ja: "Role × Style × State のアイデンティティ。",
    en: "Your role × style × state identity.",
  },
  // Aggregate embed specs ({aggregate} interpolated)
  "embeds.agg.gravityBadge.title": {
    ja: "集計重力バッジ",
    en: "Aggregate Gravity Badge",
  },
  "embeds.agg.gravityBadge.desc": {
    ja: "個人ではなく{aggregate}全体の平均重力（生き残ったコードの構造的質量）を示す小さなバッジ。ラベルは「eis galaxy gravity」で個人バッジと区別されます。",
    en: 'A compact badge of {aggregate}\'s average gravity — the surviving structural mass of the whole team, not one person. Its label reads "eis galaxy gravity", so it never reads like the personal badge.',
  },
  "embeds.agg.badge.title": { ja: "集計バッジ", en: "Aggregate Badge" },
  "embeds.agg.badge.desc": {
    ja: "個人ではなく{aggregate}全体の平均シグナルとタイプを示す小さなバッジ。",
    en: "A compact badge of {aggregate}'s average signal and morphology type — the whole team, not one person.",
  },
  "embeds.agg.observatory.title": {
    ja: "観測所サマリー",
    en: "Observatory Summary",
  },
  "embeds.agg.observatory.desc": {
    ja: "{aggregate}の集計シグナル（コアメンバー・残存率・7軸レーダーなど）。",
    en: "{aggregate}'s aggregate signal — core members, retention, 7-axis radar.",
  },
  "embeds.agg.orbit.title": { ja: "軌道（時系列）", en: "Orbit (timeline)" },
  "embeds.agg.orbit.desc": {
    ja: "{aggregate}シグナルの時系列推移を折れ線で（平均＋合計の2軸）。",
    en: "{aggregate}'s signal trajectory over time, as lines (avg + total).",
  },
  "embeds.agg.handover.title": {
    ja: "世代交代（ヒートマップ）",
    en: "Generational Handover",
  },
  "embeds.agg.handover.desc": {
    ja: "各期間を担った Architect/Anchor を行×期間のヒートマップで。各世代のリーダーに王冠。",
    en: "Who carried each generation: an Architect/Anchor × period heatmap, a crown on each generation's leader.",
  },
  // Period pin + repository controls
  "embeds.pinPeriod": { ja: "期間を固定", en: "Pin a period" },
  "embeds.fromMonth": { ja: "開始月", en: "From month" },
  "embeds.toMonth": { ja: "終了月", en: "To month" },
  "embeds.clear": { ja: "解除", en: "Clear" },
  "embeds.repository": { ja: "リポジトリ", en: "Repository" },
  "embeds.scopeToRepo": { ja: "リポジトリで絞る", en: "Scope to a repository" },
  "embeds.allWholeGalaxy": {
    ja: "すべて（銀河全体）",
    en: "All (whole galaxy)",
  },
  // Domain controls
  "embeds.domain": { ja: "ドメイン", en: "Domain" },
  "embeds.allDomains": { ja: "すべて", en: "All" },
  // Personal tab
  "embeds.personal.signInHint": {
    ja: "ログインするとあなたのGitHubログイン名が自動で入ります。",
    en: "Sign in to autofill your GitHub login.",
  },
  // Bridge from the per-repo/per-galaxy badges to the whole cross-galaxy footprint
  // (the career card at /me/career). "Badge = doorway, career card = the full story."
  "embeds.personal.footprintCta.body": {
    ja: "これらは1リポ・1銀河単位の観測です。あなたの銀河横断の全キャリア（本編）は1枚にまとまります。",
    en: "These are per-repo / per-galaxy snapshots. Your whole cross-galaxy career fits on one page.",
  },
  "embeds.personal.footprintCta.link": {
    ja: "全footprintを見る →",
    en: "See your whole footprint →",
  },
  // Team tab
  "embeds.team.noneHint": {
    ja: "部署/チームがまだありません。Teams ページで作成してください。",
    en: "No departments/teams yet. Create them on the Teams page.",
  },
  "embeds.team.deptTeam": { ja: "部署 / チーム", en: "Department / Team" },
  "embeds.team.pickDeptTeam": {
    ja: "部署またはチームを選択",
    en: "Pick a department or team",
  },
  "embeds.team.select": { ja: "選択してください", en: "Select…" },
  "embeds.team.wholeDept": {
    ja: "{name}（部署全体）",
    en: "{name} (whole department)",
  },
  "embeds.team.aggregateNote": {
    ja: "選んだ部署/チームのメンバーに絞った集計です。集計値のみで個人は特定されません（N<5 は集計を抑制）。",
    en: "Aggregated over the selected department/team's members. Aggregate only — no individual is named (suppressed below N<5).",
  },
  "embeds.team.pickHint": {
    ja: "部署またはチームを選ぶと埋め込みが表示されます。",
    en: "Pick a department or team to see its embeds.",
  },
  // Galaxy tab
  "embeds.galaxy.aggregateNote": {
    ja: "集計値のみで個人は特定されません。組織やリポジトリの README に貼れます。",
    en: "Aggregate only — no individual is named. Safe for an org or repo README.",
  },

  // ── Plan (per-galaxy plan / billing) ──
  "plan.title": { ja: "プラン", en: "Plan" },
  "plan.subtitle.org": {
    ja: "チームの天文台を設計する",
    en: "Design your team observatory",
  },
  "plan.subtitle.personal": {
    ja: "望遠鏡の性能を選ぶ",
    en: "Choose your telescope",
  },
  // Current plan card
  // Seat-cost line, split so a Nova under the 8-seat base never shows the
  // confusing "+ 課金 0 席 × $18" term. `included` = still within the base (no
  // overage); `overage` = seats past the base, where the per-seat charge is real.
  "plan.seatLine.included": {
    ja: "{seats} 席 · ${total}/月（{included} 席まで基本料金に込み）",
    en: "{seats} seat{plural} · ${total}/mo ({included} seats included in the base)",
  },
  "plan.seatLine.overage": {
    ja: "{seats} 席 · 基本 ${base} + 追加 {billed} 席 × ${perSeat} = ${total}/月",
    en: "{seats} seat{plural} · ${base} base + {billed} × ${perSeat} = ${total}/mo",
  },
  "plan.currentPlan": { ja: "現在のプラン", en: "Current plan" },
  "plan.cadence.yearly": { ja: "年払い", en: "Yearly" },
  "plan.cadence.monthly": { ja: "月払い", en: "Monthly" },
  "plan.renews": { ja: " · 次回更新 {date}", en: " · renews {date}" },
  "plan.cancels": {
    ja: "{date} に解約予定（それまで有効）",
    en: "Cancels {date} (active until then)",
  },
  "plan.manageBilling.opening": { ja: "開いています", en: "Opening" },
  "plan.manageBilling": { ja: "請求を管理", en: "Manage billing" },
  "plan.sync.syncing": { ja: "同期中", en: "Syncing" },
  "plan.sync.done": { ja: "完了", en: "Done" },
  "plan.sync": { ja: "同期", en: "Sync" },
  // Specs header + toggle
  "plan.specs.org": { ja: "天文台のスペック", en: "Observatory Specs" },
  "plan.specs.personal": { ja: "望遠鏡のスペック", en: "Telescope Specs" },
  // Plan defs — personal
  "plan.free.subtitle": { ja: "望遠鏡を手に入れる", en: "Get your telescope" },
  "plan.pro.subtitle": {
    ja: "望遠鏡をアップグレード",
    en: "Upgrade your telescope",
  },
  "plan.pro.subtitle.publicOrg": {
    ja: "公開天文台をアップグレード",
    en: "Upgrade your public observatory",
  },
  // Plan defs — org
  "plan.starter.subtitle": {
    ja: "天文台の観測を始める",
    en: "Start your observatory",
  },
  "plan.nova.subtitle": {
    ja: "天文台をスケールする",
    en: "Scale your observatory",
  },
  // Feature lists (one key per line; interpolated where pricing values appear)
  "plan.feat.unlimitedPublic": {
    ja: "公開リポジトリは無制限",
    en: "Unlimited public repos",
  },
  "plan.feat.upTo1Private": {
    ja: "非公開リポジトリは1つまで",
    en: "Up to 1 private repo",
  },
  "plan.feat.oneObsPerDay": { ja: "1日1回の観測", en: "1 observation / day" },
  // Paid manual cadence + automatic daily schedule. Free = 1/day, Pro/Nova =
  // 3/day (MaxObservationsPerDay) AND can arm the daily auto-observation cron
  // (observation_schedule.go; Free is 402). Marketed from 2026-07 per the GTM
  // decision to surface them (was in-app-only; see pricing-v2.md).
  "plan.feat.threeObsPerDay": {
    ja: "1日3回まで手動観測",
    en: "Up to 3 manual observations / day",
  },
  "plan.feat.autoDaily": {
    ja: "自動日次観測（スケジュール）",
    en: "Automatic daily observation",
  },
  // Starter card phrases the same cadence differently ("観測 1日1回"); kept as its
  // own key so reusing oneObsPerDay never silently rewrites the Japanese wording.
  "plan.feat.oneObsPerDayStarter": {
    ja: "観測 1日1回",
    en: "1 observation / day",
  },
  // The 6-month timeline window is a PRIVATE-repo limit only. Public galaxies are
  // uncapped by nature (docs/design/oss-public-tier.md; galaxy_view.go exempts
  // them from the clamp), so the card must say so or a public-repo user reads the
  // clamped timeline as a bug.
  "plan.feat.sixMonthsHistory": {
    ja: "履歴は6ヶ月（非公開リポのみ・公開はフル）",
    en: "6-month history (private repos only — public is full)",
  },
  "plan.feat.sevenAxis": { ja: "7軸シグナル", en: "7-axis signals" },
  "plan.feat.proPrivateRepos": {
    ja: "非公開は{included}つ込み・超過は +${overage}/リポジトリ・月",
    en: "{included} private repos included, then +${overage}/repo·mo",
  },
  "plan.feat.fullHistory": { ja: "全期間の履歴", en: "Full history" },
  // Badges & certificates are FREE on every plan (no runtime gate reads
  // BadgeEnabled/CertificateEnabled; mint is deliberately free — flywheel +
  // job-seeker-never-pays). So the free/starter cards state they're free, and
  // the Pro card sells the DEPTH Pro adds, not the artifact itself.
  "plan.feat.badgesCertsFree": {
    ja: "バッジ・証明書（公開の仕事は無料）",
    en: "Badges & certificates — free (public work)",
  },
  "plan.feat.certDepth": {
    ja: "証明書に非公開リポ・全期間・複数銀河横断を反映",
    en: "Certificate reflects private repos, full period & cross-galaxy",
  },
  "plan.feat.fullEventLayer": {
    ja: "Event Layer 全機能",
    en: "Full Event Layer",
  },
  "plan.feat.yearlyTotal": {
    ja: "年額 ${total}（${perMonth}/月）",
    en: "${total}/year (${perMonth}/mo)",
  },
  "plan.feat.yearlyPerMonth": {
    ja: "年払いなら ${perMonth}/月",
    en: "Yearly ${perMonth}/mo",
  },
  "plan.feat.upTo5Free": { ja: "5名まで無料", en: "Up to 5 members free" },
  "plan.feat.teamHealth": { ja: "チームヘルス", en: "Team health" },
  "plan.feat.moduleTopology": {
    ja: "モジュールトポロジー",
    en: "Module topology",
  },
  "plan.feat.unlimitedPrivate": {
    ja: "非公開リポジトリも無制限",
    en: "Unlimited private repos",
  },
  "plan.feat.novaSeats": {
    ja: "席を購入してメンバーに割り当て（{included}席まで基本${base}に込み・{includedPlus}席目から ${perSeat}/席）",
    en: "Buy + assign seats (first {included} included in the ${base} base, then ${perSeat}/seat)",
  },
  "plan.feat.seatHoldersView": {
    ja: "非公開リポの観測は席を持つメンバーのみ閲覧可",
    en: "Private-repo observations: viewable only by seated members",
  },
  "plan.feat.flatOrgWide": {
    ja: "組織全体でフラット（席課金なし）",
    en: "Flat org-wide (no per-seat)",
  },
  "plan.feat.conwayAlerts": {
    ja: "Conway検証・予測・アラート",
    en: "Conway / predictions / alerts",
  },
  // MCP ("OrbitLens for AI") is on EVERY plan — the only difference is the hourly
  // tools/call ceiling (an abuse guardrail, not a metered feature; free DOES get
  // MCP). Caps: free 60/h, pro 1000/h, nova 500/h per seat (4000/h at the 8-seat
  // base). Source of truth = mcp/ratelimit.go (NOT GetLimits). Keep these numbers
  // in sync with mcpHourlyCapFree / mcpHourlyCapPro / mcpPerSeatStep there.
  "plan.feat.mcpFree": {
    ja: "AI連携（MCP）60 リクエスト/時",
    en: "AI (MCP) integration — 60 calls/hr",
  },
  "plan.feat.mcpPro": {
    ja: "AI連携（MCP）1,000 リクエスト/時",
    en: "AI (MCP) integration — 1,000 calls/hr",
  },
  "plan.feat.mcpNova": {
    ja: "AI連携（MCP）席数 × 500 リクエスト/時",
    en: "AI (MCP) integration — 500 calls/hr per seat",
  },
  // Nova-only org INTERPRETATION (the ownership structure itself is on every
  // plan; only its org-level reading is gated). CanSeeOrgInterpretationForPlan.
  "plan.feat.orgInterpretation": {
    ja: "Collapse Risk・離脱影響・組織解釈",
    en: "Collapse Risk, departure impact, org interpretation",
  },
  // Card chrome
  "plan.recommended": { ja: "おすすめ", en: "RECOMMENDED" },
  "plan.perMonth": { ja: "月", en: "mo" },
  "plan.novaBase": {
    ja: "基本料金（{included}席込）+ {includedPlus}席目から ${perSeat}/追加席",
    en: "Base ({included} seats incl.) + ${perSeat}/seat from the {includedPlus}th",
  },
  "plan.volumeDiscounts": {
    ja: "ボリュームディスカウント",
    en: "Volume discounts",
  },
  "plan.scaleObservatory": { ja: "天文台をスケール", en: "Scale Observatory" },
  "plan.upgradeTelescope": {
    ja: "望遠鏡をアップグレード",
    en: "Upgrade Telescope",
  },
  "plan.adminOnlyBilling": {
    ja: "組織の管理者のみが契約・支払いを管理できます",
    en: "Only an organization admin can manage billing",
  },
  // Stripe-checkout terms line. {terms} and {privacy} mark the two inline links
  // (spliced via splitMarkupN); the link LABELS are their own keys so the link
  // element stays in JSX with exact word order, no copy split.
  "plan.terms.agree": {
    ja: "購読することで {terms}・{privacy}に同意したものとみなされます",
    en: "By subscribing, you agree to our {terms} and {privacy}.",
  },
  "plan.terms.tos": { ja: "利用規約", en: "Terms of Service" },
  "plan.terms.privacy": { ja: "プライバシーポリシー", en: "Privacy Policy" },
  // Public-first / OSS support block
  "plan.oss.heading": {
    ja: "公開リポジトリは永久に無料・無制限 🌍",
    en: "Public repos are free forever, unlimited 🌍",
  },
  "plan.oss.body1": {
    ja: "OSS プロジェクトを応援しています。課金の対象は非公開リポジトリだけ。無料プランは公開リポ無制限（履歴・タイムラインも全期間）＋非公開1つまでで、2つ目の非公開リポから Nova（席課金）へ。",
    en: "We support open source. Only private repos count toward billing — free gives you unlimited public repos (with full history/timeline) plus up to 1 private; the 2nd private repo moves you to Nova (per-seat).",
  },
  "plan.oss.body2": {
    ja: "1つの組織に公開・非公開が混在する場合は「private チーム」として Nova トラックになりますが、公開リポジトリは引き続き無料・無制限です。",
    en: "An org with both public and private repos is treated as a private team (Nova track) — but its public repos stay free and unlimited.",
  },

  // ── Members (seat management) ──
  "members.title": { ja: "メンバー", en: "Members" },
  "members.subtitle.nova": {
    ja: "天文台のメンバーと課金席を管理する",
    en: "Manage your observatory's members and billable seats",
  },
  "members.subtitle.plain": {
    ja: "天文台のメンバーを管理する",
    en: "Manage your observatory's members",
  },
  // Nova upsell
  "members.upsell.title": {
    ja: "Nova で席を割り当てて課金管理",
    en: "Assign seats & manage billing with Nova",
  },
  "members.upsell.body": {
    ja: "現在この銀河は課金されていません。Nova にアップグレードすると、席を購入してメンバーに割り当て、フル開示・チーム機能が使えます（基本 ${base}/月に{included}席込み、{includedPlus}席目から ${perSeat}/席・月）。",
    en: "This galaxy isn't being billed. Upgrade to Nova to buy seats, assign them to members, and unlock full disclosure + team features (${base}/mo base includes {included} seats, then ${perSeat}/seat·mo from the {includedPlus}th).",
  },
  "members.upsell.cta": { ja: "Nova にアップグレード", en: "Upgrade to Nova" },
  // Seat roster
  "members.seats.title": {
    ja: "席（課金メンバー）",
    en: "Seats (billed members)",
  },
  "members.members": { ja: "メンバー", en: "Members" },
  "members.seatCount": {
    ja: "席 {used} / {purchased}・メンバー {members} 名",
    en: "{used} / {purchased} seats · {members} members",
  },
  "members.totalCount": { ja: "全 {total} 名", en: "{total} total" },
  "members.seatsForPrivate": {
    ja: "課金席（Nova）は非公開リポジトリを観測するためのものです。公開リポジトリは席に関係なく無料・無制限で観測できます。",
    en: "Paid seats (Nova) are for observing PRIVATE repositories. Public repos are always free & unlimited — no seat required.",
  },
  "members.toggleOffNote": {
    ja: "退職者などをオフにすると課金席から外れます（観測には影響しません）。",
    en: "Toggle off a departed member to drop them from billing — observation is unaffected.",
  },
  "members.cancelNotice": {
    ja: "このプランは {date} に解約予定です。それまで Nova が有効で、以降は自動的に Free に切り替わります。",
    en: "This plan is set to cancel on {date}. Nova stays active until then, after which it drops to Free.",
  },
  "members.purchasedSeats": { ja: "購入席数", en: "Purchased seats" },
  "members.apply": { ja: "適用", en: "Apply" },
  "members.buySeats": { ja: "+{delta} 席を購入", en: "Buy +{delta} seats" },
  "members.reduceSeats": {
    ja: "{delta} 席に減らす",
    en: "Reduce {delta} seats",
  },
  "members.monthly": { ja: "月額", en: "Monthly" },
  "members.perMonth": { ja: "/月", en: "/mo" },
  "members.prorate.up": {
    ja: "席はすぐ使えます。増えた分は日割りで次回請求に加算（今すぐの課金はありません）。",
    en: "Seats are usable immediately; the increase is prorated onto your next invoice (no charge right now).",
  },
  "members.prorate.down": {
    ja: "次回請求から減額。減らした分は日割りで差し引かれます（返金ではありません）。",
    en: "Lower from the next invoice; the reduction is credited (prorated) against it — not refunded.",
  },
  "members.prorate.flat": {
    ja: "基本 ${base}/月に{included}席込み・{includedPlus}席目から ${perSeat}/席・月。増減はいつでも可能で、日割りで次回請求に反映されます。",
    en: "${base}/mo base includes {included} seats, then ${perSeat}/seat·mo from the {includedPlus}th. Change anytime — prorated onto the next invoice.",
  },
  // Over-assign CTA
  "members.allSeatsUsed": {
    ja: "購入した席がすべて埋まっています",
    en: "All purchased seats are in use",
  },
  "members.seatsUsedDetail": {
    ja: "使用 {used} / 購入 {purchased} 席。メンバーを席に割り当てるには席を追加してください。",
    en: "{used} of {purchased} seats used. Add seats to assign more members.",
  },
  "members.addSeatPaid": {
    ja: "席を 1 つ追加（+${cost}/月）",
    en: "Add a seat (+${cost}/mo)",
  },
  "members.addSeatFree": {
    ja: "席を 1 つ追加（基本料金内・追加料金なし）",
    en: "Add a seat (within base — no extra charge)",
  },
  // Roster rows
  "members.admin": { ja: "管理者", en: "Admin" },
  "members.member": { ja: "メンバー", en: "Member" },
  "members.billed": { ja: "課金対象", en: "Billed" },
  "members.excluded": { ja: "対象外", en: "Excluded" },
  "members.adminsAlwaysBilled": {
    ja: "管理者は常に課金対象です",
    en: "Admins are always billed",
  },
  "members.cantChangeOwnSeat": {
    ja: "自分の席は変更できません",
    en: "You can't change your own seat",
  },
  "members.noMembers": {
    ja: "メンバーはまだいません。",
    en: "No members yet.",
  },
  // Pager
  "members.pageRange": {
    ja: "{from}–{to} / {total} 名",
    en: "{from}–{to} of {total}",
  },
  "members.prev": { ja: "前へ", en: "Prev" },
  "members.next": { ja: "次へ", en: "Next" },
  // Observed authors
  "members.observed.title": {
    ja: "観測済みの未参加メンバー",
    en: "Observed, not yet joined",
  },
  "members.observed.desc": {
    ja: "git 履歴で観測されており、GitHub Organization のメンバーだが、まだ OrbitLens に参加していない author です。本人だけが受け取れる招待リンクを発行できます。",
    en: "Authors EIS has observed in git history who are members of your GitHub Organization but haven't joined OrbitLens yet. Generate an invite link only they can claim.",
  },
  "members.observed.generating": { ja: "発行中…", en: "Generating…" },
  "members.observed.generate": {
    ja: "招待リンクを発行",
    en: "Generate invite link",
  },
  // Invite links
  "members.invite.title": { ja: "招待リンク", en: "Invite links" },
  "members.invite.desc": {
    ja: "git 履歴に出てこない人（新しいメンバーやこれからコミットする人）は、このリンクで招待します。リンクを持っている人は誰でも参加できます。",
    en: "Invite people who aren't in git history yet (new or not-yet-committing members) with a link. Anyone who has the link can join.",
  },
  "members.invite.byLink": {
    ja: "リンクで招待（履歴がない人向け）",
    en: "Invite by link (no git history)",
  },
  "members.invite.asAdmin": { ja: "管理者として招待", en: "Invite as admin" },
  "members.invite.adminWarning": {
    ja: "「管理者として招待」は、課金（クレジットカード入力）・メンバー管理・リポジトリ管理を含む完全な管理者権限を与えます。経理担当など、別の管理者を迎えるときに使ってください。",
    en: "“Invite as admin” grants full admin — billing (card entry), member & repo management. Use it to bring on another admin, e.g. an accountant.",
  },
  "members.invite.adminTag": { ja: "管理者", en: "admin" },
  "members.invite.boundTo": { ja: "宛て", en: "only" },
  "members.invite.expires": { ja: "期限:", en: "Expires:" },
  "members.invite.copied": { ja: "コピーしました", en: "Copied!" },
  "members.invite.copy": { ja: "コピー", en: "Copy" },
  "members.invite.revokeTitle": {
    ja: "この招待リンクを無効化",
    en: "Revoke this invite link",
  },
  "members.invite.revoke": { ja: "削除", en: "Revoke" },

  // === i18n-migration: chronicle / timeline ===
  "chronicle.title": { ja: "クロニクル", en: "Chronicle" },
  "chronicle.subtitle": {
    ja: "設計判断・ADR・走っていたプロジェクト・メンバー — 時間軸に刻む dark matter",
    en: "Decisions, ADRs, projects, members — the dark matter, pinned to the timeline",
  },
  "chronicle.record": { ja: "記録する", en: "Record" },
  "chronicle.loading": { ja: "読み込み中…", en: "Loading…" },
  "chronicle.empty": {
    ja: "最初の観測が届くと、ファーストライトが時間軸に灯ります",
    en: "The first observation lights up first light on the axis",
  },
  "chronicle.quota.title": {
    ja: "無料枠: 記録 {used} 件 / 上限 {limit} 件",
    en: "Free tier: {used} recorded / {limit} limit",
  },
  "chronicle.quota.label": {
    ja: "無料枠 {used} / {limit} 件",
    en: "Free {used} / {limit}",
  },
  "chronicle.upsell.cta.title": {
    ja: "無料枠 {limit} 件に到達。{upsell} で無制限に記録",
    en: "Free limit ({limit}) reached — {upsell} unlocks unlimited",
  },
  "chronicle.upsell.cta.label": {
    ja: "{upsell} で記録",
    en: "Record with {upsell}",
  },
  "chronicle.lane.dragHint": {
    ja: "ドラッグで期間を記録 / クリックで単月",
    en: "Drag to record a range · click for a single month",
  },
  "chronicle.teamScopeHidden": {
    ja: "チームスコープ表示中 — 全体・他チームの {count} 件は非表示",
    en: "Scoped to a team — {count} org-wide / other-team record{s} hidden",
  },
  "chronicle.outsideYear": {
    ja: "この年の外に {count} 件",
    en: "{count} more outside this year",
  },
  "chronicle.freeHidden": {
    ja: "無料枠は最新 {limit} 件まで表示 — {upsell} で全 {used} 件を表示",
    en: "Free shows the latest {limit} — {upsell} reveals all {used}",
  },
  "chronicle.orbit.title": {
    ja: "組織の軌道 · {month}",
    en: "Org orbit · {month}",
  },
  "chronicle.orbit.noClassification": {
    ja: "分類なし",
    en: "no classification",
  },
  "chronicle.orbit.members": { ja: "メンバー", en: "Members" },
  "chronicle.orbit.avgSignal": { ja: "平均シグナル", en: "Avg signal" },
  "chronicle.orbit.avgSurvival": { ja: "平均 Survival", en: "Avg survival" },
  "chronicle.action.edit": { ja: "編集", en: "Edit" },
  "chronicle.action.delete": { ja: "削除", en: "Delete" },
  "chronicle.action.close": { ja: "閉じる", en: "Close" },
  "chronicle.action.openInMemberPage": {
    ja: "メンバーページで開く",
    en: "Open in member page",
  },
  "chronicle.adr.contextLabel": { ja: "背景: ", en: "Context: " },
  "chronicle.adr.decisionLabel": { ja: "決定: ", en: "Decision: " },
  "chronicle.adr.consequencesLabel": { ja: "影響: ", en: "Consequences: " },
  "chronicle.compose.notePlaceholder": {
    ja: "例: 認証を pkg/auth に切り出した。理由は galaxy 境界を一箇所で守るため…",
    en: "e.g. Extracted auth into pkg/auth so the galaxy boundary lives in one place…",
  },
  "chronicle.compose.setRange": { ja: "期間指定", en: "Set range" },
  "chronicle.compose.addRange": { ja: "+期間", en: "+range" },
  "chronicle.compose.clearRange": { ja: "期間解除", en: "Clear range" },
  "chronicle.compose.rolesPlaceholder": {
    ja: "ロール (例: admin, member)",
    en: "roles (e.g. admin, member)",
  },
  "chronicle.compose.selectMember": {
    ja: "対象メンバーを選択…",
    en: "Select a member…",
  },
  "chronicle.compose.eventTitlePlaceholder": {
    ja: "例: チームの設計レビューを主導",
    en: "e.g. Led the team's design review",
  },
  "chronicle.compose.eventDescPlaceholder": {
    ja: "補足 (任意): git に出ない貢献の中身…",
    en: "Notes (optional): the contribution git can't see…",
  },
  "chronicle.compose.adrTitlePlaceholder": {
    ja: "決定のタイトル (例: 認証を pkg/auth に分離)",
    en: "Decision title (e.g. Extract auth into pkg/auth)",
  },
  "chronicle.compose.adrDecisionPlaceholder": {
    ja: "決定: 何を選び、なぜそれにしたか…",
    en: "Decision: what was chosen, and why…",
  },
  "chronicle.compose.adrContextPlaceholder": {
    ja: "背景 (任意): 何が問題で、どの選択肢があったか…",
    en: "Context (optional): the problem and the options…",
  },
  "chronicle.compose.adrConsequencesPlaceholder": {
    ja: "影響 (任意): この決定で何が変わるか…",
    en: "Consequences (optional): what this changes…",
  },
  "chronicle.compose.saving": { ja: "保存中", en: "Saving" },
  "chronicle.compose.record": { ja: "記録", en: "Record" },
  "chronicle.compose.update": { ja: "更新", en: "Update" },
  "chronicle.compose.cancel": { ja: "キャンセル", en: "Cancel" },
  "chronicle.compose.rangeError": {
    ja: "終了月は開始月以降にしてください",
    en: "End month must be on or after the start month",
  },
  "observationLog.title": { ja: "観測履歴", en: "Observation Runs" },
  "observationLog.recentRuns": {
    ja: "最近 {count} 件の観測",
    en: "{count} recent runs",
  },
  "observationLog.okCount": {
    ja: "{total}件中 {ok}件成功",
    en: "{ok}/{total} ok",
  },
  "observationLog.pending": { ja: "{count}件 未解消", en: "{count} pending" },
  "observationLog.running": { ja: "{count}件 実行中", en: "{count} running" },
  "observationLog.failedCount": { ja: "{count}件 失敗", en: "{count} failed" },
  "observationLog.resolved": {
    ja: "{count}件 解消済み",
    en: "{count} resolved",
  },
  "observationLog.elapsed": { ja: "経過 {d}", en: "{d} elapsed" },
  "observationLog.elapsedRunning": { ja: "経過 {d}…", en: "{d} elapsed…" },
  "observationLog.elapsedHint": {
    ja: "実経過 {elapsed}（うち観測計算 {compute}）",
    en: "{elapsed} wall-clock ({compute} spent computing)",
  },
  "observationLog.shas": { ja: "sha", en: "shas" },
  "observationLog.col.at": { ja: "時刻", en: "at" },
  "observationLog.col.status": { ja: "ステータス", en: "status" },
  "observationLog.col.detail": { ja: "詳細", en: "detail" },
  "observationLog.status.ok": { ja: "成功", en: "ok" },
  "observationLog.status.resolved": { ja: "✓ 解消済み", en: "✓ resolved" },
  "observationLog.status.running": { ja: "● 実行中", en: "● running" },
  "observationLog.status.stalled": { ja: "応答なし", en: "unresponsive" },
  "observationLog.status.failed": { ja: "失敗", en: "failed" },
  "observationLog.runningDetail": {
    ja: "観測を実行中…",
    en: "Observation in progress…",
  },
  "observationLog.startedStaleDetail": {
    ja: "ワーカーの応答が途絶えました（停止 / OOM の可能性）",
    en: "The worker went silent (possibly stopped / OOM)",
  },
  "observationLog.resolvedBy": {
    ja: "({when} の観測で解消)",
    en: "(resolved by {when})",
  },
  "observationLog.failure.partialRollup": {
    ja: "一部のドメインが観測できず、今回の更新は見送りました（前回の完全な観測を表示中）",
    en: "Some domains couldn't be observed; this run was skipped (the last complete observation is kept)",
  },
  "observationLog.failure.notFound": {
    ja: "リポジトリが見つかりません（移管・削除・権限なし）",
    en: "Repository not found (transferred, deleted, or no access)",
  },
  "observationLog.failure.permission": {
    ja: "アクセス権限がありません（GitHub App の再認可が必要かも）",
    en: "Permission denied (the GitHub App may need re-authorising)",
  },
  "observationLog.failure.timeout": {
    ja: "観測がタイムアウトしました（リポジトリが大きすぎる可能性）",
    en: "Observation timed out (the repo may be too large)",
  },
  "observationLog.failure.tooLarge": {
    ja: "リポジトリが大きすぎます",
    en: "Repository too large",
  },
  "observationLog.failure.generic": {
    ja: "観測に失敗しました",
    en: "Observation failed",
  },

  // === i18n-migration: repos ===
  "repos.title": { ja: "リポジトリ", en: "Repositories" },
  "repos.activeSummary": {
    ja: "{active} 件アクティブ / {total} 件",
    en: "{active} active / {total} available",
  },
  "repos.cap.privateCount": {
    ja: "非公開リポジトリ {active} / {max}（無料枠）",
    en: "Private repos {active} / {max} (free)",
  },
  "repos.cap.publicUnlimited": {
    ja: " ・ 公開リポジトリは無制限",
    en: " · public repos unlimited",
  },
  "repos.cap.limitReached": {
    ja: "上限到達 — {plan} で無制限に",
    en: "limit reached — {plan} for unlimited",
  },
  "repos.refresh": { ja: "↻ 更新", en: "↻ Refresh" },
  "repos.refreshTitle": { ja: "GitHub から再取得", en: "Refresh from GitHub" },
  "repos.metered.privateBilled": {
    ja: "非公開リポジトリ（課金）",
    en: "Private repos (billed)",
  },
  "repos.metered.included": {
    ja: "{active} / {included} 込み",
    en: "{active} / {included} included",
  },
  "repos.metered.label": { ja: "従量", en: "Metered" },
  "repos.perMonth": { ja: "/月", en: "/mo" },
  "repos.metered.explainer": {
    ja: "公開リポジトリは無料・無制限。非公開は {included} 件まで込みで、超過分は1件あたり +${usd}/月（ハード上限なし）。課金対象の増減は、下のリポジトリ一覧で非公開リポを ON / OFF して行います。",
    en: "Public repos are free & unlimited. Private repos are included up to {included}; each one beyond is +${usd}/mo (no hard cap). Add or remove billed repos by toggling private repos in the list below.",
  },
  "repos.tokenExpired.title": {
    ja: "GitHub 認可が切れています",
    en: "GitHub authorization has expired",
  },
  "repos.tokenExpired.body": {
    ja: "リポジトリ一覧を取得するには、GitHub にもう一度サインインしてください。接続済みの組織は再認可不要です。",
    en: "Sign in to GitHub again to fetch your repository list. Connected organizations don't need re-authorization.",
  },
  "repos.tokenExpired.cta": { ja: "再認可する", en: "Re-authorize" },
  "repos.installCta.title": {
    ja: "非公開リポジトリを観測するには App をインストール",
    en: "Install the App to observe private repos",
  },
  "repos.installCta.body": {
    ja: "公開リポジトリはサインインだけで観測できますが、非公開リポジトリは OrbitLens App を個人アカウントにインストールし、対象リポへのアクセスを許可した場合のみ観測できます。",
    en: "Public repos are observed with just sign-in, but private repos are only visible once you install the OrbitLens App on your personal account and grant it access to those repos.",
  },
  "repos.installCta.button": {
    ja: "App をインストール",
    en: "Install the App",
  },
  "repos.filter.removed": { ja: "削除済み", en: "Removed" },
  "repos.removed.empty": {
    ja: "削除済みのリポジトリはありません。",
    en: "No removed repositories.",
  },
  "repos.removed.rowNote": {
    ja: "一覧から削除済み（観測データは保持）",
    en: "Removed from the list (observations retained)",
  },
  "repos.restore": { ja: "復元", en: "Restore" },
  "repos.toggle.forkBlocked": {
    ja: "fork は上流リポジトリの履歴を引き継ぐため、観測対象外です。",
    en: "Forks inherit the upstream repo's history, so they aren't observed.",
  },
  "repos.toggle.archivedBlocked": {
    ja: "アーカイブ済みリポジトリは新しい git 活動が無いため、観測を開始できません。",
    en: "This repo is archived — no new git activity to observe, so it can't be turned on.",
  },
  "repos.toggle.sizeBlocked": {
    ja: "このリポは無料枠の上限（{mb}MB）を超えています。{plan} で観測できます。",
    en: "This repo exceeds the free-tier limit ({mb}MB). Upgrade to {plan} to observe it.",
  },
  "repos.badge.archived": { ja: "アーカイブ済み", en: "Archived" },
  "repos.badge.archivedTitle": {
    ja: "GitHub 上でアーカイブ済み。新しい git 活動が無いため観測を開始できません。",
    en: "Archived on GitHub — no new git activity, so observation can't be started.",
  },
  "repos.badge.fork": { ja: "観測対象外（fork）", en: "Fork — not observed" },
  "repos.badge.forkTitle": {
    ja: "fork は上流リポジトリの履歴（上流の作者の仕事）を引き継ぐため、この銀河の観測対象外です。",
    en: "A fork inherits the upstream repo's history (the upstream authors' work), so it isn't observed for this galaxy.",
  },
  "repos.badge.sizeBlocked": {
    ja: "{mb}MB超 · {plan} で観測可能",
    en: "Over {mb}MB · {plan} to observe",
  },
  "repos.badge.sizeBlockedTitle": {
    ja: "無料枠の上限（{mb}MB）を超えるリポジトリ。{plan} で観測できます。",
    en: "Exceeds the free-tier {mb}MB limit. Upgrade to {plan} to observe.",
  },
  "repos.badge.excludedOversize": {
    ja: "観測対象外（上限超）",
    en: "Excluded (too large)",
  },
  "repos.badge.excludedOversizeTitle": {
    ja: "上限（{mb}MB）を超えたため観測対象から外れています。{plan} で観測できます。",
    en: "Over the {mb}MB limit — excluded from observation. Upgrade to {plan} to observe it.",
  },
  "repos.badge.excludedOverFree": {
    ja: "観測対象外（無料枠超）",
    en: "Excluded (over free limit)",
  },
  "repos.badge.excludedOverFreeTitle": {
    ja: "無料プランの非公開上限（{max}）を超えています。{plan} で全件観測、または他の非公開を OFF にすると観測されます。",
    en: "Over the free plan's private-repo limit ({max}). Upgrade to {plan} to observe all, or turn off other private repos to observe this one.",
  },
  "repos.badge.notFound": { ja: "GitHubに無い", en: "Not found" },
  "repos.badge.notFoundTitle": {
    ja: "GitHub 上に見つからず観測を停止しました（移管/削除など）",
    en: "Not found on GitHub — observation stopped (transferred/deleted)",
  },
  "repos.badge.dbOnly": { ja: "GitHub未取得", en: "Not on GitHub API" },
  "repos.badge.dbOnlyTitle": {
    ja: "DB に登録済み / GitHub API では未取得（App 未連携など）",
    en: "Registered in our DB but not returned by the GitHub API (App not installed, etc.)",
  },
  "repos.removeButton.title": {
    ja: "GitHub 上に無いリポジトリを一覧から削除します（観測データは保持・復元可能）。",
    en: "Remove a repo that's gone on GitHub from the list (observations kept, restorable).",
  },
  "repos.cancel": { ja: "キャンセル", en: "Cancel" },
  "repos.remove.title": {
    ja: "リポジトリを一覧から削除",
    en: "Remove repository from the list",
  },
  // Remove-confirm body. {repo} marks the repo name (kept red, inline); spliced
  // in via splitMarkup so the sentence stays one entry with exact word order.
  "repos.remove.body": {
    ja: "{repo} を一覧から削除します。GitHub 上に見つからないリポジトリの行を非表示にするだけで、これまでの観測データは保持されます。",
    en: "Remove {repo} from the list. This only hides a row whose repo is gone on GitHub — the observation history is kept.",
  },
  "repos.remove.restoreHint": {
    ja: "「削除済み」フィルタからいつでも復元できます。",
    en: "You can restore it any time from the “Removed” filter.",
  },
  "repos.remove.confirm": { ja: "削除", en: "Remove" },
  "repos.overage.title": { ja: "従量課金の確認", en: "Confirm metered repo" },
  // Overage-confirm body. {included} is the plain included-repo count (interp'd
  // first); {repo} and {price} mark the two yellow highlights spliced inline via
  // splitMarkupN — one entry per sentence, exact word order per locale.
  "repos.overage.body": {
    ja: "{repo} は Pro の無料枠（非公開{included} リポジトリ）を超えます。観測を有効化すると {price}（日割り）が加算されます。",
    en: "{repo} is beyond Pro's included {included} private repos. Observing it adds {price} (prorated).",
  },
  "repos.overage.priceTag": {
    ja: "+${overageUsd}/月",
    en: "+${overageUsd}/mo",
  },
  "repos.overage.afterNote.one": {
    ja: "この有効化後、従量課金の非公開リポジトリは {count} 件（計 +${monthlyDelta}/月）になります。公開リポジトリは常に無料・無制限です。",
    en: "After this, {count} private repo is metered (+${monthlyDelta}/mo total). Public repos are always free & unlimited.",
  },
  "repos.overage.afterNote.other": {
    ja: "この有効化後、従量課金の非公開リポジトリは {count} 件（計 +${monthlyDelta}/月）になります。公開リポジトリは常に無料・無制限です。",
    en: "After this, {count} private repos are metered (+${monthlyDelta}/mo total). Public repos are always free & unlimited.",
  },
  "repos.overage.confirm": {
    ja: "有効化して +${usd}/月",
    en: "Observe · +${usd}/mo",
  },

  // === i18n-migration: onboarding / team / control-room ===
  "onboarding.welcomeTitle": {
    ja: "OrbitLens Ace は望遠鏡です",
    en: "OrbitLens Ace is a telescope",
  },
  "onboarding.welcomeBody": {
    ja: "OrbitLens Ace はコードベースに働く重力を観測します。人を採点するスコアボードではありません。",
    en: "OrbitLens Ace observes the gravity at work in your codebase. It is not a scoreboard that ranks people.",
  },
  "onboarding.welcomePoint1": {
    ja: "観測であって、評価ではない",
    en: "Observation, not evaluation",
  },
  "onboarding.welcomePoint2": {
    ja: "重力 = 生き残ったコード（活動量ではない・月単位で偽装できない）",
    en: "Gravity = surviving code — not activity, and it can't be faked over months",
  },
  "onboarding.welcomePoint3": {
    ja: "銀河境界 — あなたの組織のデータは他と混ざらない",
    en: "Galaxy boundary — your org's data never mixes with another's",
  },
  "onboarding.welcomeAdmin": {
    ja: " — あなたはこの銀河の最初のユーザー、つまり管理者です。開示の境界とメンバーを管理できます。",
    en: " — you're the first user, so the admin of this galaxy. You set the disclosure boundary and manage members.",
  },
  "onboarding.welcomePersonal": {
    ja: "この銀河はあなた自身です。あなたの git 履歴から、あなたのエンジニアリングの重力を観測します。",
    en: "This galaxy is you. We observe your own engineering gravity from your git history.",
  },
  "onboarding.continue": { ja: "次へ", en: "Continue" },
  "onboarding.back": { ja: "戻る", en: "Back" },
  "onboarding.boundaryTitle": {
    ja: "開示の境界を決める",
    en: "Set the disclosure boundary",
  },
  "onboarding.boundaryBody": {
    ja: "OrbitLens Ace は既定でメンバーを匿名化します。どこまで実名を見せるかは、あなたが決めます。各メンバーは自分の開示を自分で上げることに同意できます。",
    en: "OrbitLens Ace keeps members anonymous by default. You decide how much identity is shown — and each member can consent to reveal more of their own.",
  },
  "onboarding.modeObservationDesc": {
    ja: "匿名。各メンバーは Member-A として表示。名前でなく構造を見る。",
    en: "Anonymous — each member shows as Member-A. You see structure, not names.",
  },
  "onboarding.modeContextDesc": {
    ja: "名前と役割を表示。関係性を理解する段階に。",
    en: "Names and roles shown. For understanding relationships.",
  },
  "onboarding.modeFullInsightDesc": {
    ja: "実名と全シグナル。合意の上での運用向け。",
    en: "Real names + full signals. For consented, operational use.",
  },
  "onboarding.recommended": { ja: "推奨", en: "recommended" },
  "onboarding.boundarySettingsNote": {
    ja: "後から Settings でいつでも変更できます。",
    en: "You can change this anytime later in Settings.",
  },
  "onboarding.darkmatterTitle": {
    ja: "OrbitLens Ace に見えないもの",
    en: "What OrbitLens Ace can't see",
  },
  "onboarding.darkmatterBody": {
    ja: "OrbitLens Ace は git に刻まれた痕跡しか読みません。観測の外には、測れない重力があります。",
    en: "OrbitLens Ace only reads the traces left in git. Beyond observation lies gravity it cannot measure.",
  },
  "onboarding.darkmatterItem1": {
    ja: "メンタリング、人を育てる時間",
    en: "Mentoring — the time spent growing people",
  },
  "onboarding.darkmatterItem2": {
    ja: "コードレビューに宿る判断と知恵",
    en: "The judgment and wisdom inside a code review",
  },
  "onboarding.darkmatterItem3": {
    ja: "Slack で詰まりを解いて回る人",
    en: "The person who unblocks everyone in Slack",
  },
  "onboarding.darkmatterItem4": {
    ja: "文化、信頼、心理的安全性",
    en: "Culture, trust, psychological safety",
  },
  "onboarding.darkmatterPartialView": {
    ja: "これは部分的な眺めです。人を裁くためでなく、より良い問いを立てるために使ってください。",
    en: "This is a partial view. Use it to ask better questions — not to judge people.",
  },
  "onboarding.darkmatterContinue": {
    ja: "理解しました — 次へ",
    en: "I understand — Continue",
  },
  "onboarding.beginning": {
    ja: "観測を開始しています…",
    en: "Beginning observation…",
  },
  // Onboarding step: tune signaling (eis.yaml) — optional but recommended
  "onboarding.signaling.title": {
    ja: "シグナリングを調整する（任意）",
    en: "Tune your signaling (optional)",
  },
  "onboarding.signaling.body": {
    ja: "eis.yaml は別名の統合・bot 除外・ドメイン分類などシグナリングを微調整する設定です。なくても観測は始まりますが、設定するとより正確になります。",
    en: "eis.yaml fine-tunes signaling — merging aliases, excluding bots, classifying domains. Observation starts without it, but it makes the signal sharper.",
  },
  "onboarding.signaling.next": { ja: "次へ", en: "Next" },
  "onboarding.signaling.skip": { ja: "あとで", en: "Skip for now" },
  "onboarding.completeTitle": {
    ja: "最初の観測が形成されています",
    en: "Your first observation is forming",
  },
  "onboarding.completeBody": {
    ja: "OrbitLens Ace は重力がどこに宿るかを見せます。あなたの銀河を、評価ではなく観測として。気づきは判決ではなく、次の対話のための問いです。",
    en: "OrbitLens Ace will show where gravity lives — your galaxy as observation, not evaluation. What it surfaces is a question for a conversation, never a verdict.",
  },
  "onboarding.openObservatory": {
    ja: "天文台を開く",
    en: "Open the Observatory",
  },
  "team.syncNotice": {
    ja: "GitHub から同期しました（部署 {departments} / チーム {teams} / メンバー {members}）",
    en: "Synced from GitHub — {departments} departments, {teams} teams, {members} members",
  },
  "team.noSuggestions": {
    ja: "提案できる共有モジュールが見つかりませんでした",
    en: "No co-ownership clusters to suggest yet",
  },
  "team.searchMembersPlaceholder": {
    ja: "観測されたメンバーを検索...",
    en: "Search observed members...",
  },
  "team.addMember": { ja: "+ メンバー追加", en: "+ Add member" },
  "team.sourceSuggested": { ja: "提案", en: "suggested" },
  "reviewFlow.title": { ja: "レビュー流量", en: "Review Flow" },
  "reviewFlow.subtitle": {
    ja: "PR レビューの月次件数。活動量（throughput）であり、重力とは別軸。",
    en: "Monthly PR-review counts — activity throughput, kept separate from gravity.",
  },
  "reviewFlow.empty": {
    ja: "まだレビューが観測されていません",
    en: "No reviews observed yet",
  },
  "reviewFlow.approved": { ja: "Approve", en: "Approved" },
  "reviewFlow.changesRequested": { ja: "Changes", en: "Changes" },
  "reviewFlow.commented": { ja: "Comment", en: "Comments" },
  "reviewFlow.total": { ja: "合計", en: "Total" },
  "reviewFlow.topReviewers": {
    ja: "アクティブなレビュアー",
    en: "Most active reviewers",
  },
  "team.title": { ja: "部署・チーム", en: "Departments & Teams" },
  "team.subtitle": {
    ja: "エンジニア（観測された git author）を2階層でグルーピングし、その単位で観測する",
    en: "Group engineers (observed git authors) into a department → team tree to scope observation.",
  },
  "team.analyzing": { ja: "観測中…", en: "Scanning…" },
  "team.suggestFromObservation": {
    ja: "観測から提案",
    en: "Suggest from observation",
  },
  "team.syncing": { ja: "同期中…", en: "Syncing…" },
  "team.syncFromGithub": { ja: "GitHub から同期", en: "Sync from GitHub" },
  "team.suggestionsHeading": {
    ja: "提案チーム（観測されたモジュール共有から）",
    en: "Suggested teams (from observed module co-ownership)",
  },
  "team.suggestionsDesc": {
    ja: "同じモジュールを支えているエンジニアのまとまりです。承認するとチームになります。",
    en: "Clusters of engineers who sustain the same modules. Approve to turn one into a team.",
  },
  "team.memberCountSuffix": { ja: "名", en: " members" },
  "team.approve": { ja: "承認", en: "Approve" },
  "team.reject": { ja: "却下", en: "Reject" },
  "team.departmentNamePlaceholder": { ja: "部署名", en: "Department name" },
  "team.createDepartment": { ja: "部署を作成", en: "Create department" },
  "team.emptyTitle": { ja: "まだ部署がありません", en: "No departments yet." },
  "team.emptyHint": {
    ja: "部署を作るか、GitHub から同期してください",
    en: "Create a department, or sync from GitHub.",
  },
  "team.deleteDepartment": { ja: "部署を削除", en: "Delete department" },
  "team.deleteTeam": { ja: "チームを削除", en: "Delete team" },
  "team.teamNamePlaceholder": { ja: "チーム名", en: "Team name" },
  "team.add": { ja: "追加", en: "Add" },
  "team.cancel": { ja: "取消", en: "Cancel" },
  "team.addTeam": { ja: "+ チームを追加", en: "+ Add team" },
  "team.shareOrbit": { ja: "チームの軌道を共有", en: "Share team orbit" },
  "team.shareOrbitTitle": {
    ja: "このチームがくぐった集合的な軌道を共有（集合体匿名）",
    en: "Share this team's collective orbit (collective-anonymous)",
  },
  "team.shareOrbitGenerating": { ja: "生成中…", en: "Generating…" },
  "team.shareOrbitCopyLink": { ja: "リンクをコピー", en: "Copy link" },
  "team.shareOrbitCopied": { ja: "コピーしました", en: "Copied" },
  "team.shareOrbitOpen": { ja: "公開ページを開く", en: "Open public page" },
  "team.shareOrbitOnX": { ja: "X で共有", en: "Share on X" },
  "team.shareOrbitDownload": { ja: "画像を保存", en: "Download image" },
  "team.shareOrbitText": {
    ja: "チームがくぐった集合的な軌道 — 望遠鏡が記録した集合の痕跡（集合体匿名）",
    en: "The collective orbit a team passed through — the trace the telescope recorded (collective-anonymous)",
  },
  "team.shareOrbitTooSmall": {
    ja: "このチームは観測された貢献者が5人未満のため、集合体匿名で共有できません",
    en: "This team has fewer than 5 observed contributors, so it can't be shared collective-anonymously",
  },
  "projectHealth.share": {
    ja: "プロジェクト健全性を公開",
    en: "Publish project health",
  },
  "projectHealth.shareTitle": {
    ja: "このプロジェクトの持続可能性を集合体匿名で公開（個人は晒さない）",
    en: "Publish this project's sustainability, collective-anonymous (no individuals)",
  },
  "projectHealth.shareDesc": {
    ja: "bus-factor と Orphaned Gravity を集合体匿名（5人以上）で公開する検索可能なページを発行します。個人名・順位は一切出しません。",
    en: "Publish a searchable page of bus-factor + Orphaned Gravity, collective-anonymous (5+ contributors). No individual name, no ranking.",
  },
  "projectHealth.shareGenerating": { ja: "生成中…", en: "Generating…" },
  "projectHealth.shareCopyLink": { ja: "リンクをコピー", en: "Copy link" },
  "projectHealth.shareCopied": { ja: "コピーしました", en: "Copied" },
  "projectHealth.shareOpen": { ja: "公開ページを開く", en: "Open public page" },
  "projectHealth.shareOnX": { ja: "X で共有", en: "Share on X" },
  "projectHealth.shareText": {
    ja: "プロジェクトの持続可能性 — bus-factor と Orphaned Gravity を集合体匿名で観測",
    en: "A project's sustainability — bus-factor and Orphaned Gravity, observed collective-anonymous",
  },
  "projectHealth.shareTooSmall": {
    ja: "このプロジェクトは観測された貢献者が5人未満のため、集合体匿名で公開できません",
    en: "This project has fewer than 5 observed contributors, so it can't be published collective-anonymously",
  },
  "controlRoom.loadingOrgs": {
    ja: "組織を読み込み中...",
    en: "Loading organizations...",
  },
  "controlRoom.connected": { ja: "接続済み", en: "Connected" },
  "controlRoom.adminRequired": { ja: "Admin権限が必要", en: "Admin required" },
  "controlRoom.notConnected": { ja: "未接続", en: "Not connected" },
  "controlRoom.selectOrgHint": {
    ja: "GitHub App のインストール画面で組織を選択してください",
    en: "Select your organization on the GitHub App installation page",
  },
  "controlRoom.installGithubApp": {
    ja: "GitHub App をインストール",
    en: "Install GitHub App",
  },
  "controlRoom.title": { ja: "制御室", en: "Control Room" },
  "controlRoom.subtitle": {
    ja: "アカウントと、観測中の銀河・GitHub 連携",
    en: "Your account, galaxies & GitHub connection",
  },
  "controlRoom.reauthNeeded": { ja: "再認証が必要", en: "Re-auth needed" },
  "controlRoom.reconnect": { ja: "再接続", en: "Reconnect" },
  "controlRoom.observationTargets": {
    ja: "観測対象",
    en: "Observation Targets",
  },
  "controlRoom.personal": { ja: "個人", en: "Personal" },
  "controlRoom.organization": { ja: "組織", en: "Organization" },
  "controlRoom.connectOrg": { ja: "組織を接続", en: "Connect Organization" },
  "controlRoom.connectOrgBody": {
    ja: "GitHub 組織を OrbitLens に接続して、チームの観測を始める",
    en: "Connect your GitHub organization to start team observation",
  },
  "controlRoom.dangerZone": { ja: "デンジャーゾーン", en: "Danger Zone" },
  "controlRoom.dangerZoneBody": {
    ja: "アカウントを削除すると、ログイン情報とチーム所属が削除されます。git履歴由来の観測データ（シグナル）は観測記録として残ります。",
    en: "Deleting your account removes login credentials and team memberships. Git-derived observation data (signals) remain as historical records.",
  },
  "controlRoom.deleteAccount": { ja: "アカウントを削除", en: "Delete Account" },
  "controlRoom.deleteSignalsLabel": {
    ja: "シグナルデータも完全に削除する（忘れられる権利）。チェックしない場合、git履歴由来の観測データは匿名の記録として残ります。",
    en: "Also delete all signal data (right to be forgotten). If unchecked, git-derived observation data remains as anonymous records.",
  },
  "controlRoom.deleting": { ja: "削除中...", en: "Deleting..." },
  "controlRoom.confirmDelete": {
    ja: "本当に削除する",
    en: "Yes, delete my account",
  },
  "controlRoom.cancel": { ja: "キャンセル", en: "Cancel" },

  // Shared Galaxies (docs/design/shared-galaxies.md) — owner mint/manage + viewer claim/leave.
  "sharedGalaxies.owner.title": { ja: "銀河を共有", en: "Shared galaxies" },
  "sharedGalaxies.owner.intro": {
    ja: "GitHub のユーザー名を入力して、この銀河の観測を読み取り専用で共有します。相手は Ace アカウントが未作成でも、その GitHub でサインインすれば自動で共有された状態になります。書き込みはできません。メンバーの見せ方は下の「見せ方」で選べます — 既定の匿名なら全員 Member-N、フル公開ならこの銀河の可視性モードと各メンバーの同意に従います。",
    en: "Enter a GitHub username to share this galaxy's observations read-only. The recipient needs no Ace account yet — access activates the moment they sign in with that GitHub identity. They can never write. How members appear is set by \"Show members as\" below — anonymous (the default) shows everyone as Member-N; full follows the galaxy's visibility mode and each member's own consent.",
  },
  "sharedGalaxies.owner.loginPlaceholder": {
    ja: "GitHub のユーザー名（@login）",
    en: "GitHub username (@login)",
  },
  "sharedGalaxies.owner.searchReauth": {
    ja: "GitHub 接続が切れているため検索できません",
    en: "Can't search — your GitHub connection expired",
  },
  "sharedGalaxies.owner.share": { ja: "共有", en: "Share" },
  "sharedGalaxies.owner.sharing": { ja: "共有中...", en: "Sharing..." },
  "sharedGalaxies.owner.shared": {
    ja: "@{login} と共有しました。",
    en: "Shared with @{login}.",
  },
  "sharedGalaxies.owner.noSuchUser": {
    ja: "その GitHub ユーザーが見つかりません。",
    en: "No such GitHub user.",
  },
  "sharedGalaxies.owner.empty": {
    ja: "まだ誰にも共有していません。",
    en: "Not shared with anyone yet.",
  },
  "sharedGalaxies.owner.revoke": { ja: "解除", en: "Revoke" },
  "sharedGalaxies.owner.revokeTitle": { ja: "共有を解除", en: "Revoke share" },
  "sharedGalaxies.owner.revokeBody": {
    ja: "@{login} との共有を解除すると、相手はこの銀河を見られなくなります。取り消せません。",
    en: "Revoking the share with @{login} closes their view of this galaxy. This can't be undone.",
  },
  "sharedGalaxies.owner.revoked": {
    ja: "共有を解除しました。",
    en: "Share revoked.",
  },
  "sharedGalaxies.owner.auditTitle": {
    ja: "この銀河を読んだゲスト",
    en: "Guests who read this galaxy",
  },
  "sharedGalaxies.owner.auditReads": { ja: "{count}回", en: "{count} reads" },
  "sharedGalaxies.owner.disclosureLabel": {
    ja: "見せ方",
    en: "Show members as",
  },
  "sharedGalaxies.owner.disclosureAnonymous": { ja: "匿名", en: "Anonymous" },
  "sharedGalaxies.owner.disclosureFull": { ja: "フル公開", en: "Full" },
  "sharedGalaxies.owner.disclosureAnonymousHint": {
    ja: "全メンバーを匿名（Member-N）で見せます。銀河の可視性モードに関わらず、実名やシグナルは出ません。",
    en: "Every member appears anonymized (Member-N) — no real names or signals, whatever the galaxy's visibility mode.",
  },
  "sharedGalaxies.owner.disclosureFullHint": {
    ja: "銀河の可視性モードと各メンバーの同意に従って見せます。full_insight 銀河なら実名＋シグナル、observation 銀河なら匿名のまま。銀河が内部で決めた範囲を超えて外部に出すことはありません。",
    en: "Members appear per the galaxy's visibility mode and each member's own consent — real names + signals on a full_insight galaxy, still anonymized on an observation one. Never more than the galaxy already decided internally.",
  },

  "sharedGalaxies.viewer.title": {
    ja: "共有銀河",
    en: "Galaxies shared with you",
  },
  "sharedGalaxies.viewer.intro": {
    ja: "他のオーナーが読み取り専用であなたに共有した銀河です。あなたの GitHub 宛に共有されたものは自動で表示されます。オープン共有を許可している銀河なら、その GitHub アカウント名（組織／ユーザー）を下に入力して自分で追加できます。追加した銀河は左上の銀河スイッチャーから開いて観測を閲覧できます（書き込みはできません）。",
    en: "Galaxies an owner shared with you, read-only. Ones shared with your GitHub identity appear automatically; for a galaxy that allows open self-add, type its GitHub account name (org or user) below to add it. Open an added galaxy from the galaxy switcher (top-left) to view its observations — you can't write to it.",
  },
  "sharedGalaxies.viewer.empty": {
    ja: "共有された銀河はまだありません。",
    en: "No galaxies shared with you yet.",
  },
  "sharedGalaxies.viewer.addPlaceholder": {
    ja: "GitHub アカウント名（例: orbitlensio）",
    en: "GitHub account (e.g. orbitlensio)",
  },
  "sharedGalaxies.viewer.add": { ja: "追加", en: "Add" },
  "sharedGalaxies.viewer.adding": { ja: "追加中…", en: "Adding…" },
  "sharedGalaxies.viewer.added": {
    ja: "{login} を共有銀河に追加しました。",
    en: "Added {login} to your shared galaxies.",
  },
  "sharedGalaxies.viewer.addNotFound": {
    ja: "そのログインの銀河は見つかりませんでした。",
    en: "No galaxy found for that login.",
  },
  "sharedGalaxies.viewer.addNotOpen": {
    ja: "この銀河は自由な追加を許可していません。",
    en: "This galaxy isn't open for self-add.",
  },
  "sharedGalaxies.viewer.addOwn": {
    ja: "これはあなた自身の銀河です。",
    en: "This galaxy is already yours.",
  },
  "sharedGalaxies.viewer.open": { ja: "開く", en: "Open" },
  "sharedGalaxies.viewer.readOnly": { ja: "閲覧のみ", en: "Read-only" },
  "sharedGalaxies.viewer.leave": { ja: "退出", en: "Leave" },
  "sharedGalaxies.viewer.leaveTitle": {
    ja: "共有された銀河から退出",
    en: "Leave shared galaxy",
  },
  "sharedGalaxies.viewer.leaveBody": {
    ja: "退出すると、オーナーが再共有しない限り、この銀河は二度と見られなくなります。",
    en: "Once you leave, you won't be able to see this galaxy again unless the owner shares it with you once more.",
  },
  "sharedGalaxies.viewer.left": {
    ja: "共有された銀河から退出しました。",
    en: "Left the shared galaxy.",
  },

  // === i18n-migration: affiliations / career / presence / members ===
  "affiliations.record.subtitle": {
    ja: "所属記録（退職済み）",
    en: "Affiliation record (departed)",
  },
  "affiliations.record.peakGravity": { ja: "最大重力", en: "Peak gravity" },
  "affiliations.record.peakWhen": {
    ja: "{month} — あなたがこの組織で最も重力を持っていた時期",
    en: "{month} — when you held the most gravity here",
  },
  "affiliations.record.woundDown": {
    ja: "退職時点では {value}（在籍末期は重力が下がるのが自然です）",
    en: "{value} at departure (winding down before leaving is natural)",
  },
  "affiliations.record.noGravity": {
    ja: "在籍中の観測がないため、重力は記録されていません。",
    en: "No observation during tenure, so no gravity was recorded.",
  },
  "affiliations.record.tenure": { ja: "在籍", en: "Tenure" },
  "affiliations.record.yourTrajectory": {
    ja: "あなたの軌道",
    en: "Your trajectory",
  },
  "affiliations.record.orgTrajectory": {
    ja: "組織の軌道とできごと",
    en: "Organization trajectory & chronicle",
  },
  "affiliations.record.scrubHint": {
    ja: "のできごと（軌道をスクラブ）",
    en: "— scrub the trajectory",
  },
  "affiliations.record.noRecordMonth": {
    ja: "この月の記録はありません。",
    en: "No record this month.",
  },
  "affiliations.record.handover": {
    ja: "世代交代",
    en: "Generational handover",
  },
  "affiliations.record.anonymizeAll": {
    ja: "全員を匿名化",
    en: "Anonymize everyone",
  },
  "affiliations.record.handoverNote": {
    ja: "既定では各メンバーの開示設定を尊重します（開示している人は実名、そうでない人は Member-N）。全員を匿名化したい場合は上のトグルで。",
    en: "By default each member's own disclosure is respected (named if they disclose, Member-N if not). Toggle above to anonymize everyone.",
  },
  "affiliations.teaser.title": {
    ja: "在籍期間の完全な記録を Pro で開く",
    en: "Open your full tenure record with Pro",
  },
  "affiliations.teaser.body": {
    ja: "在籍ウィンドウの軌道・組織のできごと・世代交代ヒートマップを期間ごとに探索できます。重力の見出しと証明書はこのまま無料で見られます。",
    en: "Explore your in-tenure trajectory, the organization's chronicle, and the generational-handover heatmap period by period. Your gravity headline and certificate stay free, right here.",
  },
  "affiliations.teaser.cta": {
    ja: "Pro で記録を開く",
    en: "Open the record with Pro",
  },
  "affiliations.legacy.title": { ja: "レガシーの足跡", en: "Legacy footprint" },
  "affiliations.legacy.subtitle": {
    ja: "作者を超えて生き残るコード — 持続する構造的影響力",
    en: "Code that outlives its author — structural influence that persists",
  },
  "affiliations.legacy.rawSurvival": {
    ja: "Raw Survival（ピーク）",
    en: "Raw Survival (peak)",
  },
  "affiliations.legacy.currentSurvival": {
    ja: "現在の Survival",
    en: "Current Survival",
  },
  "affiliations.legacy.decayRate": { ja: "減衰率", en: "Decay rate" },
  "affiliations.legacy.dissolved": { ja: "溶解", en: "dissolved" },
  // Aspiration framing (not loss): the departed member gets to WITNESS their work
  // living on. No "act or it disappears" pressure.
  "affiliations.legacy.live": {
    ja: "古巣はいまも観測を続けています。あなたが残したコードがどれだけ生き続けているか、その軌跡を見届けられます。",
    en: "Your old galaxy is still being observed. Watch how much of what you built lives on.",
  },
  "affiliations.legacy.frozenNote": {
    ja: "退職時点で凍結された値です。",
    en: "Frozen at the moment you left.",
  },
  "affiliations.legacy.orphanedTitle": {
    ja: "後継者なしの重力",
    en: "Gravity without a successor",
  },
  "affiliations.legacy.orphanedNote": {
    ja: "あなたが番人だったモジュールのうち {count} 個が、退職後もあなたの重力に依存したまま — resident な後継者が現れていません。",
    en: "{count} of the modules you stewarded still lean on your gravity — no resident successor has emerged since you left.",
  },
  "affiliations.legacy.proTeaser": {
    ja: "残したコードのその後を見届ける（Pro）",
    en: "Witness what becomes of your legacy (Pro)",
  },
  "affiliations.legacy.busFactorTitle": {
    ja: "バス係数リスク評価",
    en: "Bus factor risk assessment",
  },
  "affiliations.record.certificate": { ja: "証明書", en: "Certificate" },
  "affiliations.record.hiddenHere": {
    ja: "あなたはこの銀河で非表示（Member-N として匿名化）です。",
    en: "You are hidden in this galaxy (anonymized as Member-N).",
  },
  "affiliations.record.hideYourself": {
    ja: "この銀河で自分を非表示にする",
    en: "Hide yourself in this galaxy",
  },
  "affiliations.record.restoreHere": {
    ja: "表示を元に戻す",
    en: "Restore visibility",
  },
  "affiliations.cards.title": { ja: "カード＆グラフ", en: "Cards & graphs" },
  "affiliations.cards.tenureLine": {
    ja: "{login} ・ 在籍 {start}–{end}（期間固定）",
    en: "{login} · tenure {start}–{end} (fixed)",
  },
  "affiliations.cards.anonymizeHandover": {
    ja: "世代交代を全員匿名化",
    en: "Anonymize everyone in handover",
  },
  "affiliations.cards.failed": {
    ja: "カードを生成できませんでした。",
    en: "Could not generate cards.",
  },
  "affiliations.cards.footer": {
    ja: "URL を知っている人だけが見られます。すべてあなたの凍結値のみで、他のメンバーは含まれません。",
    en: "Visible only to those with the URL. Everything is your frozen figures only — no other members.",
  },
  "affiliations.cards.revokeHint": {
    ja: "貼り付け済みの埋め込みリンクを、まとめて無効化します。無効化すると、このページのカードは新しいリンクに置き換わります。",
    en: "Invalidates the embed links you've already pasted, all at once. After that, the cards on this page become fresh links.",
  },
  "affiliations.cards.revoke": {
    ja: "発行済みリンクを無効化",
    en: "Invalidate issued links",
  },
  "affiliations.cards.revokeConfirm": {
    ja: "無効化を確定",
    en: "Confirm invalidation",
  },
  "affiliations.cards.revokeCancel": { ja: "やめる", en: "Cancel" },
  "affiliations.cards.revoking": {
    ja: "無効化しています…",
    en: "Invalidating…",
  },
  "affiliations.cards.revoked": {
    ja: "既存のリンクを無効化しました。上のカードは新しいリンクです。",
    en: "Existing links invalidated. The cards above are fresh links.",
  },
  "affiliations.cards.revokeFailed": {
    ja: "無効化に失敗しました。時間をおいて再試行してください。",
    en: "Could not invalidate the links. Please try again in a moment.",
  },
  "career.veto.heading": {
    ja: "この銀河から発行された証明書",
    en: "Certificates issued from this galaxy",
  },
  "career.veto.intro": {
    ja: "退職メンバーが自分でこの銀河を発行体として Gravity Certificate を発行します。事実に誤りがある場合のみ無効化できます（発行を止める権限ではありません）。",
    en: "Departed members mint their own Gravity Certificate with this galaxy as the issuer. You can revoke one only if a fact is disputed — this is not a gate on issuance.",
  },
  "career.veto.accepted": { ja: "承認済み", en: "Accepted" },
  "career.veto.pending": { ja: "承認待ち", en: "Pending" },
  "career.veto.revoked": { ja: "無効化済み", en: "Revoked" },
  "career.veto.revoke": { ja: "無効化", en: "Revoke" },
  // Revoke-confirm modal (mirrors SlackDisconnectConfirm's parent-owned pattern):
  // revoking a galaxy-signed Gravity Certificate is irreversible-facing, so the
  // destructive action gets a confirm step. {subject} marks the certificate's
  // subject login (kept red, inline) spliced via splitMarkup.
  "career.veto.confirmTitle": {
    ja: "証明書を無効化",
    en: "Revoke certificate",
  },
  "career.veto.confirmBody": {
    ja: "{subject} 宛に発行された Gravity Certificate を無効化します。事実に誤りがある場合のみ実行してください。",
    en: "Revoke the Gravity Certificate issued to {subject}. Do this only when a signed fact is disputed.",
  },
  "career.veto.confirmNote": {
    ja: "署名が取り消され、証明書は検証時に「無効化済み」と表示されます。観測データ自体は保持されます（D-07）。",
    en: "The signature is voided and the certificate verifies as “revoked”. The observation data itself is kept (D-07).",
  },
  "career.veto.confirmCancel": { ja: "キャンセル", en: "Cancel" },
  "career.veto.confirmRevoke": { ja: "無効化する", en: "Revoke" },
  // OSS research participation (oss-claim.md) — BROADCAST-only opt-in. The framing
  // is an invitation ("help EIS's open research"), not a defensive opt-out.
  // Personal Gravity Card (self-observation wedge)
  "gravityCard.loadError": {
    ja: "カードの読み込みに失敗しました。",
    en: "Failed to load your card.",
  },
  "gravityCard.notObserved.title": {
    ja: "まだ観測されていません",
    en: "Not observed yet",
  },
  "gravityCard.notObserved.body": {
    ja: "あなたのコードはまだ観測対象の公開リポジトリに含まれていません。対象が広がると、あなたが書いて生き残ったコードがここに現れます。",
    en: "Your code isn't in the observed public repos yet. As the observed set grows, the code you wrote that survived will appear here.",
  },
  "gravityCard.observedSummary": {
    ja: "{galaxies} 銀河 · {months}ヶ月の観測",
    en: "{galaxies} galaxies · {months} months",
  },
  "gravityCard.gravityUnit": { ja: "重力", en: "grav" },
  "gravityCard.signalUnit": { ja: "シグナル", en: "sig" },
  "gravityCard.archetypes": {
    ja: "アーキタイプの通算",
    en: "Your archetypes over time",
  },
  "gravityCard.archetypes.note": {
    ja: "各観測期にどの型だったか。順位ではなく、あなた自身の時間の分布です。",
    en: "Which archetype you were, window by window — your own distribution over time, not a ranking.",
  },
  "gravityCard.lifetime": { ja: "通期重力", en: "Lifetime gravity" },
  "gravityCard.lifetime.sub": {
    ja: "あなたが書いたコードのうち、今も生き残っている分の重み",
    en: "the weight of the code you wrote that is still alive today",
  },
  "gravityCard.careerTotal": { ja: "累積重力", en: "Career total" },
  "gravityCard.careerSignal": { ja: "累積シグナル", en: "Signal total" },
  "gravityCard.activeMonths": { ja: "{d} 活動", en: "{d} active" },
  "gravityCard.maxGravity": { ja: "最大重力", en: "Max gravity" },
  "gravityCard.maxSignal": { ja: "最大シグナル", en: "Max signal" },
  "gravityCard.topSurviving": { ja: "代表作", en: "Notable work" },
  "gravityCard.topGalaxies": { ja: "主要銀河", en: "Top galaxies" },

  // Career footprint share (the /me/career cross-galaxy hero). Image-only: the
  // aggregate spans multiple galaxies, so there is no hosted public link — the
  // subject exports their OWN card as a PNG (copy or download), never a URL.
  "footprintShare.share": { ja: "共有", en: "Share" },
  "footprintShare.title": {
    ja: "フットプリントを画像として共有",
    en: "Share your footprint as an image",
  },
  "footprintShare.copyImage": { ja: "画像をコピー", en: "Copy image" },
  "footprintShare.copied": { ja: "コピーしました", en: "Copied" },
  "footprintShare.download": { ja: "PNG をダウンロード", en: "Download PNG" },
  "footprintShare.copyFellBackToDownload": {
    ja: "クリップボードが使えないため画像を保存しました",
    en: "Clipboard unavailable — saved the image instead",
  },
  // Public career-card share destinations (mirrors the per-galaxy certificate menu).
  "footprintShare.shareOnX": { ja: "X で共有", en: "Share on X" },
  "footprintShare.shareOnFacebook": {
    ja: "Facebook で共有",
    en: "Share on Facebook",
  },
  "footprintShare.shareOnLinkedin": {
    ja: "LinkedIn で共有",
    en: "Share on LinkedIn",
  },
  "footprintShare.copyLink": {
    ja: "公開リンクをコピー",
    en: "Copy public link",
  },
  "footprintShare.linkCopied": { ja: "コピーしました", en: "Copied" },
  "footprintShare.openPage": { ja: "公開ページを開く", en: "Open public page" },
  "footprintShare.editGalaxies": {
    ja: "銀河の公開設定",
    en: "Galaxy visibility",
  },
  "footprintShare.unpublish": { ja: "公開を取り消す", en: "Unpublish" },
  "footprintShare.revoked": {
    ja: "公開を取り消しました",
    en: "Card unpublished",
  },
  "footprintShare.shareText": {
    ja: "銀河を横断した私のキャリア重力フットプリント（OrbitLens）",
    en: "My cross-galaxy career gravity footprint on OrbitLens",
  },
  "footprintShare.shareNative": { ja: "共有…", en: "Share…" },
  // Career-card galaxy-visibility editor.
  "careerCard.state.named": { ja: "名前を出す", en: "Show name" },
  "careerCard.state.anon": { ja: "名前を伏せる", en: "Hide name" },
  "careerCard.state.excluded": { ja: "載せない", en: "Leave off" },
  "careerCard.editor.title": {
    ja: "銀河名を伏せますか？",
    en: "Hide galaxy names?",
  },
  "careerCard.editor.subtitle": {
    ja: "各銀河について、名前を出す / 名前を伏せる（Galaxy A）/ 載せない、を選べます。数値は伏せても出ます。",
    en: "For each galaxy: show its name, hide it (Galaxy A), or leave it off. Figures show either way.",
  },
  "careerCard.editor.private": { ja: "非公開", en: "private" },
  "careerCard.editor.privateNamedWarning": {
    ja: "非公開の銀河の名前を出すと、そこに所属していたことが公開されます。",
    en: "Showing a private galaxy's name publishes that you belonged to it.",
  },
  "careerCard.editor.note": {
    ja: "公開されるのはあなた自身の数値だけです。他のメンバーは一切含まれません。名前を伏せた銀河は、名前もアイコンも出ません。",
    en: "Only your own figures are published — no other member is ever included. A hidden galaxy shows neither name nor icon.",
  },
  "careerCard.editor.publish": { ja: "公開する", en: "Publish" },
  "careerCard.editor.update": { ja: "更新する", en: "Update" },
  "careerCard.editor.publishing": { ja: "公開中...", en: "Publishing..." },
  "careerCard.editor.published": { ja: "公開しました", en: "Published" },
  "careerCard.editor.empty": {
    ja: "共有できるキャリア観測がまだありません。まず証明書を発行してください。",
    en: "No career observations to share yet — seal a certificate first.",
  },

  "oss.title": {
    ja: "EIS の公開研究に協力する",
    en: "Join EIS's open research",
  },
  "oss.subtitle": {
    ja: "EIS は公開 OSS を観測して「どのコードが生き残り、誰が構造を支えたか」を出版します（OSS Gravity Map ほか）。既定では、あなたは匿名（Member-N）です。望むなら、あなたの名前で協力できます。",
    en: "EIS observes public open source and publishes which code survives and who held its structure (the OSS Gravity Map and more). By default you are anonymous (Member-N). If you want, you can take part under your own name.",
  },
  "oss.note": {
    ja: "これは公開研究の出版物にだけ効きます。Ace にログインして公開銀河を観測したときの表示は変わりません。あなたの名前が出るのは、ここで協力を選んだときだけです。いつでも変更・撤回できます。",
    en: "This affects only the PUBLISHED research. It does not change what an authenticated viewer sees in Ace. Your name appears only if you opt in here, and you can change or withdraw it anytime.",
  },
  "oss.identity": {
    ja: "あなたの GitHub アイデンティティ",
    en: "Your GitHub identity",
  },
  "oss.research.title": {
    ja: "あなたの観測された研究重力",
    en: "Your observed research gravity",
  },
  "oss.research.desc": {
    ja: "公開研究で観測された、あなた自身の重力です（あなたにだけ見えています）。協力を選べば、この重力をあなたの名前で公開できます。",
    en: "Your own gravity as observed in the public research — visible only to you. If you take part, you can publish it under your name.",
  },
  "oss.loadError": {
    ja: "参加設定を読み込めませんでした。GitHub で観測された公開アイデンティティが必要です。",
    en: "Couldn't load your participation settings. A GitHub-resolved public identity is required.",
  },
  "oss.opt.full.label": { ja: "実名 ＋ 重力を公開", en: "Name + gravity" },
  "oss.opt.full.desc": {
    ja: "出版物にあなたの実名と重力を出す。最も協力的。",
    en: "Show your real name and gravity in published research. The fullest contribution.",
  },
  "oss.opt.name_only.label": { ja: "実名のみ", en: "Name only" },
  "oss.opt.name_only.desc": {
    ja: "実名と役割は出すが、重力は伏せる。",
    en: "Show your real name and role, but withhold the gravity.",
  },
  "oss.opt.private.label": { ja: "匿名のまま", en: "Stay anonymous" },
  "oss.opt.private.desc": {
    ja: "観測には含まれるが、出版物では名前を出さない（Member-N）。",
    en: "Stay in the observation, but never named in publications (Member-N).",
  },
  "oss.opt.removed.label": {
    ja: "公開研究から外す",
    en: "Remove from public research",
  },
  "oss.opt.removed.desc": {
    ja: "匿名の集計からも外す。出版物に一切現れない（観測データは削除されません）。",
    en: "Drop out of the anonymous aggregate too — never appear in publications (your observation data is not deleted).",
  },
  "oss.saved": { ja: "参加設定を更新しました", en: "Participation updated" },
  "oss.requestParticipate": {
    ja: "EIS の公開研究に協力する",
    en: "Join EIS's open research",
  },
  "oss.targets.title": {
    ja: "公開研究の対象リポジトリ",
    en: "Public-research target repositories",
  },
  "oss.targets.desc": {
    ja: "EIS は次の OSS リポジトリを観測対象にしています。これらにコントリビュートしているなら、ぜひ実名で協力してください。",
    en: "EIS observes the following open-source repositories. If you contribute to any of them, please take part under your real name.",
  },
  "oss.targets.futureNote": {
    ja: "コントリビューターでなくても、いま参加設定をしておけば、今後対象が追加されたときにスムーズに公開できます。",
    en: "Even if you're not a contributor yet, setting your participation now means you'll be published smoothly when new targets are added.",
  },
  "oss.targets.empty": {
    ja: "現在、公開されている対象リポジトリはありません。",
    en: "No target repositories are listed right now.",
  },
  "oss.request.title": {
    ja: "対象リポジトリの追加をリクエスト",
    en: "Request a repository to be added",
  },
  "oss.request.desc": {
    ja: "観測してほしい OSS リポジトリがあれば教えてください。運営が検討します。",
    en: "Tell us an open-source repository you'd like observed. We'll review it.",
  },
  "oss.request.placeholder": {
    ja: "owner/repo（例: machuz/eis）",
    en: "owner/repo (e.g. machuz/eis)",
  },
  "oss.request.submit": { ja: "リクエストを送る", en: "Send request" },
  "oss.request.sent": {
    ja: "リクエストを送りました。ありがとうございます。",
    en: "Request sent — thank you.",
  },
  "oss.request.invalid": {
    ja: "owner/repo の形式で入力してください。",
    en: "Enter it as owner/repo.",
  },
  "disclosure.ossExplainer": {
    ja: "公開 OSS の出版研究（OSS Gravity Map）で実名を出すかは別の設定です。既定は匿名。",
    en: "Whether your real name appears in the PUBLISHED OSS research (the OSS Gravity Map) is a separate choice. Anonymous by default.",
  },
  "membersRO.subtitle": {
    ja: "この銀河のメンバー一覧（閲覧のみ）",
    en: "Members of this galaxy (view only)",
  },
  "membersRO.adminOnly": {
    ja: "メンバーや課金席の管理は管理者のみが行えます。",
    en: "Only an admin can manage members and billable seats.",
  },
  "membersRO.total": { ja: "全 {count} 名", en: "{count} total" },
  "membersRO.empty": { ja: "メンバーがいません。", en: "No members." },

  // === i18n-migration: observatory / observation / modules / constellation ===
  "observatory.showMore": {
    ja: "さらに表示（残り {remaining} 名）",
    en: "Show more ({remaining} remaining)",
  },
  "observatory.partialFailure": {
    ja: "直近の観測は一部のドメインが未完了のため反映を見送りました（前回の完全な観測を表示中）。",
    en: "The latest run was incomplete (some domains failed) and was not applied — showing the last complete observation.",
  },
  "observatory.failureCount": {
    ja: "直近の観測で {count} 件の失敗があります（clone 失敗など）。一部のリポジトリが観測されていない可能性があります。",
    en: "{count} failure(s) in recent observations (e.g. clone failed). Some repositories may not be observed.",
  },
  "observatory.allMembers": { ja: "全メンバー", en: "All Members" },
  "observatory.searchMembers": {
    ja: "メンバーを名前で検索…",
    en: "Search members by name…",
  },
  "observatory.searchMatch": {
    ja: "「{query}」に一致: {count} 名",
    en: '{count} match "{query}"',
  },
  "observatory.peripheral": { ja: "周辺メンバー", en: "Peripheral" },
  "observatory.excludedFromHealth": {
    ja: "ヘルス集計から除外",
    en: "excluded from health metrics",
  },
  "observatory.nonDisclosed": { ja: "非開示メンバー", en: "Non-disclosed" },
  "observatory.moreCore": {
    ja: "ほか {count} 名がコア",
    en: "{count} more core",
  },
  "observatory.domainNoObservations": {
    ja: "{domain} ドメインにはまだ観測データがありません。",
    en: "{domain} domain has no observations yet.",
  },
  "observatory.tryAnotherDomain": {
    ja: "他のドメインタブを選択するか、観測を実行してください。",
    en: "Try selecting another domain tab, or run an observation.",
  },
  "observatory.ageDay": { ja: "{n}日前", en: "{n} day ago" },
  "observatory.ageDays": { ja: "{n}日前", en: "{n} days ago" },
  "observatory.ageHour": { ja: "{n}時間前", en: "{n} hour ago" },
  "observatory.ageHours": { ja: "{n}時間前", en: "{n} hours ago" },
  "observatory.ageMin": { ja: "{n}分前", en: "{n} min ago" },
  "observatory.coreCount": { ja: "コア{count}名", en: "{count} core" },
  "observatory.teamTrajectory": { ja: "チーム軌道", en: "Team Trajectory" },
  "observatory.avgSignalPerMember": {
    ja: "平均シグナル（一人あたり）",
    en: "Average Signal (per member)",
  },
  "observatory.aggregateSignalSum": {
    ja: "総合シグナル（合計）",
    en: "Aggregate Signal (sum)",
  },
  "observationRuns.loading": {
    ja: "観測履歴を読み込み中…",
    en: "Loading observation runs…",
  },
  "observationRuns.title": { ja: "観測履歴", en: "Observation Runs" },
  "observationRuns.subtitle": {
    ja: "ワーカーが何をいつ観測し、どこで失敗したかの記録。",
    en: "Record of what the worker observed and where it failed.",
  },
  "observationRuns.runs": { ja: "観測 run 数", en: "Runs" },
  "observationRuns.events": { ja: "観測イベント数", en: "Events" },
  "observationRuns.pendingFailures": {
    ja: "未解消の失敗",
    en: "Pending failures",
  },
  "observationRuns.resolvedFailures": {
    ja: "解消済みの失敗",
    en: "Resolved failures",
  },
  "observationRuns.empty": {
    ja: "まだ観測履歴がありません。Observe を実行すると記録されます。",
    en: "No observation runs yet — the history shows up here after an Observe.",
  },
  "modules.riskStatus.risk": { ja: "危険", en: "Risk" },
  "modules.riskStatus.watch": { ja: "注意", en: "Watch" },
  "modules.riskStatus.good": { ja: "良好", en: "Good" },
  "modules.axisLegend": {
    ja: "バーの長さ＝指標値（0–100）。色で良し悪しを表す（緑=良好 / 黄=注意 / 赤=危険）",
    en: "Bar length = metric value (0–100); colour shows good/bad (Green=Good / Yellow=Watch / Red=Risk)",
  },
  "modules.scopeFallback": { ja: "スコープ", en: "scope" },
  "modules.coverageTitle": {
    ja: "{domain}: {scope}が保有するモジュール {held} / 全 {total}（{pct}%）",
    en: "{domain}: modules held by {scope} {held} of {total} ({pct}%)",
  },
  "modules.coverageChip": {
    ja: "{scope}保有 {held} / 総数 {total}",
    en: "{scope} {held} / total {total}",
  },
  "modules.collapseFootnote": {
    ja: "占有率＝主担い手の生存重力シェア / 退職保有＝退職者が握る生存重力。担い手は開示レベルに従い匿名表示されます。",
    en: "Top share = the lead steward's gravity share; Departed = surviving gravity held by departed owners. Stewards follow disclosure-level masking.",
  },
  "modules.busFactorDrop": { ja: "bus factor 1 に転落", en: "→ bus factor 1" },
  "modules.handoverTitle": {
    ja: "{who} の引き継ぎ — {module}",
    en: "{who} handover — {module}",
  },
  "modules.handoverDesc": {
    ja: "{who} が退職。{repo}/{module} の生存重力 {share}% を保持（モジュールの退職保有 {departedShare}%）。\n引き継ぎメモ:\n- 設計意図:\n- 注意点:\n- 後任:",
    en: "{who} departed, holding {share}% of {repo}/{module}'s surviving gravity (module departed-held {departedShare}%).\nHandover notes:\n- Design intent:\n- Gotchas:\n- Successor:",
  },
  "constellation.roleLifecycle": {
    ja: "役割ライフサイクル",
    en: "Role Lifecycle",
  },
  "constellation.roleLifecycleSub": {
    ja: "役割構成をヒートマップで読む — 各セルはその年にその役割を担ったエンジニア数で、役割ごとの最大値で陰影をつけている。コードベースが成熟するにつれ、量産型の Producer が Cleaner や Anchor に道を譲る様子が見える。",
    en: "The same role mix read as a heatmap — each cell is the engineers in that role that year, shaded against the role's own peak. Watch how output-heavy Producers give way to Cleaners and Anchors as the codebase matures.",
  },

  // === i18n-migration: settings / invite / visibility / chrome ===
  "settings.slack.thisWorkspace": {
    ja: "このワークスペース",
    en: "this workspace",
  },
  "settings.slack.confirmTitle": {
    ja: "Slack 連携を解除",
    en: "Disconnect Slack",
  },
  "settings.slack.confirmKept": {
    ja: "これまでに取り込まれた年表の記録は保持されます。再連携はいつでも可能です。",
    en: "Chronicle entries already landed are kept. You can reconnect any time.",
  },
  // Disconnect-confirm body. The {workspace} token marks where the workspace
  // name (kept red, inline) is spliced in via splitMarkup() — one table entry per
  // sentence, no copy split across i18n.ts + JSX, and no empty-string lookup.
  "settings.slack.confirmBody": {
    ja: "{workspace} との連携を解除します。今後リアクションを付けても年表には取り込まれなくなります。",
    en: "Disconnect {workspace}. New reactions will no longer be landed into the chronicle.",
  },
  "settings.slack.cancel": { ja: "キャンセル", en: "Cancel" },
  "settings.slack.confirmDisconnect": { ja: "解除", en: "Disconnect" },
  "invite.processing": {
    ja: "招待を処理中...",
    en: "Processing invitation...",
  },
  "invite.joined": { ja: "参加しました", en: "Joined!" },
  "invite.joinedBody": {
    ja: "組織に参加しました。Observatoryでシグナルを確認しましょう。",
    en: "You've joined the organization. Head to the Observatory.",
  },
  "invite.openObservatory": { ja: "Observatoryを開く", en: "Open Observatory" },
  "invite.error": { ja: "エラー", en: "Error" },
  "invite.goHome": { ja: "ホームへ", en: "Go Home" },
  "visibilityMode.title": { ja: "可視性モード", en: "Visibility Mode" },
  "visibilityMode.subtitle": {
    ja: "この銀河で、メンバーにどの程度の情報を表示するかを制御します。",
    en: "Controls how much detail is visible to members of this galaxy.",
  },
  "visibilityMode.adminOnly": {
    ja: "この銀河の管理者のみ変更できます。",
    en: "Only an admin of this galaxy can change this.",
  },
  "visibilityMode.observationDesc": {
    ja: "匿名。構造パターンのみ表示",
    en: "Anonymous members, structural patterns only",
  },
  "visibilityMode.contextDesc": {
    ja: "名前・役割表示、トレンド可視",
    en: "Roles visible, trends, partial identity",
  },
  "visibilityMode.fullInsightDesc": {
    ja: "全プロフィール・個別シグナル表示",
    en: "Full profiles, individual signals",
  },
  "visibilityMode.current": { ja: "現在", en: "Current" },
  "visibilityMode.alumniLegacy.title": {
    ja: "退職者のレガシー閲覧",
    en: "Alumni Legacy View",
  },
  "visibilityMode.alumniLegacy.toggle": {
    ja: "退職者に自分のレガシー減衰の閲覧を許可",
    en: "Allow alumni to watch their legacy decay",
  },
  "visibilityMode.alumniLegacy.desc": {
    ja: "退職したメンバーが、自分が残したコードの Survival がこの銀河で減衰していく様子を見られるようにします。本人の自分のコードのみが対象で、他のメンバーや集計は一切含まれません。証明書は常に退職時点で凍結されます。既定はオフです。",
    en: "Lets a departed member watch the Survival of the code THEY left behind decay in this galaxy. Strictly their own contribution — never another member, never an aggregate. The Gravity Certificate stays frozen-at-departure regardless. Default off.",
  },
  "visibilityMode.alumniLegacy.saving": { ja: "保存中…", en: "Saving…" },
  "visibilityMode.openShare.title": { ja: "オープン共有", en: "Open Share" },
  "visibilityMode.openShare.toggle": {
    ja: "誰でもこの銀河を共有銀河に追加できるようにする",
    en: "Let anyone add this galaxy to their shared list",
  },
  "visibilityMode.openShare.desc": {
    ja: "オンにすると、サインインしている人なら誰でも、この銀河のログインを入力して自分の「共有された銀河」に読み取り専用で追加できます。招待型（相手を指定して共有）とは別に、公開して自由に閲覧してもらうための設定です。書き込みはできません。追加した人へのメンバーの見せ方は下の「追加した人への見せ方」で選べます（既定=匿名。フルにすると銀河の可視性モードと各メンバーの同意に従い実名も出得ます）。既定はオフです。",
    en: 'When on, any signed-in user can add this galaxy to their own read-only "shared" list by typing its login — a public, self-serve complement to inviting a specific person. They can never write. How members appear to self-adders is set by "Show members to self-adders as" below (default anonymous; full follows the galaxy\'s visibility mode and each member\'s consent, so real names can appear). Default off.',
  },
  "visibilityMode.openShare.saving": { ja: "保存中…", en: "Saving…" },
  "visibilityMode.openShare.disclosureLabel": {
    ja: "追加した人への見せ方",
    en: "Show members to self-adders as",
  },
  "visibilityMode.openShare.fullAudienceNote": {
    ja: "⚠ 招待（相手を指定）と違い、オープン共有の相手は不特定です。フル公開にすると、この銀河のログインを入力して self-add した誰にでも、公開メンバーの実名とシグナルが見えます。各メンバーは「自分の見え方」でこの露出を確認できます。",
    en: "⚠ Unlike an invite (a named person), open-share viewers are unbounded. With Full, any third party who self-adds this galaxy by login sees public members' real names and signals. Each member can review this exposure under their own disclosure settings.",
  },
  // Language & Time zone page (moved off EIS Config + Visibility Mode).
  "preferences.pageTitle": {
    ja: "言語・タイムゾーン",
    en: "Language & Time zone",
  },
  "preferences.pageSubtitle": {
    ja: "表示に関する設定です。観測データには一切影響しません。",
    en: "Display settings — none of these affect observation data.",
  },
  "preferences.saving": { ja: "保存中…", en: "Saving…" },
  "preferences.galaxyLanguage.title": {
    ja: "銀河の言語",
    en: "Galaxy language",
  },
  "preferences.galaxyLanguage.desc": {
    ja: "この銀河から配信される通知（Slack 週次ダイジェストなど）の言語です。チャンネル全体への一斉配信なので、個人ごとでなく銀河単位で設定します。",
    en: "The language of notifications sent from this galaxy (e.g. the weekly Slack digest). It is a galaxy-wide setting, not per-user, because those go to a whole channel at once.",
  },
  "preferences.galaxyLanguage.adminOnly": {
    ja: "管理者のみ変更できます。",
    en: "Only an admin can change this.",
  },
  "preferences.galaxyLanguage.ja": { ja: "日本語", en: "Japanese" },
  "preferences.galaxyLanguage.en": { ja: "英語", en: "English" },
  "devMenu.erasureHint": {
    ja: "観測された全 git author（メンバー＋非メンバー）が対象。本人操作は Presence ページ。",
    en: "Any observed git author (members + non-members). Self-serve UI: Presence page.",
  },
  "devMenu.disclosureHint": {
    ja: "メンバーが自分のシグナルをチームに共有する設定。本人操作は 設定 ページ。",
    en: "A member sharing their own signals with the team. Self-serve UI: Settings.",
  },
  "devMenu.quotaTitle": {
    ja: "観測回数 (per-day quota)",
    en: "Observation quota (per-day)",
  },
  "devMenu.quotaDesc": {
    ja: "直近24hの observation_log を削除して観測回数を復活（観測本体は不変）。",
    en: "Delete the last 24h of observation_log to revive the quota (snapshots untouched).",
  },
  "devMenu.quotaReset": { ja: "観測回数を復活", en: "Reset observation quota" },
  "devMenu.onboardingTitle": { ja: "オンボーディング", en: "Onboarding" },
  "devMenu.onboardingDesc": {
    ja: "この銀河の onboarding_completed を false に戻し、リロードで初回フローへ戻る。",
    en: "Flip this galaxy's onboarding_completed back to false and reload into the first-run flow.",
  },
  "devMenu.onboardingRestart": {
    ja: "オンボーディングへ飛ぶ",
    en: "Restart onboarding",
  },
  "devMenu.wipeTitle": {
    ja: "観測データを全削除（初回観測デバッグ）",
    en: "Wipe observation data (first-run debug)",
  },
  "devMenu.wipeDesc": {
    ja: "この銀河の観測データ（signals / snapshots / timeline / failures）を物理削除し、S3 の blame / clone キャッシュも消して未観測状態に戻します（次の観測はコールド）。ノート・ADR・証明書・スケジュールは保持。元に戻せません。stg/local のみ。",
    en: "Physically delete this galaxy's observation data (signals / snapshots / timeline / failures) AND evict its S3 blame / clone caches, resetting it to the never-observed state (next observation runs cold). Notes, ADRs, certificates and schedules are kept. Irreversible. stg/local only.",
  },
  "devMenu.wipeButton": {
    ja: "観測データを全削除",
    en: "Wipe observation data",
  },
  "devMenu.wipeConfirm": {
    ja: "本当に全削除（元に戻せません）",
    en: "Confirm — delete everything (irreversible)",
  },
  "devMenu.wipeCancel": { ja: "キャンセル", en: "Cancel" },
  "devMenu.revealAllTitle": {
    ja: "全開示モード (reveal all)",
    en: "Reveal-all mode",
  },
  "devMenu.revealAllDesc": {
    ja: "可視化・開示設定を全無視して全員のシグナルを実名フル表示（値の妥当性確認用）。stg/local のみ有効。",
    en: "Ignore all disclosure/visibility and show every member's signals in full (value verification). stg/local only.",
  },
  "devMenu.revealAllOn": {
    ja: "全開示: ON（クリックでOFF）",
    en: "Reveal-all: ON (click to turn off)",
  },
  "devMenu.revealAllOff": {
    ja: "全開示: OFF（クリックでON）",
    en: "Reveal-all: OFF (click to turn on)",
  },
  "devMenu.archiveDesc": {
    ja: "現在の銀河を退職アーカイブにする/戻す。cert クリアで再 mint をクリーンに。",
    en: "Archive (depart) / restore the current galaxy affiliation. Clear cert to re-mint cleanly.",
  },
  "sidebar.scopeFiltered": {
    ja: "現在のチームスコープで絞り込み中",
    en: "Filtered by the active team scope",
  },
  "sidebar.scopeAware": {
    ja: "チームスコープの影響を受けるページ",
    en: "Reflects the team scope when one is selected",
  },
  "sidebar.reposOversize": {
    ja: "{count} 件のリポジトリが無料プランの上限を超えており、観測されていません",
    en: "{count} repo(s) exceed the free plan size limit and are not being observed",
  },
  "sidebar.workerComplete": { ja: "観測完了", en: "complete" },
  "sidebar.workerFailed": { ja: "失敗", en: "failed" },
  "sidebar.workerObserving": { ja: "観測中…", en: "observing…" },
  "seatGate.title": {
    ja: "非公開チームの観測所",
    en: "A private team's observatory",
  },
  "seatGate.body": {
    ja: "この銀河は非公開リポジトリを含みます。観測の閲覧には課金席が必要です。管理者に席の割り当てを依頼するか、Nova で席を購入してください。",
    en: "This galaxy includes private repositories. Viewing its observations requires a paid seat — ask an admin to assign you one, or get a seat with Nova.",
  },
  "seatGate.publicNote": {
    ja: "公開リポジトリだけの銀河は席なしで閲覧できます。",
    en: "Galaxies with only public repos are viewable without a seat.",
  },
  "seatGate.cta": { ja: "Nova で席を取得", en: "Get a seat with Nova" },
  "common.dismiss": { ja: "閉じる", en: "Dismiss" },
  "lapsedBanner.title.pastDue": {
    ja: "Nova の支払いに失敗しています",
    en: "Your Nova payment failed",
  },
  "lapsedBanner.title.canceled": {
    ja: "Nova プランが失効しました",
    en: "Your Nova plan has lapsed",
  },
  "lapsedBanner.body.pastDue": {
    ja: "決済が確認できず、フル機能が停止しています。カード情報を更新すると自動で復帰します。",
    en: "We couldn't confirm your payment, so full features are paused. Update your card and it restores automatically.",
  },
  "lapsedBanner.body.canceled": {
    ja: "Free に戻っています。フル機能に戻すには Nova を再開してください。",
    en: "You're back on Free. Restart Nova to restore full features.",
  },
  "lapsedBanner.overSeats": {
    ja: "非公開の観測を閲覧できるのは{free}名まで。超過{over}名は席がなく閲覧できません。",
    en: "Only {free} members can view private observations — {over} are over the limit and locked out.",
  },
  "lapsedBanner.overRepos": {
    ja: "非公開リポは先頭1件のみ観測されます（残りは停止）。",
    en: "Only the first 1 private repo is observed (the rest are paused).",
  },
  "lapsedBanner.featuresOff": {
    ja: "Collapse Risk・Conway 検証・モジュールトポロジ・全期間履歴・証明書 は停止中。",
    en: "Collapse Risk, Conway check, module topology, full history, and certificates are off.",
  },
  "lapsedBanner.cta.fixPayment": { ja: "決済情報を更新", en: "Update payment" },
  "lapsedBanner.cta.resume": { ja: "Nova を再開", en: "Restart Nova" },
  "lapsedBanner.cta.review": {
    ja: "席・リポを見直す",
    en: "Review seats & repos",
  },
  "lapsedBanner.memberNote": {
    ja: "この銀河は Nova が失効中で、Free の制限で動作しています。管理者にご確認ください。",
    en: "This galaxy's Nova plan has lapsed; it's running under Free limits. Check with an admin.",
  },
  "archiveLayout.record": { ja: "記録", en: "Record" },
  "archiveLayout.cards": { ja: "カード＆グラフ", en: "Cards & graphs" },
  "archiveLayout.openMenu": { ja: "メニューを開く", en: "Open menu" },
  "archiveLayout.noRecord": {
    ja: "この銀河の所属記録はありません。",
    en: "No record of this affiliation.",
  },
  "galaxySwitcher.active": { ja: "アクティブ", en: "Active" },
  "galaxySwitcher.shared": { ja: "共有", en: "Shared" },
  "galaxySwitcher.archive": { ja: "アーカイブ", en: "Archive" },
  "teamScopeSwitcher.all": { ja: "全スコープ", en: "All engineers" },
  // ── i18n-guard migration (relocated from inline `locale === "ja" ? …` ternaries) ──
  // Pure relocation: every entry below carries the EXACT ja/en wording that
  // previously lived inline at the call site. No visible copy changed; this only
  // moves the strings into the table so the no-restricted-syntax guard (which now
  // also catches `locale === "ja"` ternaries) stays green.

  // Common — shared chrome reused across timeline/constellation views.
  "common.searchMembers": {
    ja: "メンバーを名前で検索…",
    en: "Search members by name…",
  },
  // interp({remaining},{total}): "show N more of M". Used by paginated lists
  // (heatmap, gravity map, cohort list) that previously spliced both counts.
  "common.showMore": {
    ja: "さらに表示（残り {remaining} 名 / 全 {total} 名）",
    en: "Show more ({remaining} of {total} remaining)",
  },

  // Embed / disclosure mask — the rotated "UNDISCLOSED" stamp on sparklines and
  // the radar fallback. Distinct from disclosure.private (en "Private"): these
  // render the literal word UNDISCLOSED, so a separate key keeps that wording.
  "embed.undisclosed": { ja: "非開示", en: "UNDISCLOSED" },

  // EventInput — range sub-controls.
  "eventInput.endAfterStart": {
    ja: "終了月は開始月以降",
    en: "End must be after start",
  },
  "eventInput.setRange": { ja: "期間指定", en: "Set range" },

  // WorkerStatus — ETA tooltip (clone-stage-only caveat).
  "worker.etaTooltip": {
    ja: "クローン工程の概算（シグナリング・保存は含まず）",
    en: "Approximate — covers the clone stage only (signaling + saving not included)",
  },

  // ConstellationFull — "all members" breadcrumb + header.
  "constellationFull.allMembers": { ja: "全メンバー", en: "All Members" },
  "constellationFull.heading": {
    ja: "星座図 — 全メンバー",
    en: "Constellation — All Members",
  },
  // Suffix after a count: "<n>人のエンジニア" / "<n> engineers".
  "constellationFull.engineersSuffix": {
    ja: "人のエンジニア",
    en: " engineers",
  },

  // DisclosureSettings — opt-out explainer + erasure link.

  // FeedbackTickets — row meta tooltips.
  "feedback.messagesTooltip": { ja: "メッセージ数", en: "messages" },
  "feedback.lastUpdatedTooltip": { ja: "最終更新", en: "last updated" },

  // HealthHero — sparkline empty state.
  "healthHero.noObservation": { ja: "観測データなし", en: "no observation" },

  // StarRadarPanel — historical-fallback note.
  "starRadar.historicalFallback": {
    ja: "直近の活動なし — 過去の観測データを表示中",
    en: "No recent activity — showing historical observation",
  },
  "starRadar.lastActive": {
    ja: "最終活動",
    en: "Last active",
  },
  "starRadar.productionWindowNote": {
    ja: "表示中の値は、最後に活動した観測時点のもの。生産量は長期ウィンドウの累積変更量であり、直近の活動量とは別の指標。",
    en: "These values are from the last active observation. Production is a cumulative change-volume over a long window — not a measure of recent activity.",
  },

  // StarDetail — self-only share. Bridges the viewer's OWN star to the Gravity
  // Certificate mint flow in Career Orbit. The raw star is never shared (it
  // carries the 7-axis scoring surface); only the aggregate-anonymous,
  // single-subject certificate leaves the telescope.
  "starDetail.selfShare.title": {
    ja: "あなたの実績を共有",
    en: "Share your record",
  },
  "starDetail.selfShare.body": {
    ja: "この観測ダッシュボードそのものは共有できません。共有できるのは、署名付きの Gravity Certificate — あなた自身の名前だけが実名で、銀河は集合体として匿名化された認定カードです。Career Orbit で、退職した所属は封じて、今いる銀河は「現在のスナップショット」として発行できます。",
    en: "This observation dashboard itself can't be shared. What you can share is a signed Gravity Certificate — a card where only your own name is identified and the galaxy is anonymized as an aggregate. In Career Orbit you seal a departed affiliation, or snapshot your current orbit in a galaxy you still belong to.",
  },
  // CTA — was "Mint a certificate in Career Orbit", which over-promised: minting
  // needs a departed affiliation, so a current member who clicked it just landed
  // on an empty state. Now it's honest navigation — "Open Career Orbit" — and the
  // mint affordance lives ON Career Orbit, shown only when something is sealable.
  "starDetail.selfShare.cta": {
    ja: "Career Orbit を開く",
    en: "Open Career Orbit",
  },
  "starDetail.selfShare.boundary": {
    ja: "7軸シグナル・順位・他メンバーは共有面に出ません（銀河境界）。",
    en: "No 7-axis signals, rankings, or other members leave the share surface (galaxy boundary).",
  },

  // StarDetail — empty / non-disclosed / per-repo section copy.
  "starDetail.noObservation": {
    ja: "このユーザーの観測データがありません",
    en: "No observation data for this user",
  },
  // interp({first},{last},{count}).
  "starDetail.availablePeriods": {
    ja: "利用可能: {first} — {last}（{count}期間）",
    en: "available: {first} — {last} ({count} periods)",
  },
  "starDetail.notDisclosed": {
    ja: "このメンバーは非開示設定です",
    en: "This member is not disclosed",
  },
  "starDetail.notDisclosedBody": {
    ja: "本人が表示設定を「匿名化／非表示」にしている（または開示に同意していない）ため、シグナルの数値は表示されません。観測データ自体は保持されています。",
    en: "Their signal values are hidden because they set their visibility to anonymized/hidden (or haven't opted into disclosure). The observation data itself is retained.",
  },
  "starDetail.maskAnonymized": {
    ja: "匿名化 — Observation",
    en: "anonymized — observation",
  },
  "starDetail.maskScoresHidden": {
    ja: "数値非開示 — Context",
    en: "signals hidden — context",
  },
  "starDetail.activeThisPeriod": { ja: "今期活動", en: "Active this period" },
  "starDetail.survivingTooltip": {
    ja: "ここに並ぶ数値は累積の生存重力（過去コードが今も残っている強さ）で、今期の活動量ではありません。",
    en: "These figures are cumulative surviving gravity (your past code still alive), not current activity.",
  },
  "starDetail.survivingHeading": {
    ja: "生存重力（今期活動なし）",
    en: "Surviving gravity (no activity this period)",
  },
  "starDetail.inactiveDomainTooltip": {
    ja: "これらは per_repo にエントリが無い repo、つまりこの期間に観測された痕跡（commit も生存行も）が無いリポジトリです。aggregate Total はこれらに届く場合があります。",
    en: "These repos have no per_repo entry — no observed trace (neither commits nor a surviving row) this period. The aggregate Total may still reach into them.",
  },
  // interp({count}).
  "starDetail.inactiveDomainHeading": {
    ja: "ドメイン内の他リポジトリ（観測痕跡なし） ({count})",
    en: "Other repos in domain (no observed trace) ({count})",
  },
  // interp({plan}).
  "starDetail.eventPlanLocked": {
    ja: "イベント記録には{plan}プラン以上が必要です",
    en: "{plan} plan or above required to record events",
  },
  "starDetail.viewPlans": { ja: "プランを確認", en: "View Plans" },
  "starDetail.collapse": { ja: "折りたたむ", en: "Collapse" },
  // interp({count}).
  "starDetail.showMoreEvents": {
    ja: "残り{count}件を表示",
    en: "Show {count} more",
  },

  // Logbook — header, observe button, panel chrome, org summary.
  "logbook.title": { ja: "観測日誌", en: "Logbook" },
  "logbook.subtitle": {
    ja: "観測日誌 — 重力がどう変化したかを記録する",
    en: "Observation logbook — how gravity shifted over time",
  },
  "logbook.observing": { ja: "観測中…", en: "Observing..." },
  "logbook.observe": { ja: "観測する", en: "Observe" },
  "logbook.selectStar": {
    ja: "スターを選択すると詳細が表示されます",
    en: "Select a star to inspect its gravity",
  },
  "logbook.orgSummary": { ja: "組織サマリー", en: "Organization Summary" },
  "logbook.avgDesign": { ja: "平均 Design", en: "Avg Design" },
  "logbook.roleMix": { ja: "ロール構成", en: "Role Mix" },

  // === i18n-migration: connectors / slack (SlackConnectorPage, ex #970) ===
  "connectors.slack.backToSettings": {
    ja: "設定へ戻る",
    en: "Back to settings",
  },
  "connectors.slack.pageTitle": { ja: "Slack 連携", en: "Slack Connector" },
  "connectors.slack.darkMatter": {
    ja: "EIS（git 観測）が測れない dark matter — 設計判断・プロジェクトの節目・退職など、git に刻まれない出来事 — を年表に残すための装置です。コネクタは生成しません。誰かが「これは記録すべき」と明示したマーク（リアクション）を観測して着地させるだけです。",
    en: "It's the device for the dark matter EIS (git observation) can't measure — design decisions, project milestones, departures: events that never touch git. The connector does not generate. It observes a mark a human already made (the reaction) and lands it; nothing is summarized or interpreted.",
  },
  "connectors.slack.connectedNotice": {
    ja: "Slack を接続しました。",
    en: "Slack connected.",
  },
  // Sentences that wrap an inline <code> mark (the trigger emoji or the /invite
  // example). {code} marks where the <code> element is spliced in via
  // splitMarkup, so the copy stays one i18n entry with exact word order while
  // the code element renders the (variable) emoji/command inline.
  "connectors.slack.intro": {
    ja: "Slack の重要な投稿に {code} リアクションを付けると、その投稿が組織年表（Chronicle）に観測として取り込まれます。",
    en: "React to an important Slack message with {code} and that message lands into your organization's chronicle as an observation.",
  },
  "connectors.slack.step1": {
    ja: "ワークスペースにカスタム絵文字 {code} を追加します。",
    en: "Add the custom emoji {code} to your workspace.",
  },
  "connectors.slack.step2": {
    ja: "対象チャンネルに Bot を招待します（{code}）。",
    en: "Invite the bot to the channels you want observed ({code}).",
  },
  "connectors.slack.step4": {
    ja: "投稿に {code} でリアクション → 数秒で年表に着地します（チーム可視・出典リンク付き・同じ投稿の重複は 1 件にまとまります）。",
    en: "React to a message with {code} → it lands in the chronicle within seconds (team-visible, with a link back to the source; duplicate reactions on the same message collapse to one entry).",
  },
  "connectors.slack.connectedDesc": {
    ja: "{code} が付いた投稿を年表に取り込みます。",
    en: "Messages reacted with {code} are landed into the chronicle.",
  },
  "connectors.slack.howTitle": { ja: "使い方", en: "How it works" },
  "connectors.slack.useThisImage": {
    ja: "この画像を使う（ダウンロード）",
    en: "Use this image (download)",
  },
  "connectors.slack.openEmojiPage": {
    ja: "Slack のカスタム絵文字ページを開く",
    en: "Open Slack's custom emoji page",
  },
  "connectors.slack.step3": {
    ja: "このページで「Slack を接続」します（下のボタン）。",
    en: "Connect Slack on this page (the button below).",
  },
  "connectors.slack.notTitle": {
    ja: "やらないこと",
    en: "What it deliberately does not do",
  },
  "connectors.slack.not1": {
    ja: "全メッセージは読みません。リアクションを付けた投稿だけを取り込みます。",
    en: "It does not read every message — only the ones you react to.",
  },
  "connectors.slack.not2": {
    ja: "AI で要約・解釈しません。投稿の本文をそのまま記録します。",
    en: "It does not summarize or interpret with AI — the message body is recorded verbatim.",
  },
  "connectors.slack.not3": {
    ja: "Slack に投稿し返しません（取り込みは一方向です）。",
    en: "It does not post back to Slack (ingestion is one-way).",
  },
  "connectors.slack.not4": {
    ja: "個人を採点しません。年表は記録であって評価ではありません。",
    en: "It does not score individuals — the chronicle is a record, not an evaluation.",
  },
  "connectors.slack.loading": { ja: "読み込み中…", en: "Loading…" },
  "connectors.slack.upgradeCta": {
    ja: "Slack 連携は {tier} の機能です。年表が自分で埋まる体験を有効化しましょう。",
    en: "Slack connect is a {tier} feature. Let your chronicle fill itself.",
  },
  "connectors.slack.enableWith": {
    ja: "{tier} で有効化",
    en: "Enable with {tier}",
  },
  "connectors.slack.connectedLabel": { ja: "接続済み", en: "Connected" },
  "connectors.slack.disconnect": { ja: "接続を解除", en: "Disconnect" },
  "connectors.slack.connectButton": { ja: "Slack を接続", en: "Connect Slack" },
  "connectors.slack.docs": {
    ja: "Slack 連携のドキュメント",
    en: "Slack connector docs",
  },
  "connectors.slack.errNotConfigured": {
    ja: "この環境では Slack 連携が未設定です。管理者に SLACK_* の設定を依頼してください。",
    en: "Slack connect isn't configured on this environment. Ask an admin to set the SLACK_* credentials.",
  },
  "connectors.slack.startFailed": {
    ja: "Slack 連携の開始に失敗しました。",
    en: "Failed to start Slack connect.",
  },
  "connectors.slack.disconnectFailed": {
    ja: "連携の解除に失敗しました。",
    en: "Failed to disconnect.",
  },
  "connectors.slack.errCancelled": {
    ja: "Slack の連携がキャンセルされました。",
    en: "Slack connect was cancelled.",
  },
  "connectors.slack.errWorkspaceTaken": {
    ja: "この Slack ワークスペースは既に別の銀河に接続されています。1 つのワークスペースは 1 つの銀河にのみ接続できます（リアクションが複数の銀河の年表に混ざるのを防ぐため）。別の銀河で使うには、先にそちらの銀河で接続を解除してください。",
    en: "This Slack workspace is already connected to another galaxy. A workspace can connect to only one galaxy — so reactions never bleed into multiple galaxies' chronicles. To use it on a different galaxy, disconnect it there first.",
  },
  "connectors.slack.errFailed": {
    ja: "Slack 連携に失敗しました。",
    en: "Slack connect failed.",
  },
  "connectors.slack.digestTitle": {
    ja: "週次ダイジェスト",
    en: "Weekly digest",
  },
  "connectors.slack.digestDesc": {
    ja: "先週の構造的な観測を週に一度 Slack に読み上げます。下の投稿先チャンネルにプレビューを送れます（自動の週次投稿は別途）。",
    en: "Reads last week's structural observations aloud to Slack once a week. Send a preview to the channel below (the automatic weekly post is a separate feature).",
  },
  "connectors.slack.digestChannelLabel": {
    ja: "投稿先チャンネル ID",
    en: "Destination channel ID",
  },
  "connectors.slack.digestInviteNote": {
    ja: "Bot を対象チャンネルに招待しておく必要があります（/invite）。",
    en: "Invite the bot to the destination channel first (/invite).",
  },
  "connectors.slack.digestPreviewButton": {
    ja: "プレビュー投稿",
    en: "Post preview",
  },
  "connectors.slack.digestPosted": {
    ja: "プレビューを投稿しました。",
    en: "Preview posted.",
  },
  "connectors.slack.digestChannelRequired": {
    ja: "投稿先チャンネル ID を入力してください。",
    en: "Enter a destination channel ID.",
  },
  "connectors.slack.digestErrUnset": {
    ja: "投稿先チャンネルが未設定です。チャンネル ID を入力してください。",
    en: "No digest channel set. Enter a channel ID.",
  },
  "connectors.slack.digestErrNotConnected": {
    ja: "先に Slack を接続してください。",
    en: "Connect Slack first.",
  },
  "connectors.slack.digestErrPostFailed": {
    ja: "投稿に失敗しました。Bot がチャンネルに招待されているか確認してください。",
    en: "Failed to post. Check that the bot is invited to the channel.",
  },
  "connectors.slack.digestErrFailed": {
    ja: "プレビュー投稿に失敗しました。",
    en: "Failed to post preview.",
  },
  "connectors.slack.digestEnabledLabel": {
    ja: "毎週自動投稿する",
    en: "Post automatically every week",
  },
  // digestSchedule is templated from the chosen day/time/tz ({day}/{time}/{tz}).
  // It describes WHEN the weekly post lands; the cadence stays weekly.
  "connectors.slack.digestSchedule": {
    ja: "毎週{day} {time} ({tz}) に、先週の構造的な観測を投稿します。",
    en: "Posts last week's structural observations every {day} at {time} ({tz}).",
  },
  "connectors.slack.digestDayLabel": { ja: "投稿する曜日", en: "Posting day" },
  "connectors.slack.digestTimeLabel": {
    ja: "投稿する時刻",
    en: "Posting time",
  },
  "connectors.slack.digestTzLabel": { ja: "タイムゾーン", en: "Timezone" },
  // Day names, indexed by Go time.Weekday (Sunday=0..Saturday=6).
  "connectors.slack.day.sun": { ja: "日曜", en: "Sunday" },
  "connectors.slack.day.mon": { ja: "月曜", en: "Monday" },
  "connectors.slack.day.tue": { ja: "火曜", en: "Tuesday" },
  "connectors.slack.day.wed": { ja: "水曜", en: "Wednesday" },
  "connectors.slack.day.thu": { ja: "木曜", en: "Thursday" },
  "connectors.slack.day.fri": { ja: "金曜", en: "Friday" },
  "connectors.slack.day.sat": { ja: "土曜", en: "Saturday" },
  "connectors.slack.digestScheduleInvalid": {
    ja: "曜日・時刻・タイムゾーンの設定が不正です。",
    en: "The posting day, time, or timezone is invalid.",
  },
  // Shown when the picker is available but the bot can reach no channels yet
  // (available && channels.length === 0): the dropdown would be empty, so fall
  // back to manual id entry with a hint to /invite the bot.
  "connectors.slack.digestNoChannelsHint": {
    ja: "Bot が招待されているチャンネルがまだありません。対象チャンネルで /invite すると候補に出ます。今はチャンネル ID を直接入力できます。",
    en: "The bot isn't in any channel yet. /invite it to the target channel to see it listed; for now you can enter a channel ID directly.",
  },
  "connectors.slack.digestChannelPickerLabel": {
    ja: "投稿先チャンネル",
    en: "Destination channel",
  },
  "connectors.slack.digestChannelPlaceholder": {
    ja: "チャンネルを選択…",
    en: "Select a channel…",
  },
  "connectors.slack.digestPickerScopeNote": {
    ja: "チャンネル一覧の取得には Slack App の channels:read / groups:read スコープが必要です。無い場合はチャンネル ID を直接入力してください。",
    en: "Listing channels needs the Slack App's channels:read / groups:read scopes. Without them, enter a channel ID directly.",
  },
  "connectors.slack.digestSave": { ja: "保存", en: "Save" },
  "connectors.slack.digestSaved": {
    ja: "設定を保存しました。",
    en: "Settings saved.",
  },
  "connectors.slack.digestSaveFailed": {
    ja: "設定の保存に失敗しました。",
    en: "Failed to save settings.",
  },
  "connectors.slack.digestEnableNeedsChannel": {
    ja: "自動投稿を有効にするには投稿先チャンネルを選択してください。",
    en: "Select a destination channel to enable automatic posting.",
  },
  "connectors.slack.digestChatWriteNote": {
    ja: "投稿には chat:write スコープと、対象チャンネルへの Bot の招待（/invite）が必要です。",
    en: "Posting needs the chat:write scope and the bot invited to the channel (/invite).",
  },
  "connectors.slack.digestCurrentOff": {
    ja: "現在: 自動投稿はオフ",
    en: "Current: automatic posting off",
  },
  "connectors.slack.digestCurrentOn": {
    ja: "現在: 自動投稿はオン →",
    en: "Current: automatic posting on →",
  },

  // MemberLink — a11y label for the shared "open this member's StarDetail" affordance.
  // {author} is interpolated. Kept generic so every clickable member name/card
  // (heatmap, roster, transitions, mini-radars, four-forces, event band) reads
  // the same to a screen reader.
  "member.detailAria": {
    ja: "{author} の詳細を観測",
    en: "Observe {author}'s detail",
  },

  // Observation-incomplete guard (#1066 follow-up). Shown in place of / beside a
  // domain headline when the BE flagged observation_incomplete: the surfaced Total
  // was derived only from partial observations (a repo that failed to clone), so
  // it is deflated and drifts between runs — honest "pending", not a number.
  "observation.incomplete.badge": {
    ja: "観測未完了",
    en: "Observation incomplete",
  },
  "observation.incomplete.hint": {
    ja: "一部のリポジトリを観測できなかったため、このシグナルは確定値ではありません。",
    en: "Some repositories could not be observed, so this signal is not yet final.",
  },
  // {analyzed}/{active} repos observed — explains the partial coverage.
  "observation.incomplete.coverage": {
    ja: "観測 {analyzed}/{active} リポ",
    en: "Observed {analyzed}/{active} repos",
  },

  // 通期(cumulative / 現在地) vs 期間(period / その月時点) — the StandingBadge.
  // Cumulative = the account-analysis HEAD scan (all-history surviving gravity,
  // the current standing). Period = one month's window from the timeline.
  "standing.cumulative": { ja: "通期", en: "All-time" },
  "standing.cumulativeHint": {
    ja: "全履歴を通した現在地。重力は「残った構造」を測る生涯フットプリントで、月次の軌跡（その月の live 重力）とは別の指標です。",
    en: "Your standing across all history. Gravity reads the lifetime footprint — the structure that lasted — a different metric from the monthly Timeline (that month's live gravity).",
  },
  // {month} 時点 — the value AS OF a specific historical month (period window).
  "standing.asOf": { ja: "{month} 時点", en: "as of {month}" },
  // The current month is a partial window (not finished) — shown on the latest
  // trajectory point and in place of "観測未完了" for the in-progress month.
  "standing.inProgress": { ja: "進行中", en: "In progress" },
  // Same as inProgress but names the partial month, so it's clear WHICH month's
  // data is still mid-window (e.g. "2026-06 進行中").
  "standing.inProgressAt": { ja: "{month} 進行中", en: "{month} in progress" },
  "standing.inProgressHint": {
    ja: "今月はまだ途中経過（部分月）です。確定は月末。現在の総合値は「通期」を参照してください。",
    en: "This month is still a partial window. It finalizes at month-end — see the all-time standing for your current total.",
  },
  // Headline mode toggle: 通期(現在地) vs 軌跡(月次/進行中).
  "standing.toggleCumulative": { ja: "通期", en: "All-time" },
  "standing.toggleTimeline": { ja: "軌跡", en: "Timeline" },
  "standing.toggleHint": {
    ja: "「通期」=全履歴の現在地（1つの総合値）。「軌跡」=月次の推移で、最新月は進行中（部分月）。別々に選べます。",
    en: "All-time = your current standing across all history (one number). Timeline = the monthly path; the latest month is in progress (partial). Pick either.",
  },
  // InfoPopover: why 通期 and 軌跡 are DIFFERENT metrics (and not directly comparable).
  "standing.explainTitle": {
    ja: "通期と軌跡は別の指標です",
    en: "All-time and Timeline are different metrics",
  },
  "standing.explainBody": {
    ja: "通期＝全履歴を通した現在地（1つの総合値）。軌跡＝月ごとの推移で、最新月は進行中（部分月）。\n\n重力については両者は別の指標です。通期は「生涯フットプリント」を読みます——全履歴で残った構造（素の生存）を測るので、土台を敷いて静まったアーキタイプは今は誰も触っていなくても高く出ます。軌跡はその月に現役で揺れていた live の重力（今まさに圧がかかっているか）。だから同じ人でも通期と軌跡で値が変わります。\n\n「通期＝何を残したか／今どこにいるか」「軌跡＝どう来たか」として読み、二つの数字を直接比較しないでください。",
    en: "All-time = your current standing across all history (one number). Timeline = the month-by-month path; the latest month is in progress (partial).\n\nFor gravity the two are different metrics. All-time reads the lifetime footprint — the structure that lasted across all history (raw survival), so a foundation that was laid and went quiet still reads high even when no one touches it now. Timeline reads the live gravity that was active that month (whether others press on it right now). So the same person can read differently in All-time vs Timeline.\n\nRead All-time as “what you left / where you are now” and Timeline as “how you got here” — don’t compare the two numbers directly.",
  },
} as const;

export type MessageKey = keyof typeof messages;

export function t(key: MessageKey, locale: Locale): string {
  const msg = messages[key];
  if (!msg) return key;
  return msg[locale] || msg.en;
}

export function useT(locale: Locale) {
  return (key: MessageKey) => t(key, locale);
}

/**
 * Template interpolation: replaces {key} placeholders in a translated string
 * with values. Lets a single i18n entry carry a runtime value (counts, names,
 * prices) instead of splicing locale ternaries around the message. Mirrors the
 * helper StructuralSummary uses for `{author}` — kept here so any caller that
 * pulls a parameterised message from this table can fill it the same way.
 */
export function interp(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/**
 * Markup-injection split: takes a translated template carrying a single
 * `{token}` placeholder and returns the [before, after] plain-text segments
 * around it, so a caller can render `before` + a JSX element (a <code>, <a>,
 * highlighted <span>, …) + `after`. This keeps a sentence that wraps inline
 * markup as ONE i18n entry (no copy split across i18n.ts + inline JSX, the
 * thing the i18n guard bans) while preserving the exact rendered output —
 * including locale-specific word order, where the marked element lands at the
 * start (ja) or middle (en) of the sentence. A template with no token yields
 * [whole, ""]; an empty trailing segment is fine (it's the SOURCE that's never
 * empty, not a lookup — §16's empty-string ban is about t() keys, not slices).
 */
export function splitMarkup(template: string, token: string): [string, string] {
  const marker = `{${token}}`;
  const i = template.indexOf(marker);
  if (i === -1) return [template, ""];
  return [template.slice(0, i), template.slice(i + marker.length)];
}

/**
 * Multi-marker variant of splitMarkup: splits a template on an ORDERED list of
 * `{token}` markers and returns the N+1 plain-text segments between them, so a
 * caller can interleave N JSX elements (e.g. two highlighted <span>s in one
 * sentence) at exactly the marker positions. The tokens must appear in the
 * template in the given order (the natural authoring order); a missing marker
 * collapses to an empty segment. Plain-value interpolation (a count with no
 * markup) should be done with interp() BEFORE this, leaving only the markup
 * markers — that keeps one i18n entry per sentence with exact word order.
 */
export function splitMarkupN(template: string, tokens: string[]): string[] {
  const parts: string[] = [];
  let rest = template;
  for (const token of tokens) {
    const marker = `{${token}}`;
    const i = rest.indexOf(marker);
    if (i === -1) {
      parts.push("");
      continue;
    }
    parts.push(rest.slice(0, i));
    rest = rest.slice(i + marker.length);
  }
  parts.push(rest);
  return parts;
}
